import { render, type ComponentChildren } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import {
  api,
  ApiError,
  clearImpersonationReturn,
  clearKey,
  discountedPriceEur,
  fmtFoundingPrice,
  foundingOfferEligible,
  getImpersonationReturn,
  hasActiveSubscription,
  isQuotaExhausted,
  isQuotaWarning,
  getKey,
  resolveFoundingDiscount,
  setImpersonationReturn,
  setKey,
  shouldAutoRedeemFoundingOffer,
  type AdminAccountRow,
  type AdminApp,
  type AdminCodeRow,
  type AdminEarningRow,
  type AdminOverview,
  type AiSummaryBody,
  type AiSummaryRecord,
  type AppTraffic,
  type AuditEntry,
  type BenefitType,
  type ConversationSession,
  type ConversationsSummary,
  type FunnelTotals,
  type ClaimCounts,
  type InsightsResponse,
  type InstallPlatform,
  type DailyPoint,
  type SessionFunnel,
  type LaunchOffer,
  type BusinessProfileInput,
  type ContentDistribution,
  type ContentItem,
  type ContentKind,
  type ContentRevision,
  type ContentStatus,
  type SocialChannelInfo,
  type Me,
  type Plan,
  type PlanId,
  type PlanSummary,
  type ReferralInfo,
  type Ticket,
  type TicketDetail,
  type WidgetBranding,
  type WidgetTone,
  type WidgetVoice,
} from "./api";
// subpath, nu indexul lui shared: acesta din urmă trage zod în bundle (123KB → 465KB, măsurat)
import { hexToRgb, inkOnPrimary, inkOnSurface } from "@ai-guidance/shared/brandColor";
// subpath fără zod, ca brandColor - indexul lui shared ar trage zod în bundle
import { PRESET_TOGGLE_ICONS, PRESET_TOGGLE_ICON_IDS, type PresetToggleIconId } from "@ai-guidance/shared/presetIcons";
import { AGENT_NOTES_MAX_CHARS, PRIVACY_PASSAGE, PRIVACY_PASSAGE_AI_PROMPT, PRIVACY_PASSAGE_PLACEMENT, PRIVACY_PASSAGE_VOICE, buildPrivacyPassageAiPrompt, stripAllExpressiveTags } from "@ai-guidance/shared";
import { brandingForm, brandingPayload, DEFAULT_PRIMARY_COLOR, DEFAULT_TEXT_COLOR } from "./branding";
import { t, tRaw, type StringKey } from "./i18n";
import { getThemePref, setThemePref, watchSystemTheme, type ThemePref } from "./theme";
import { billingErrText, errText, eur, fmtDate, fmtDuration, initials, num, pct, canaryLocalToEpoch, epochToCanaryLocal, fmtCanary, renewalLine, siteCreateErrText, visitsLabel, questionsLabel } from "./format";
import { RESEND_COOLDOWN_S, lastVerificationEmail, markVerificationSent, resendCooldownLeft } from "./verification";
import { navigate, parseAuthMode, parseHash, BILLING_SELF_SERVE, SIGNUPS_OPEN, type AuthMode, type Route, type SiteTab } from "./route";
import { initAIConciergeLoader } from "./aiConciergeLoader";
import { createDictation, defaultDictationLang, DICTATION_LANGS, dictationSupported, feedbackScreen, type Dictation, type DictationProblem } from "./feedback";
import { initPwaInstall, installState, promptInstall, subscribeInstall, type InstallState } from "./pwa";
import MarkdownIt from "markdown-it";
import "./styles.css";

// Aceeași bibliotecă și aceeași configurație ca backendul (services/contentRender.ts):
// HTML brut dezactivat - corpul vine de la un LLM, deci escapăm orice `<script>`,
// și preview-ul din editor arată EXACT ce va servi backendul public.
const previewMd = new MarkdownIt({ html: false, linkify: true, typographer: true });
// Artwork de plan - aceleași imagini crystal ca pe landing (deploy/landing/assets).
// esbuild le emite ca fișiere cu nume stabil în dist/ (vezi build.mjs).
import planStarterIcon from "./assets/plan-starter.webp";
import planGrowthIcon from "./assets/plan-growth.webp";
import planAgencyIcon from "./assets/plan-agency.webp";
import planEnterpriseIcon from "./assets/plan-enterprise.webp";
// Logo-ul de brand ca imagine (același badge raster ca pe landing / favicon /
// social), nu SVG: aproximarea vectorială nu semăna cu poza.
import logoBadge from "./assets/logo-badge.png";

// Originea sitului de prezentare (landing), per mediu. Consola stă pe alt
// subdomeniu decât landing-ul (app. vs apex), iar pe staging ambele sunt sub
// .staging. O derivăm din host-ul curent, ca „înapoi la site" și link-urile
// legale să rămână pe ACELAȘI mediu pe care-l testezi - altfel un tester de pe
// staging ajungea pe landing-ul de PRODUCȚIE. Oglinda lui deploy/landing/env-links.js
// (care rezolvă direcția inversă, landing → app).
const SITE_ORIGIN = (() => {
  const h = typeof location !== "undefined" ? location.hostname : "";
  if (h === "app.staging.askmywebsiteai.com") return "https://staging.askmywebsiteai.com";
  if (h === "app.askmywebsiteai.com") return "https://askmywebsiteai.com";
  // Local dev: backendul servește landing-ul pe aceeași origine (npm run dev, portul 4000).
  return typeof location !== "undefined" ? location.origin : "https://askmywebsiteai.com";
})();

/**
 * Unde duce un CTA de tip „upgrade" și cum se numește.
 *
 * Cu cumpărarea deschisă: tabul Plan & billing. În private beta tabul nu mai
 * există (`BILLING_SELF_SERVE`), iar planul se schimbă vorbind cu noi - deci
 * contactul, într-un tab nou. Nouă locuri chemau `navigate("#/plan")`; trecute
 * printr-un singur punct ca să nu ajungă unele într-un redirect mut spre
 * dashboard, care ar arăta ca un buton stricat.
 */
function goUpgrade(): void {
  if (BILLING_SELF_SERVE) {
    navigate("#/plan");
    return;
  }
  window.open(`${SITE_ORIGIN}/contact`, "_blank", "noopener");
}

/** Eticheta aceluiași CTA: cea specifică planului cât timp se poate cumpăra,
 *  „Contact us" în beta - unde chiar duce. */
function upgradeLabel(key: string): string {
  return BILLING_SELF_SERVE ? t(key as never) : t("plan.betaCta");
}

const PLAN_ICON: Record<PlanId, string> = {
  // „free" nu ajunge niciodată pe un card de preț (backendul trimite ALL_PLANS
  // clientului, fără el) - intrarea există doar ca Record-ul să rămână total.
  free: planStarterIcon,
  starter: planStarterIcon,
  growth: planGrowthIcon,
  agency: planAgencyIcon,
  enterprise: planEnterpriseIcon,
};

// ---------- helpers ----------

async function copyText(text: string): Promise<void> {
  if (window.isSecureContext && navigator.clipboard) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      /* fallback below */
    }
  }
  const helper = document.createElement("textarea");
  helper.value = text;
  helper.style.position = "fixed";
  helper.style.opacity = "0";
  document.body.appendChild(helper);
  helper.select();
  document.execCommand("copy");
  helper.remove();
}

// ---------- hash router ----------

function useRoute(): Route {
  const [route, setRoute] = useState<Route>(parseHash());
  useEffect(() => {
    const on = () => setRoute(parseHash());
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  return route;
}

// ---------- icons ----------

const Svg = (p: { children: ComponentChildren }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
    {p.children}
  </svg>
);
const IconOverview = () => (
  <Svg><rect x="3" y="3" width="7" height="9" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" /><rect x="14" y="12" width="7" height="9" rx="1.5" /><rect x="3" y="16" width="7" height="5" rx="1.5" /></Svg>
);
const IconSites = () => (
  <Svg><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c2.5 2.5 2.5 15 0 18M12 3c-2.5 2.5-2.5 15 0 18" /></Svg>
);
const IconPlan = () => (
  <Svg><rect x="2.5" y="5" width="19" height="14" rx="2.5" /><path d="M2.5 9.5h19" /></Svg>
);
const IconConsole = () => (
  <Svg><path d="M12 3l7.5 3v5c0 4.4-3 8.2-7.5 9.5C7.5 19.2 4.5 15.4 4.5 11V6L12 3z" /><path d="m9 11.5 2 2 4-4" /></Svg>
);
const IconTicket = () => (
  <Svg><path d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4z" /><path d="M10 6v12" stroke-dasharray="2 2" /></Svg>
);
const IconSpark = () => (
  <svg viewBox="0 0 24 24">
    <path d="M19.1 9.8 A7.5 7.5 0 1 1 14.3 4.8" fill="none" stroke="#a5b4fc" stroke-width="1.7" stroke-linecap="round" />
    <path d="M18.9 5.1 Q13.5 12 15.19 15.19 Q12 13.5 8.81 15.19 Q10.5 12 8.81 8.81 Q12 10.5 18.9 5.1 Z" fill="#fff" />
  </svg>
);
/**
 * Cristalul-cometă vectorial, folosit DOAR ca fallback în preview-ul de avatar al
 * widget-ului (când clientul n-a încărcat încă un logo). Logo-ul nostru de brand
 * din nav/login e acum imaginea raster (`logoBadge`), nu acest SVG. Se desenează
 * deschis, pe fundal cu gradient; fără inel - la 20–27px două contururi se
 * amestecă și cristalul, protagonistul, pierde.
 */
const BrandMark = () => (
  <svg viewBox="0 0 64 64" aria-hidden="true">
    <defs>
      <linearGradient id="bmBody" x1="0" y1="0" x2=".3" y2="1">
        <stop offset="0" stop-color="#d9cffb" /><stop offset=".5" stop-color="#eadffd" /><stop offset="1" stop-color="#ffffff" />
      </linearGradient>
      <linearGradient id="bmTrail" x1="0" y1="1" x2="0" y2="0">
        <stop offset="0" stop-color="#f0e6ff" stop-opacity=".9" /><stop offset="1" stop-color="#c9b6f2" stop-opacity="0" />
      </linearGradient>
      <radialGradient id="bmGlow" cx=".5" cy=".5" r=".5">
        <stop offset="0" stop-color="#ffffff" stop-opacity=".34" />
        <stop offset="1" stop-color="#ffffff" stop-opacity="0" />
      </radialGradient>
    </defs>
    <circle cx="32" cy="32" r="26" fill="url(#bmGlow)" />
    <g transform="rotate(38 32 32)">
      <g transform="translate(32 32) scale(.92) translate(-32 -32)">
        <g transform="translate(0 4)">
          <path fill="url(#bmTrail)" d="M28.4 14 Q30.4 2 32 -11 Q33.6 2 35.6 14 Z" />
          <path fill="url(#bmBody)" d="M27.9 12.8 L32 8 L36.1 12.8 L37.6 51.2 L32 56 L26.4 51.2 Z" />
          <path d="M32 8 L32 56" stroke="#8f7ee0" stroke-opacity=".4" stroke-width="1.1" fill="none" />
        </g>
      </g>
    </g>
  </svg>
);
const IconCopy = () => (
  <Svg><rect x="9" y="9" width="12" height="12" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h8" /></Svg>
);
const IconCheck = () => <Svg><path d="M20 6 9 17l-5-5" /></Svg>;
const IconPlus = () => <Svg><path d="M12 5v14M5 12h14" /></Svg>;
const IconShield = () => <Svg><path d="M12 3l7 3v6c0 4.4-3 8.3-7 9-4-.7-7-4.6-7-9V6z" /><path d="M9 12l2 2 4-4" /></Svg>;
const IconGear = () => <Svg><circle cx="12" cy="12" r="3.2" /><path d="M19.4 13.5a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1.04 1.56v.17a2 2 0 1 1-4 0v-.09a1.7 1.7 0 0 0-1.11-1.56 1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.7 1.7 0 0 0 .34-1.87 1.7 1.7 0 0 0-1.56-1.04H2.9a2 2 0 1 1 0-4h.09a1.7 1.7 0 0 0 1.56-1.11 1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 1.87.34H8a1.7 1.7 0 0 0 1.04-1.56V2.9a2 2 0 1 1 4 0v.09a1.7 1.7 0 0 0 1.04 1.56 1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.34 1.87V8a1.7 1.7 0 0 0 1.56 1.04h.17a2 2 0 1 1 0 4h-.09a1.7 1.7 0 0 0-1.56 1.04z" /></Svg>;
const IconExternal = () => <Svg><path d="M15 3h6v6M10 14 21 3M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /></Svg>;
const IconTrash = () => <Svg><path d="M4 7h16M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13" /></Svg>;
const IconChart = () => <Svg><path d="M3 21h18M7 21V9M12 21V3M17 21v-8" /></Svg>;
const IconChat = () => <Svg><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></Svg>;
/** Microfon - marchează turele/sesiunile de canal voce (vs. text) în Conversations. */
const IconMic = () => <Svg><path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z" /><path d="M19 10v1a7 7 0 0 1-14 0v-1M12 18v4M9 22h6" /></Svg>;
/** Săgeată ascendentă - tabul „Progress". Deliberat diferit de IconChart (bare):
 *  Statistics = volum, Progress = direcție/interpretare. */
const IconTrendUp = () => <Svg><path d="M3 17l6-6 4 4 8-8" /><path d="M14 7h7v7" /></Svg>;
const IconPalette = () => <Svg><path d="M12 22a10 10 0 1 1 0-20c5.5 0 9 3.6 9 8 0 2.5-2 4.5-4.5 4.5H14a1.7 1.7 0 0 0-1 3.1c.4.3.6.8.6 1.3 0 1.2-1 2.1-1.6 2.1z" /><circle cx="7.5" cy="10.5" r="1.2" fill="currentColor" stroke="none" /><circle cx="12" cy="7" r="1.2" fill="currentColor" stroke="none" /><circle cx="16.5" cy="10.5" r="1.2" fill="currentColor" stroke="none" /></Svg>;
const IconBook = () => <Svg><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20M4 19.5A2.5 2.5 0 0 0 6.5 22H20V2H6.5A2.5 2.5 0 0 0 4 4.5z" /></Svg>;
const IconChevronDown = () => <Svg><path d="m6 9 6 6 6-6" /></Svg>;
const IconChevronLeft = () => <Svg><path d="m15 18-6-6 6-6" /></Svg>;
const IconChevronRight = () => <Svg><path d="m9 18 6-6-6-6" /></Svg>;
const IconLogout = () => <Svg><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" /></Svg>;
const IconInfo = () => (
  <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "14px", height: "14px", borderRadius: "50%", background: "rgba(107, 114, 128, 0.15)", color: "var(--ink-3)", fontSize: "10px", fontWeight: "600", cursor: "help", position: "relative", flexShrink: 0, marginLeft: "4px" }}>
    <span style={{ lineHeight: 1 }}>i</span>
    <div style={{ position: "absolute", bottom: "100%", left: "50%", transform: "translateX(-50%)", marginBottom: "6px", whiteSpace: "nowrap", background: "var(--ink-2)", color: "white", padding: "6px 10px", borderRadius: "4px", fontSize: "12px", zIndex: 1000, pointerEvents: "none", opacity: 0, transition: "opacity 0.15s", visibility: "hidden" }} class="tooltip">
      Waiting out the 30-day refund window.
    </div>
    <style>{`.ref-stats .stat > div:has(> div:has(> .tooltip)):hover .tooltip { opacity: 1; visibility: visible; }`}</style>
  </div>
);

const IconHelp = () => (
  <Svg><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="3.6" /><path d="m5.8 5.8 3.7 3.7M14.5 14.5l3.7 3.7M18.2 5.8l-3.7 3.7M9.5 14.5l-3.7 3.7" /></Svg>
);
const IconMail = () => <Svg><rect x="3" y="5" width="18" height="14" rx="2.5" /><path d="m3.5 7 8.5 6 8.5-6" /></Svg>;
const IconSettings = () => (
  <Svg><circle cx="12" cy="12" r="3.2" /><path d="M19.4 13a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></Svg>
);
const IconSun = () => <Svg><circle cx="12" cy="12" r="4.2" /><path d="M12 2v2.5M12 19.5V22M4.2 4.2l1.8 1.8M18 18l1.8 1.8M2 12h2.5M19.5 12H22M4.2 19.8 6 18M18 6l1.8-1.8" /></Svg>;
const IconMoon = () => <Svg><path d="M20.5 14.5A8.2 8.2 0 0 1 9.5 3.5a8.2 8.2 0 1 0 11 11z" /></Svg>;
const IconAuto = () => <Svg><rect x="3" y="4" width="18" height="13" rx="2" /><path d="M8 21h8M12 17v4" /></Svg>;
const IconGift = () => <Svg><path d="M20 12v9H4v-9M2 7h20v5H2zM12 21V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7" /></Svg>;

// ---------- shared bits ----------

/**
 * Eticheta unui plan după id, din catalogul primit în /me. Fallback pe id-ul
 * brut: un plan pe care UI-ul nu-l cunoaște încă e mai bine afișat crud decât
 * deloc.
 */
function planLabelFor(me: Me, planId: string): string {
  return me.plans.find((p) => p.id === planId)?.label ?? planId;
}

/**
 * Numele planului EFECTIV - cel grantat dacă există, altfel cel plătit. De
 * folosit oriunde textul stă lângă o LIMITĂ (site-uri, răspunsuri): limitele
 * urmează grantul, deci numele trebuie să-l urmeze și el, altfel mesajul se
 * contrazice singur.
 */
function effectivePlanLabel(me: Me): string {
  return me.plan.grantedPlan ? planLabelFor(me, me.plan.grantedPlan) : me.plan.planLabel;
}

function Msg({ kind, text }: { kind: "ok" | "err" | "info"; text: string }) {
  return (
    <div class={`msg ${kind}`} role={kind === "err" ? "alert" : "status"} aria-live={kind === "err" ? "assertive" : "polite"}>
      {text}
    </div>
  );
}

/**
 * Dialogul standard. `wide` e pentru conținut de citit, nu de confirmat (raportul
 * AI, istoricul lui): lățește caseta și scoate padding-ul, ca antetul lipicios
 * (`ModalHead`) să atingă marginile. Implicit rămâne varianta îngustă - toate
 * celelalte ~10 modaluri din aplicație depind de ea.
 */
function Modal({ children, onClose, wide }: { children: ComponentChildren; onClose: () => void; wide?: boolean }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div class="modal-back" onClick={onClose}>
      <div class={`modal${wide ? " wide" : ""}`} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

/**
 * Antetul lipicios al unui modal `wide`: titlu + „×" mereu vizibil. Fără el,
 * singura ieșire dintr-un raport lung era butonul de la capătul conținutului.
 */
function ModalHead({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div class="modal-head">
      <h3>{title}</h3>
      <button type="button" class="modal-x" aria-label={t("common.close")} onClick={onClose}>×</button>
    </div>
  );
}

type ConfirmAsk = {
  title: string;
  body?: string;
  /** Linie de avertisment sub corp - pentru pașii pe care nu-i mai poți da înapoi. */
  warn?: string;
  confirmLabel: string;
  tone?: "primary" | "danger";
  /** Prezent doar pentru varianta „prompt": cere un text obligatoriu. */
  input?: { label: string; placeholder?: string };
  resolve: (value: string | null) => void;
};

/**
 * Confirmările dashboardului, în locul lui `confirm()`/`prompt()` nativ.
 *
 * Dialogul nativ scrie „app.askmywebsiteai.com says" deasupra textului nostru și
 * nu se poate stiliza - pe un ecran de plată arată a alertă de browser, nu a pas
 * din flux. Hook și nu componentă globală fiindcă apelantul are nevoie de un
 * răspuns *inline* (`if (!(await confirm(...))) return;`), deci de o promisiune,
 * exact ca API-ul nativ pe care îl înlocuiește.
 */
function useConfirm() {
  const [ask, setAsk] = useState<ConfirmAsk | null>(null);
  const [value, setValue] = useState("");

  function open(o: Omit<ConfirmAsk, "resolve">): Promise<string | null> {
    setValue("");
    return new Promise((resolve) => setAsk({ ...o, resolve }));
  }
  function close(answer: string | null) {
    if (!ask) return;
    ask.resolve(answer);
    setAsk(null);
  }

  /** Da/Nu. Rezolvă `false` și la Escape sau click pe fundal. */
  const confirm = (o: Omit<ConfirmAsk, "resolve" | "input">): Promise<boolean> =>
    open(o).then((v) => v !== null);
  /** Cere un text; rezolvă `null` dacă userul renunță. */
  const promptText = (o: Omit<ConfirmAsk, "resolve"> & { input: NonNullable<ConfirmAsk["input"]> }) => open(o);

  const node = ask ? (
    <Modal onClose={() => close(null)}>
      <h3>{ask.title}</h3>
      {ask.body && <p>{ask.body}</p>}
      {ask.input && (
        <div>
          <label>{ask.input.label}</label>
          <input
            autofocus
            value={value}
            placeholder={ask.input.placeholder}
            onInput={(e) => setValue((e.target as HTMLInputElement).value)}
          />
        </div>
      )}
      {ask.warn && <p class="confirm-warn">{ask.warn}</p>}
      <div class="actions">
        <button class="btn ghost" onClick={() => close(null)}>{t("common.cancel")}</button>
        <button
          class={`btn ${ask.tone === "danger" ? "solid-danger" : "primary"}`}
          disabled={Boolean(ask.input) && !value.trim()}
          onClick={() => close(ask.input ? value.trim() : "")}
        >
          {ask.confirmLabel}
        </button>
      </div>
    </Modal>
  ) : null;

  return { confirmNode: node, confirm, promptText };
}

/** Empty state compact pentru interiorul cardurilor: icon + text + CTA opțional. */
function EmptyMini({ icon, text, cta }: { icon: ComponentChildren; text: string; cta?: { label: string; onClick: () => void } }) {
  return (
    <div class="empty-mini">
      <div class="ic">{icon}</div>
      <span>{text}</span>
      {cta && <button class="btn subtle sm" onClick={cta.onClick}>{cta.label}</button>}
    </div>
  );
}

/** Bloc shimmer pentru stările de încărcare. */
function Skeleton({ w, h, style }: { w?: string; h?: string; style?: Record<string, string> }) {
  return <span class="skeleton" style={{ width: w ?? "100%", height: h ?? "13px", ...style }} />;
}

/**
 * Semnul întrebării cu explicație, lângă o etichetă de metrică. Tooltipul e
 * PUR CSS (`::after` cu `attr(data-tip)`), deci nu ține stare și nu poate rămâne
 * agățat pe ecran; `tabIndex` + `:focus-visible` îl fac accesibil de la tastatură,
 * iar `aria-label` îl citește cititorul de ecran - de-aceea textul e o singură
 * propoziție completă, nu un fragment.
 */
function InfoTip({ text }: { text: string }) {
  return <span class="info-tip" tabIndex={0} role="img" aria-label={text} data-tip={text}>?</span>;
}

// ---------- daily response-volume chart ----------

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// duminică..sâmbătă, ca Date#getDay() - o singură literă, gen calendarele minimale
// (ambiguitatea T/T, S/S nu contează: weekend-ul e oricum evidențiat separat)
const WEEKDAY_LETTER = ["S", "M", "T", "W", "T", "F", "S"];

function DayChart({ points, unit }: { points: DailyPoint[]; unit?: string }) {
  const u = unit ?? t("chart.responses");
  const map = new Map(points.map((p) => [p.day, p.count]));
  const today = new Date();
  const days: { day: string; count: number; weekday: string; isWeekend: boolean }[] = [];
  for (let i = 29; i >= 0; i--) {
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - i);
    const key = ymd(d);
    const dow = d.getDay();
    days.push({ day: key, count: map.get(key) ?? 0, weekday: WEEKDAY_LETTER[dow], isWeekend: dow === 0 || dow === 6 });
  }
  const max = Math.max(1, ...days.map((d) => d.count));
  const label = (k: string) => { const [, m, dd] = k.split("-"); return `${dd}.${m}`; };
  return (
    <>
      <div class="daychart">
        {days.map((d) => (
          <div key={d.day} class={`daycol ${d.isWeekend ? "weekend" : ""}`} title={`${label(d.day)} · ${d.count} ${u}`}>
            <span class="daynum">{d.count > 0 ? d.count : ""}</span>
            <div class="daybar-track">
              <div class={`daybar ${d.count === 0 ? "z" : ""}`} style={{ height: `${Math.max(2, (d.count / max) * 100)}%` }} />
            </div>
            <span class="daywd">{d.weekday}</span>
          </div>
        ))}
      </div>
      <div class="daychart-x">
        <span>{label(days[0].day)}</span>
        <span>{label(days[15].day)}</span>
        <span>{label(days[29].day)}</span>
      </div>
    </>
  );
}

const EMPTY_FUNNEL: FunnelTotals = { impressions: 0, opens: 0, asked: 0, guided: 0, reached: 0 };

/**
 * GET /stats/daily pentru un site (`appId`) sau pentru tot contul - răspunsuri/zi
 * ȘI pâlnia, într-un SINGUR apel. Hook, nu fetch în fiecare card, tocmai fiindcă
 * volumul și pâlnia vin din același răspuns: două carduri care se montează
 * împreună ar fi cerut altfel de două ori aceleași date.
 * `null` = încă se încarcă (eroarea cade pe date goale, ca înainte).
 */
type DailyStats = { days: DailyPoint[]; funnel: SessionFunnel; claims: ClaimCounts };
function useDailyStats(appId?: string): DailyStats | null {
  const [data, setData] = useState<DailyStats | null>(null);
  useEffect(() => {
    let alive = true;
    setData(null);
    api
      .statsDaily(30, appId)
      .then((r) => { if (alive) setData(r); })
      .catch(() => { if (alive) setData({ days: [], funnel: { totals: { ...EMPTY_FUNNEL }, days: [] }, claims: { verified: 0, corrected: 0 } }); });
    return () => { alive = false; };
  }, [appId]);
  return data;
}

/** Card cu graficul de volum răspunsuri. Datele vin de la părinte (vezi useDailyStats); `null` = loading. */
function DailyResponsesCard({ points, compact }: { points: DailyPoint[] | null; compact?: boolean }) {
  const total = (points ?? []).reduce((s, p) => s + p.count, 0);
  return (
    <div class={`card${compact ? " compact" : ""}`}>
      <div class="card-head">
        <div><h2>{t("chart.title")}</h2><p>{t("chart.sub")}</p></div>
        <div class="chart-legend"><span class="big">{num(total)}</span><span class="muted">{t("chart.responses")}</span></div>
      </div>
      {points === null && <Skeleton h={compact ? "78px" : "150px"} style={{ borderRadius: "10px" }} />}
      {points && total === 0 && <EmptyMini icon={<IconChart />} text={t("chart.empty")} />}
      {points && total > 0 && <DayChart points={points} />}
    </div>
  );
}

// ---------- pâlnia asistentului (Shown → Opened → Asked, + ghidarea) ----------

/**
 * Procentul dintr-o treaptă în cea de deasupra ei. `null` - deci nu afișăm
 * niciun procent - când treapta de sus e goală SAU când cea de jos o depășește:
 * un „140% opened the assistant" ar fi o minciună mai mare decât absența
 * cifrei. Se întâmplă real în fereastra de tranziție (SDK vechi din cache
 * trimite întrebări fără evenimente de sesiune, vezi `FunnelTotals`), iar
 * cifrele brute rămân oricum vizibile alături, plus nota din card.
 */
function stepRate(value: number, previous: number): number | null {
  if (previous <= 0 || value > previous) return null;
  return Math.round((value / previous) * 100);
}

/**
 * Singura inconsistență posibilă ACUM e `asked > opens` (SDK vechi în cache,
 * care trimite întrebări fără eveniment de sesiune - vezi `FunnelTotals`).
 * `impressions` vs `opens` NU se compară niciodată: sunt pe unități diferite
 * (pageview anonim vs. sesiune identificată), deci „mai mare" sau „mai mic"
 * n-ar însemna nimic.
 */
function funnelInconsistent(f: FunnelTotals): boolean {
  return f.asked > f.opens;
}

/**
 * „Assistant funnel": Afișări (pageview, anonim) → Deschideri → Întrebări
 * (sesiune, identificate), cu procentul din treapta PRECEDENTĂ (nu din prima)
 * - întrebarea proprietarului e „unde pierd lumea", iar asta se citește pe
 * fiecare pas, nu pe totalul de sus.
 *
 * `impressions` e pe ALTĂ unitate decât `opens`/`asked`: e un contor de
 * afișări PER PAGINĂ VIZITATĂ, anonim, nededuplicabil pe vizitator - SDK-ul
 * nu are voie să creeze un id de sesiune înainte de o interacțiune (ePrivacy
 * art. 5(3)), deci un vizitator care navighează pe 6 pagini contribuie 6, nu
 * 1. Procentul „opens din impressions" rămâne etichetat explicit ca fiind PER
 * PAGINĂ AFIȘATĂ (`funnel.opensNote`), nu per vizitator - altfel ar sugera o
 * rată de conversie pe care n-o putem calcula.
 *
 * Ghidarea (guided → reached) NU mai e aici: trăiește în „Does the assistant
 * actually help?" (`GuidanceFunnelCard`), împreună cu disclaimerul de scară
 * (`funnel.guidanceScale`). Două pâlnii care spuneau parțial aceeași poveste,
 * una lângă alta, erau exact confuzia pe care restructurarea o elimină -
 * aici rămâne strict adopția: afișare → deschidere → întrebare.
 *
 * Trend: AFIȘĂRILE pe zi, nu rata de deschidere. La scara asta o rată e nedefinită
 * pe zilele cu zero afișări și sare violent la 1–2 afișări (0% / 100%), pe când
 * volumul se citește direct; ratele sunt oricum chiar deasupra, ca agregat.
 */
function VisitorFunnelCard({ funnel }: { funnel: SessionFunnel | null }) {
  const totals = funnel?.totals ?? EMPTY_FUNNEL;
  const { impressions, opens, asked } = totals;

  // Rata de deschidere e PER PAGINĂ AFIȘATĂ, nu per vizitator - vezi comentariul de mai sus.
  const openRate = stepRate(opens, impressions);
  const askRate = stepRate(asked, opens);
  // „Shown" nu are rând propriu: numărul e deja în capul cardului
  // (chart-legend, mai jos) - un al doilea rând identic ar fi zgomot pur.
  const steps = [
    { key: "opens", label: t("funnel.opens"), tip: t("insights.tip.opens"), value: opens, note: openRate === null ? null : t("funnel.opensNote", openRate), cls: "s2" },
    { key: "asked", label: t("funnel.asked"), tip: t("insights.tip.asked"), value: asked, note: askRate === null ? null : t("funnel.askedNote", askRate), cls: "s3" },
  ];

  // Lățimile sunt relative la prima treaptă ȘI monoton descrescătoare: o bară mai
  // lungă decât cea de deasupra ei ar desena o pâlnie imposibilă. Cifrele rămân
  // cele reale - plafonăm doar desenul (impressions/opens sunt pe unități diferite,
  // dar bara e strict o proporție vizuală, nu o afirmație că se compară corect).
  let prev = 100;
  const widths = steps.map((s) => {
    const raw = impressions > 0 ? (s.value / impressions) * 100 : 0;
    prev = Math.min(prev, raw);
    return Math.max(3, prev);
  });

  const trend: DailyPoint[] = (funnel?.days ?? []).map((d) => ({ day: d.day, count: d.impressions }));

  return (
    <div class="card">
      <div class="card-head">
        <div><h2>{t("insights.q1.title")}</h2><p>{t("insights.q1.sub")}</p></div>
        <div class="chart-legend"><span class="big">{num(impressions)}</span><span class="muted">{t("funnel.impressionsUnit")}</span></div>
      </div>

      {funnel === null && <Skeleton h="190px" style={{ borderRadius: "10px" }} />}

      {funnel !== null && impressions === 0 && asked === 0 && (
        <EmptyMini icon={<IconChart />} text={t("funnel.empty")} />
      )}

      {funnel !== null && (impressions > 0 || asked > 0) && (
        <>
          <div class="funnel-steps">
            {steps.map((s, i) => (
              <div class="funnel-step" key={s.key}>
                <div class="funnel-step-top">
                  <span class="funnel-step-name">{s.label} <InfoTip text={s.tip} /></span>
                  <span class="funnel-step-count">
                    {num(s.value)}
                    {s.note !== null && <span class="funnel-step-note"> / {s.note}</span>}
                  </span>
                </div>
                <div class="funnel-track"><div class={`funnel-bar ${s.cls}`} style={{ width: `${widths[i]}%` }} /></div>
              </div>
            ))}
          </div>

          {funnelInconsistent(totals) && <p class="funnel-note">{t("funnel.mixedVersions")}</p>}

          {trend.length > 0 && (
            <div class="funnel-trend">
              <div class="funnel-trend-head">{t("funnel.trend")}</div>
              <DayChart points={trend} unit={t("funnel.impressionsUnit")} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------- widget branding editor (Growth+, §1) ----------

const TONE_OPTIONS: Array<{ value: WidgetTone | ""; label: () => string }> = [
  { value: "", label: () => t("brand.toneDefault") },
  { value: "friendly", label: () => t("brand.toneFriendly") },
  { value: "professional", label: () => t("brand.toneProfessional") },
  { value: "playful", label: () => t("brand.tonePlayful") },
  { value: "concise", label: () => t("brand.toneConcise") },
];

const VOICE_OPTIONS: Array<{ value: WidgetVoice; label: () => string }> = [
  { value: "standard", label: () => t("brand.voiceStandard") },
  { value: "serious", label: () => t("brand.voiceSerious") },
  { value: "storyteller", label: () => t("brand.voiceStoryteller") },
];

// widget-ul redimensionează avatarul oricum - 256px e suficient de mare pentru orice
// ecran, dar mic ca upload (câteva zeci de KB, comparativ cu megapixeli din poze de telefon)
const LOGO_MAX_SIZE = 256;

/** Redimensionare 100% în browser (canvas), înainte de upload - niciun megapixel inutil nu ajunge pe server. */
async function resizeLogoFile(file: File, maxSize = LOGO_MAX_SIZE): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxSize / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale));
  const h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas unsupported");
  ctx.drawImage(bitmap, 0, 0, w, h);
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("resize failed"))), "image/png");
  });
}

/**
 * Bulina de deschidere a chatului (packages/sdk/src/widget.ts .aig-toggle) -
 * fundal = culoarea aleasă, conținut = logo-ul clientului dacă există; altfel, în
 * stare 100% default (fără culoare custom) badge-ul nostru raster, iar cu o culoare
 * custom fără logo marca adaptivă (BrandMark). Oglindește exact SDK-ul (DEFAULT_TOGGLE_LOGO
 * vs TOGGLE_MARK_SVG) și modul „Just logo" (clasa .aig-toggle--logo-only).
 */
function ToggleBubblePreview(
  { logoUrl, color, logoOnly, defaultBrand, presetIcon, ink }:
    { logoUrl: string; color: string; logoOnly: boolean; defaultBrand: boolean; presetIcon: string; ink: string },
) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [logoUrl]);
  const showImg = Boolean(logoUrl) && !broken;
  // Oglindește widget.ts: „just logo" se aplică doar cât timp logo-ul chiar există
  // și s-a încărcat - altfel marca noastră de fallback are nevoie de bulina colorată.
  const stripped = logoOnly && showImg;
  // Aceeași precedență ca widget.ts mount(): logo > iconiță predefinită > fallback.
  const preset = !showImg ? PRESET_TOGGLE_ICONS[presetIcon as PresetToggleIconId] : undefined;
  return (
    <div
      class={`toggle-bubble-preview${stripped ? " toggle-bubble-preview--logo-only" : ""}`}
      style={{ background: stripped ? undefined : color }}
      title={t("brand.bubblePreviewTitle")}
    >
      {showImg
        ? <img src={logoUrl} alt="" onError={() => setBroken(true)} />
        : preset
          ? <span class="toggle-bubble-preview-icon" style={{ color: ink }} dangerouslySetInnerHTML={{ __html: preset }} />
          : defaultBrand
            ? <img src={logoBadge} alt="" />
            : <BrandMark />}
    </div>
  );
}

/** Numele iconițelor predefinite - map static, ca `t` să rămână pe chei literale. */
const PRESET_ICON_LABELS: Record<PresetToggleIconId, () => string> = {
  chat: () => t("brand.icon.chat"),
  "chat-dots": () => t("brand.icon.chat-dots"),
  question: () => t("brand.icon.question"),
  headset: () => t("brand.icon.headset"),
  bot: () => t("brand.icon.bot"),
  sparkles: () => t("brand.icon.sparkles"),
};

/**
 * Mock-up live al widgetului - reflectă câmpurile din formular ÎNAINTE de Save.
 * Oglindește exact ce randează packages/sdk/src/widget.ts: fără logo în header
 * (doar numele - decizie de produs, vezi Widget.mount), input + Send inerte
 * (`.widget-preview` are `pointer-events: none`, vezi styles.css) - un click
 * "degeaba", nu funcțional.
 */
/**
 * Oglinda widgetului real (sdk/src/widget.ts). Valorile vizuale sunt copiate de
 * acolo intenționat - culori hardcodate, nu tokenurile dashboardului: widgetul
 * rulează pe situl clientului, unde tema noastră nu există, deci un `--surface-2`
 * aici ar arăta o bulă pe care vizitatorul n-o vede niciodată.
 *
 * `poweredBy` urmează aceeași regulă ca `poweredByFor` din appRegistry.ts -
 * altfel un client Growth ar personaliza pe o previzualizare fără linia de
 * atribuire care apoi îi apare în producție.
 */
function WidgetPreviewMock(
  { name, color, textColor, welcome, poweredBy }:
    { name: string; color: string; textColor: string | null; welcome: string; poweredBy: boolean },
) {
  // aceiași helperi pe care widgetul îi pune în --aig-on-primary / --aig-primary-ink,
  // ca o culoare pastel să arate identic aici și în producție (vezi shared/brandColor.ts).
  // textColor explicit al owner-ului suprascrie cerneala calculată - exact ca în widget.ts.
  const ink = textColor ?? inkOnPrimary(color);
  const inkOnWhite = inkOnSurface(color);
  const [sr, sg, sb] = hexToRgb(ink);
  const softTint = `rgba(${sr},${sg},${sb},.16)`;
  return (
    <div class="widget-preview" aria-hidden="true">
      <div class="widget-preview-head" style={{ background: color, color: ink }}>
        <span class="widget-preview-name">{name || "AI Concierge"}</span>
        <span class="widget-preview-close" style={{ color: ink, background: softTint }}>✕</span>
      </div>
      <div class="widget-preview-messages">
        <div class="widget-preview-bubble agent">{welcome || t("brand.welcomePh")}</div>
        {/* bulă de vizitator: singurul loc unde se vede culoarea pe fundal plin, exact ca .aig-msg--user */}
        <div class="widget-preview-bubble user" style={{ background: color, color: ink }}>{t("brand.previewUserMsg")}</div>
      </div>
      <div class="widget-preview-form">
        <span class="widget-preview-input">{t("brand.previewAsk")}</span>
        {/* butonul rotund de trimitere, ca în widget: culoarea clientului cu cerneala potrivită */}
        <span class="widget-preview-send" style={{ color: inkOnWhite }} aria-label={t("brand.previewSend")}>
          <svg viewBox="0 0 16 16" fill="none">
            <path d="M8 13V3M8 3L3.5 7.5M8 3l4.5 4.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </span>
      </div>
      {poweredBy && (
        <div class="widget-preview-footer">
          Powered by AskMyWebsite[AI]
        </div>
      )}
    </div>
  );
}

/** Ordinea de afișare a opțiunilor din cardul Install; „Any website" înaintea căii GTM. */
const INSTALL_PLATFORM_ORDER: InstallPlatform[] = ["wordpress", "shopify", "wix", "squarespace", "webflow", "generic", "gtm"];

/** Chei explicite, nu template-string: așa rămân verificate de tipul StringKey la compilare. */
const INSTALL_STEP_KEYS: Record<InstallPlatform, StringKey[]> = {
  generic: ["sd.install.generic.1", "sd.install.generic.2", "sd.install.generic.3"],
  wordpress: ["sd.install.wordpress.1", "sd.install.wordpress.2", "sd.install.wordpress.3"],
  shopify: ["sd.install.shopify.1", "sd.install.shopify.2", "sd.install.shopify.3"],
  wix: ["sd.install.wix.1", "sd.install.wix.2", "sd.install.wix.3"],
  squarespace: ["sd.install.squarespace.1", "sd.install.squarespace.2", "sd.install.squarespace.3"],
  webflow: ["sd.install.webflow.1", "sd.install.webflow.2", "sd.install.webflow.3"],
  gtm: ["sd.install.gtm.1", "sd.install.gtm.2", "sd.install.gtm.3"],
};

const INSTALL_PLATFORM_NAME: Record<InstallPlatform, StringKey> = {
  generic: "sd.platform.name.generic",
  wordpress: "sd.platform.name.wordpress",
  shopify: "sd.platform.name.shopify",
  wix: "sd.platform.name.wix",
  squarespace: "sd.platform.name.squarespace",
  webflow: "sd.platform.name.webflow",
  gtm: "sd.platform.name.gtm",
};

/**
 * Selectorul de platformă + pașii de instalare (cardul Install). Serverul
 * detectează platforma din homepage (services/platformDetect.ts) și
 * precompletează `installPlatform`; clientul poate alege alta oricând -
 * alegerea se persistă în DB la fiecare click (decizie Radu, 3 aug 2026:
 * de pe alt dispozitiv trebuie să regăsești aceeași selecție, nu localStorage).
 * Fallback-ul dublu: detectare greșită → clientul corectează dintr-un click;
 * nedetectat încă/deloc → „Any website".
 */
function InstallSteps({ app }: { app: AdminApp }) {
  const [selected, setSelected] = useState<InstallPlatform>(app.installPlatform ?? app.detectedPlatform ?? "generic");
  // Detectarea e fire-and-forget la creare, deci poate ateriza DUPĂ montare -
  // pollingul de sync reîmprospătează `app`, iar cât timp clientul n-a apucat
  // să aleagă nimic local, urmăm ce a scris serverul.
  useEffect(() => {
    if (app.installPlatform) setSelected(app.installPlatform);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app.installPlatform]);
  const pick = (p: InstallPlatform) => {
    setSelected(p);
    // optimist: selecția locală rămâne chiar dacă persistarea pică - pașii
    // afișați sunt oricum cei ceruți; se re-încearcă natural la următorul click
    void api.updateInstallPlatform(app.appId, p).catch(() => {});
  };
  return (
    <>
      <p class="muted" style={{ marginBottom: "8px" }}>{t("sd.platform.label")}</p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "12px" }}>
        {INSTALL_PLATFORM_ORDER.map((p) => (
          <button key={p} class={`btn sm ${selected === p ? "primary" : "ghost"}`} onClick={() => pick(p)}>
            {t(INSTALL_PLATFORM_NAME[p])}
            {app.detectedPlatform === p ? <span style={{ opacity: 0.75, marginLeft: "5px", fontSize: "11px" }}>✓ {t("sd.platform.detected")}</span> : null}
          </button>
        ))}
      </div>
      <ol class="muted" style={{ margin: "0 0 12px", paddingLeft: "20px", fontSize: "13px", lineHeight: 1.55 }}>
        {INSTALL_STEP_KEYS[selected].map((k) => (
          <li key={k}>{t(k)}</li>
        ))}
      </ol>
      {app.detectedGtm && selected !== "gtm" ? (
        <p class="muted" style={{ marginBottom: "12px", fontSize: "12px" }}>{t("sd.platform.gtmHint")}</p>
      ) : null}
    </>
  );
}

function BrandingCard({
  appId,
  initial,
  me,
  onSaved,
}: {
  appId: string;
  initial: WidgetBranding | null;
  me: Me;
  /** situl întors de PATCH, dus imediat în lista din părinte - vezi `dirty` mai jos */
  onSaved: (app: AdminApp) => void;
}) {
  // Personalizarea widgetului (nume/logo/culori/welcome/ton) e GRATUITĂ pe orice plan
  // (31 iul 2026) - fără gate aici. Ce rămâne legat de plan: linia „Powered by" dispare
  // de la Growth în sus, iar CSS custom rămâne doar white-label (Agency+).
  const whiteLabel = me.plan.brandingLevel === "white-label";
  // „Powered by AskMyWebsite[AI]" apare doar pe amwai (Free/Starter) - oglindește poweredByFor.
  const showsPoweredBy = me.plan.brandingLevel === "amwai";
  const start = brandingForm(initial);
  const [assistantName, setAssistantName] = useState(start.assistantName);
  const [logoUrl, setLogoUrl] = useState(start.logoUrl);
  const [presetIcon, setPresetIcon] = useState(start.presetIcon);
  const [logoOnly, setLogoOnly] = useState(start.logoOnly);
  // culoarea custom e opțională: fără ea, widgetul folosește defaultul din SDK (downgrade/reset safe)
  const [hasCustomColor, setHasCustomColor] = useState(start.hasCustomColor);
  const [primaryColor, setPrimaryColor] = useState(start.primaryColor);
  // text separat de primaryColor: altfel un hex pe jumătate tastat ("#4f46") s-ar
  // rescrie singur la fiecare apăsare de tastă, imposibil de completat de mână
  const [hexDraft, setHexDraft] = useState(primaryColor);
  useEffect(() => setHexDraft(primaryColor), [primaryColor]);
  // textColor: aceeași poveste ca primaryColor - opțional, gol = cerneala calculată de widget.ts
  // (inkOnPrimary). BUG găsit 23 iul: câmpul arăta mereu alb hardcodat ca „valoare implicită",
  // indiferent dacă era custom sau nu - pe un primary destul de închis (#0d9488), inkOnPrimary
  // alege NEGRU (contrast mai bun ca alb: 5.6:1 vs 3.7:1), deci widget-ul arăta text închis cât
  // timp omul nu chiar interacționa cu selectorul (doar privea un alb care nu era încă al lui).
  // Fix: swatch-ul/hex-ul arată acum culoarea REAL calculată automat cât timp nu e custom, nu un
  // alb fix - ce vezi e ce se aplică.
  const [hasTextColor, setHasTextColor] = useState(start.hasTextColor);
  const [textColor, setTextColor] = useState(start.textColor);
  const autoTextColor = hasCustomColor ? inkOnPrimary(primaryColor) : DEFAULT_TEXT_COLOR;
  const effectiveTextColor = hasTextColor ? textColor : autoTextColor;
  const [textHexDraft, setTextHexDraft] = useState(effectiveTextColor);
  useEffect(() => setTextHexDraft(effectiveTextColor), [effectiveTextColor]);
  const [launcherCss, setLauncherCss] = useState(start.launcherCss);
  const [chatCss, setChatCss] = useState(start.chatCss);
  const [welcomeMessage, setWelcomeMessage] = useState(start.welcomeMessage);
  const [fallbackMessage, setFallbackMessage] = useState(start.fallbackMessage);
  const [tone, setTone] = useState<WidgetTone | "">(start.tone);
  const [orbChat, setOrbChat] = useState(start.orbChat);
  const [voice, setVoice] = useState<WidgetVoice>(start.voice);
  // BUG (3 aug 2026): salvarea trimite TOT brandingul (PATCH-ul înlocuiește integral),
  // iar cardul își citea starea o singură dată, la montare. Fiindcă salvarea nu
  // reîmprospăta situl din părinte, o simplă plimbare între tab-uri remonta cardul cu
  // datele DINAINTE de salvare: câmpurile numelui și ale mesajului de bun venit apăreau
  // goale (doar placeholderele, care arată exact ca defaults-urile), iar următoarea
  // salvare - de pildă doar o culoare schimbată - le ștergea din DB.
  // Două jumătăți ale aceluiași fix: `onSaved(app)` duce ÎN LISTĂ situl întors de
  // PATCH (sincron - un `reloadApps()` asincron lasă o fereastră în care o remontare
  // citește tot datele vechi; verificat în browser, 3 aug) și resincronizarea de mai
  // jos când `initial` se schimbă. `dirty` există ca resincronizarea să nu calce peste
  // ce tocmai tastează omul (situl nesincronizat reîncarcă lista la fiecare 4s, vezi
  // SiteDetail).
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    if (dirty) return;
    const f = brandingForm(initial);
    setAssistantName(f.assistantName);
    setLogoUrl(f.logoUrl);
    setPresetIcon(f.presetIcon);
    setLogoOnly(f.logoOnly);
    setHasCustomColor(f.hasCustomColor);
    setPrimaryColor(f.primaryColor);
    setHasTextColor(f.hasTextColor);
    setTextColor(f.textColor);
    setLauncherCss(f.launcherCss);
    setChatCss(f.chatCss);
    setWelcomeMessage(f.welcomeMessage);
    setFallbackMessage(f.fallbackMessage);
    setTone(f.tone);
    setOrbChat(f.orbChat);
    setVoice(f.voice);
  }, [initial]);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function handleLogoFile(file: File | undefined) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setMsg(t("brand.logoBadType"));
      return;
    }
    setUploadingLogo(true);
    setMsg(null);
    try {
      const resized = await resizeLogoFile(file);
      const { url } = await api.uploadBrandingLogo(appId, resized);
      setDirty(true);
      setLogoUrl(url);
    } catch (err) {
      setMsg(errText(err));
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function save(e: Event) {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      const { app } = await api.updateBranding(
        appId,
        brandingPayload(
          { assistantName, logoUrl, presetIcon, logoOnly, hasCustomColor, primaryColor, hasTextColor, textColor, launcherCss, chatCss, welcomeMessage, fallbackMessage, tone, orbChat, voice },
          { whiteLabel, stored: initial },
        ),
      );
      // starea din formular e acum ȘI cea din DB: o ducem în părinte pe loc, iar cardul
      // se lasă resincronizat (altfel o remontare l-ar readuce la datele vechi)
      setDirty(false);
      onSaved(app);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (err) {
      setMsg(errText(err));
    } finally {
      setBusy(false);
    }
  }

  async function doReset() {
    setResetting(true);
    setMsg(null);
    try {
      const { app } = await api.updateBranding(appId, {});
      setDirty(false);
      onSaved(app);
      setAssistantName("");
      setLogoUrl("");
      setLogoOnly(false);
      setHasCustomColor(false);
      setPrimaryColor(DEFAULT_PRIMARY_COLOR);
      setHasTextColor(false);
      setTextColor("");
      setLauncherCss("");
      setChatCss("");
      setWelcomeMessage("");
      setTone("");
      setOrbChat(false);
      setVoice("standard");
      setConfirmReset(false);
    } catch (err) {
      setMsg(errText(err));
    } finally {
      setResetting(false);
    }
  }

  return (
    <div class="card">
      <div class="card-head">
        <div><h2>{t("brand.title")}</h2><p>{t("brand.sub")}</p></div>
        <button type="button" class="btn ghost sm" disabled={busy} onClick={() => setConfirmReset(true)}>
          {t("brand.resetAll")}
        </button>
      </div>
      {msg && <Msg kind="err" text={msg} />}
      {/* „atins de om" se marchează pe formular, nu în fiecare handler: orice input/
          select/textarea de dedesubt trimite evenimentul în sus (upload-ul de logo,
          care setează câmpul programatic, își pune singur `dirty`). */}
      <form onSubmit={save} onInput={() => setDirty(true)} onChange={() => setDirty(true)} class="brand-form">
        <div class="brand-split">
          <div class="brand-fields">
            <div class="brand-row">
              <div style={{ flex: "1 1 200px", minWidth: "180px" }}>
                <label style={{ marginTop: 0 }} for={`${appId}-brand-name`}>{t("brand.name")}</label>
                <input
                  id={`${appId}-brand-name`}
                  value={assistantName}
                  onInput={(e) => setAssistantName((e.target as HTMLInputElement).value)}
                  placeholder={t("brand.namePh")}
                  maxLength={60}
                  disabled={busy}
                />
              </div>
              <div style={{ flex: "1 1 140px", minWidth: "130px" }}>
                <label style={{ marginTop: 0 }} for={`${appId}-brand-tone`}>{t("brand.tone")}</label>
                <select id={`${appId}-brand-tone`} value={tone} onInput={(e) => setTone((e.target as HTMLSelectElement).value as WidgetTone | "")} disabled={busy}>
                  {TONE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label()}</option>
                  ))}
                </select>
              </div>
              <div style={{ flex: "2 1 240px", minWidth: "200px" }}>
                <label style={{ marginTop: 0 }} for={`${appId}-brand-welcome`}>{t("brand.welcome")}</label>
                <input
                  id={`${appId}-brand-welcome`}
                  value={welcomeMessage}
                  onInput={(e) => setWelcomeMessage((e.target as HTMLInputElement).value)}
                  placeholder={t("brand.welcomePh")}
                  maxLength={300}
                  disabled={busy}
                />
              </div>
            </div>

            {/* Mesajul de „nu am răspuns" — servit de backend (fallbackAgent.ts) când
                LLM-ul pică de tot; gol = default-ul nostru neutru. */}
            <div>
              <label for={`${appId}-brand-fallback`}>{t("brand.fallback")}</label>
              <input
                id={`${appId}-brand-fallback`}
                value={fallbackMessage}
                onInput={(e) => setFallbackMessage((e.target as HTMLInputElement).value)}
                placeholder={t("brand.fallbackPh")}
                maxLength={300}
                disabled={busy}
              />
              <p class="muted" style={{ margin: "4px 0 0", fontSize: "12px" }}>{t("brand.fallbackHint")}</p>
            </div>

            {/* Cele două culori împreună: fundal (primary) + cerneala de peste el (text). */}
            <div class="brand-row">
              <div>
                <label for={`${appId}-brand-color`}>{t("brand.color")}</label>
                <div class="color-field">
                  <input
                    type="color"
                    aria-label={t("brand.color")}
                    value={primaryColor}
                    onInput={(e) => {
                      setHasCustomColor(true);
                      setPrimaryColor((e.target as HTMLInputElement).value);
                    }}
                    disabled={busy}
                  />
                  <input
                    id={`${appId}-brand-color`}
                    value={hexDraft}
                    onInput={(e) => {
                      const v = (e.target as HTMLInputElement).value;
                      setHexDraft(v);
                      if (/^#[0-9a-f]{6}$/i.test(v)) {
                        setHasCustomColor(true);
                        setPrimaryColor(v);
                      }
                    }}
                    onBlur={() => setHexDraft(primaryColor)}
                    placeholder={DEFAULT_PRIMARY_COLOR}
                    maxLength={7}
                    style={{ width: "90px", fontFamily: "ui-monospace, Menlo, monospace" }}
                    disabled={busy}
                  />
                </div>
              </div>
              <div>
                <label for={`${appId}-brand-text-color`}>{t("brand.textColor")}</label>
                <div class="color-field">
                  <input
                    type="color"
                    aria-label={t("brand.textColor")}
                    value={effectiveTextColor}
                    onInput={(e) => {
                      setHasTextColor(true);
                      setTextColor((e.target as HTMLInputElement).value);
                    }}
                    disabled={busy}
                  />
                  <input
                    id={`${appId}-brand-text-color`}
                    value={textHexDraft}
                    onInput={(e) => {
                      const v = (e.target as HTMLInputElement).value;
                      setTextHexDraft(v);
                      if (/^#[0-9a-f]{6}$/i.test(v)) {
                        setHasTextColor(true);
                        setTextColor(v);
                      }
                    }}
                    onBlur={() => setTextHexDraft(effectiveTextColor)}
                    maxLength={7}
                    style={{ width: "90px", fontFamily: "ui-monospace, Menlo, monospace" }}
                    disabled={busy}
                  />
                </div>
              </div>
            </div>
            <p class="brand-hint">{t("brand.textColorHint")}</p>

            <label>{t("brand.logo")}</label>
            <div class="row brand-logo-row">
              <ToggleBubblePreview
                logoUrl={logoUrl}
                color={hasCustomColor ? primaryColor : DEFAULT_PRIMARY_COLOR}
                logoOnly={logoOnly}
                defaultBrand={!hasCustomColor}
                presetIcon={presetIcon}
                ink={effectiveTextColor}
              />
              {/* Iconițele predefinite: fiecare swatch e o mini-bulină în culoarea aleasă,
                  deci alegerea e o previzualizare, nu un simbol abstract. Un preset și un
                  logo custom sunt căi alternative - alegerea unuia golește celălalt, iar
                  bulina mare din stânga arată mereu câștigătorul. */}
              <div class="brand-icon-presets" role="group" aria-label={t("brand.iconPresets")}>
                <button
                  type="button"
                  class="brand-icon-swatch"
                  aria-pressed={!logoUrl.trim() && !presetIcon}
                  title={t("brand.iconDefault")}
                  style={{ background: hasCustomColor ? primaryColor : DEFAULT_PRIMARY_COLOR }}
                  disabled={busy}
                  onClick={() => {
                    setDirty(true);
                    setPresetIcon("");
                    setLogoUrl("");
                    setLogoOnly(false);
                  }}
                >
                  {/* exact fallback-ul SDK-ului: badge-ul raster fără culoare custom, cristalul adaptiv cu */}
                  {hasCustomColor ? <BrandMark /> : <img src={logoBadge} alt="" />}
                </button>
                {PRESET_TOGGLE_ICON_IDS.map((id) => (
                  <button
                    key={id}
                    type="button"
                    class="brand-icon-swatch"
                    aria-pressed={!logoUrl.trim() && presetIcon === id}
                    title={PRESET_ICON_LABELS[id]()}
                    aria-label={PRESET_ICON_LABELS[id]()}
                    style={{ background: hasCustomColor ? primaryColor : DEFAULT_PRIMARY_COLOR, color: effectiveTextColor }}
                    disabled={busy}
                    onClick={() => {
                      setDirty(true);
                      setPresetIcon(id);
                      setLogoUrl("");
                      setLogoOnly(false);
                    }}
                    dangerouslySetInnerHTML={{ __html: PRESET_TOGGLE_ICONS[id] }}
                  />
                ))}
              </div>
            </div>
            <div class="row brand-logo-row">
              <input
                aria-label={t("brand.logo")}
                value={logoUrl}
                onInput={(e) => setLogoUrl((e.target as HTMLInputElement).value)}
                placeholder={t("brand.logoPh")}
                maxLength={500}
                disabled={busy || uploadingLogo}
                /* bază reală, nu `flex: 1` (bază 0): altfel câmpul se strânge la
                   nimic lângă bulină și butonul de upload, în loc să treacă pe
                   rândul următor - `.row` are deja flex-wrap */
                style={{ flex: "1 1 180px" }}
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => void handleLogoFile((e.target as HTMLInputElement).files?.[0])}
              />
              <button
                type="button"
                class="btn ghost sm"
                disabled={busy || uploadingLogo}
                onClick={() => fileInputRef.current?.click()}
              >
                {uploadingLogo ? t("brand.logoUploading") : t("brand.logoUpload")}
              </button>
              {/* „Just logo" chiar pe linia logo-ului; inert fără un logo încărcat */}
              <label class="brand-check" title={t("brand.logoOnlyHint")}>
                <input
                  type="checkbox"
                  checked={logoOnly}
                  onInput={(e) => setLogoOnly((e.target as HTMLInputElement).checked)}
                  disabled={busy || !logoUrl.trim()}
                />
                {t("brand.logoOnly")}
              </label>
            </div>
            <p class="brand-hint">{logoUrl.trim() ? t("brand.logoHint") : t("brand.iconHint")}</p>

            {/* CSS custom - sub linia de logo; doar white-label */}
            {whiteLabel ? (
              <div class="brand-row">
                <div style={{ flex: "1 1 0", minWidth: 0 }}>
                  <label for={`${appId}-brand-css-launcher`}>{t("brand.customCssLauncher")}</label>
                  <textarea
                    id={`${appId}-brand-css-launcher`}
                    value={launcherCss}
                    onInput={(e) => setLauncherCss((e.target as HTMLTextAreaElement).value)}
                    placeholder={t("brand.customCssLauncherPh")}
                    maxLength={4000}
                    rows={3}
                    spellcheck={false}
                    disabled={busy}
                  />
                </div>
                <div style={{ flex: "1 1 0", minWidth: 0 }}>
                  <label for={`${appId}-brand-css-chat`}>{t("brand.customCssChat")}</label>
                  <textarea
                    id={`${appId}-brand-css-chat`}
                    value={chatCss}
                    onInput={(e) => setChatCss((e.target as HTMLTextAreaElement).value)}
                    placeholder={t("brand.customCssChatPh")}
                    maxLength={4000}
                    rows={3}
                    spellcheck={false}
                    disabled={busy}
                  />
                </div>
              </div>
            ) : (
              <p class="brand-hint brand-css-locked">
                {t("brand.customCssLocked")}{" "}
                <a href="#" onClick={(e) => { e.preventDefault(); goUpgrade(); }}>{upgradeLabel("brand.customCssLockedCta")}</a>
              </p>
            )}

            {/* Asistentul „orb": suprafață nouă, opt-in - fără el, panoul rămâne cel clasic de chat */}
            <label style={{ marginTop: 0 }}>{t("brand.assistantExperience")}</label>
            <label class="brand-check" title={t("brand.orbChatHint")}>
              <input
                type="checkbox"
                checked={orbChat}
                onInput={(e) => setOrbChat((e.target as HTMLInputElement).checked)}
                disabled={busy}
              />
              {t("brand.orbChat")}
            </label>
            <p class="brand-hint">{t("brand.orbChatHint")}</p>
            <div style={{ maxWidth: "220px" }}>
              <label for={`${appId}-brand-voice`}>{t("brand.voice")}</label>
              <select
                id={`${appId}-brand-voice`}
                value={voice}
                onInput={(e) => setVoice((e.target as HTMLSelectElement).value as WidgetVoice)}
                disabled={busy || !orbChat}
              >
                {VOICE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label()}</option>
                ))}
              </select>
              <p class="brand-hint">{t("brand.voiceHint")}</p>
            </div>
          </div>

          <div class="widget-preview-wrap">
            <div class="widget-preview-label">{t("brand.previewTitle")}</div>
            {/* Bug: previzualizarea recalcula singură contrastul (inkOnPrimary) chiar
                și fără o culoare custom, ignorând regula reală a SDK-ului - fără
                culoare custom, cerneala rămâne alb fix (DEFAULT_TEXT_COLOR), nu se
                calculează. `effectiveTextColor` e deja calculat mai sus cu exact
                aceeași regulă ca text-ul din câmpul hex - o singură sursă de adevăr. */}
            <WidgetPreviewMock
              name={assistantName}
              color={hasCustomColor ? primaryColor : DEFAULT_PRIMARY_COLOR}
              textColor={effectiveTextColor}
              welcome={welcomeMessage}
              poweredBy={showsPoweredBy}
            />
            {showsPoweredBy && (
              <p class="brand-hint">
                {t("brand.poweredByNote")}{" "}
                <a href="#" onClick={(e) => { e.preventDefault(); goUpgrade(); }}>{upgradeLabel("brand.poweredByNoteCta")}</a>
              </p>
            )}
          </div>

          <button class="btn primary sm brand-save" disabled={busy} type="submit">
            {saved ? <><IconCheck /> {t("brand.saved")}</> : t("brand.save")}
          </button>
        </div>
      </form>

      {confirmReset && (
        <Modal onClose={() => setConfirmReset(false)}>
          <h3>{t("brand.resetTitle")}</h3>
          <p>{t("brand.resetBody")}</p>
          <div class="actions">
            <button class="btn ghost" onClick={() => setConfirmReset(false)}>{t("common.cancel")}</button>
            <button class="btn solid-danger" disabled={resetting} onClick={() => void doReset()}>{t("brand.resetConfirm")}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ---------- agent notes (instrucțiuni libere pentru agent, orice plan) ----------

// AGENT_NOTES_MAX_CHARS (shared/config.ts) - aceeași constantă cu agentNotesSchema
// (backend routes/admin.ts) și cu brieful builderului (runtime.ts, builderService.ts).
// Erau 4 copii cu valori diferite (contorul de-aici arăta „N/2000" în roșu deși
// backendul deja accepta 4000) - vezi comentariul de pe AGENT_NOTES_MAX_CHARS.
const AGENT_NOTES_MAX = AGENT_NOTES_MAX_CHARS;

/**
 * Notițe libere ale proprietarului pentru agent - card SEPARAT de branding: e o
 * preocupare diferită (text injectat server-side în prompt, care nu iese niciodată
 * prin /config), nu doar personalizare de aspect. Fără gate de plan, la fel ca
 * branding-ul (personalizarea widgetului e gratuită pe orice plan din 31 iul 2026).
 */
function AgentNotesCard({ appId, initial }: { appId: string; initial: string | null }) {
  const [notes, setNotes] = useState(initial ?? "");
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function save(e: Event) {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      await api.updateAgentNotes(appId, notes);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (err) {
      setMsg(errText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="card">
      <div class="card-head">
        <div><h2>{t("notes.title")}</h2><p>{t("notes.sub")}</p></div>
      </div>
      {msg && <Msg kind="err" text={msg} />}
      <form onSubmit={save}>
        <textarea
          aria-label={t("notes.title")}
          value={notes}
          onInput={(e) => setNotes((e.target as HTMLTextAreaElement).value)}
          placeholder={t("notes.placeholder")}
          maxLength={AGENT_NOTES_MAX}
          rows={5}
          disabled={busy}
          style={{ width: "100%", resize: "vertical", fontFamily: "inherit" }}
        />
        <div class="row" style={{ justifyContent: "space-between", alignItems: "center", marginTop: "6px" }}>
          <span class="muted" style={{ fontSize: "12px" }}>{t("notes.hint")}</span>
          <span class="muted" style={{ fontSize: "12px", fontVariantNumeric: "tabular-nums" }}>{t("notes.count", notes.length, AGENT_NOTES_MAX)}</span>
        </div>
        <button class="btn primary sm" disabled={busy} type="submit" style={{ marginTop: "12px" }}>
          {saved ? <><IconCheck /> {t("notes.saved")}</> : t("notes.save")}
        </button>
      </form>
    </div>
  );
}

// ---------- voice minutes per visitor (F-06) ----------

/**
 * Limita de minute de voce per vizitator - card mic, lângă notițe. Gol =
 * implicitul produsului (3). Validarea numerică oglindește voiceMinutesSchema
 * din backend (1..60, întreg).
 */
function VoiceMinutesCard({ appId, initial }: { appId: string; initial: number | null }) {
  const [value, setValue] = useState(initial === null ? "" : String(initial));
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function save(e: Event) {
    e.preventDefault();
    const trimmed = value.trim();
    const minutes = trimmed === "" ? null : Number(trimmed);
    if (minutes !== null && (!Number.isInteger(minutes) || minutes < 1 || minutes > 60)) {
      setMsg(t("vmin.invalid"));
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await api.updateVoiceMinutes(appId, minutes);
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (err) {
      setMsg(errText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="card">
      <div class="card-head">
        <div><h2>{t("vmin.title")}</h2><p>{t("vmin.sub")}</p></div>
      </div>
      {msg && <Msg kind="err" text={msg} />}
      <form onSubmit={save}>
        <div class="row" style={{ alignItems: "center", gap: "10px" }}>
          <input
            aria-label={t("vmin.title")}
            type="number"
            min={1}
            max={60}
            step={1}
            value={value}
            onInput={(e) => setValue((e.target as HTMLInputElement).value)}
            placeholder="3"
            disabled={busy}
            style={{ width: "90px" }}
          />
          <span class="muted" style={{ fontSize: "12px" }}>{t("vmin.hint")}</span>
        </div>
        <button class="btn primary sm" disabled={busy} type="submit" style={{ marginTop: "12px" }}>
          {saved ? <><IconCheck /> {t("vmin.saved")}</> : t("vmin.save")}
        </button>
      </form>
    </div>
  );
}

// ---------- conversations (Q&A history) ----------

/** yyyy-mm-dd (input type=date) → epoch ms, la începutul/sfârșitul zilei locale. */
function dateInputToMs(value: string, endOfDay: boolean): number | undefined {
  if (!value) return undefined;
  const d = new Date(`${value}T${endOfDay ? "23:59:59.999" : "00:00:00.000"}`);
  return Number.isNaN(d.getTime()) ? undefined : d.getTime();
}

/**
 * Istoricul conversațiilor, UN SINGUR card, pe ORICE plan: din 2 aug 2026
 * `/apps/:appId/conversations` nu mai e gated, deci aici nu există nici
 * verificare de plan, nici paywall - conversațiile sunt materia primă a
 * clientului, nu un feature de vândut.
 */
const CONV_PAGE_SIZE = 8;

/** Sumarul „gol" - folosit când primul fetch eșuează, ca banda de sus să nu
 *  rămână blocată pe skeleton la infinit. */
const EMPTY_CONV_SUMMARY: ConversationsSummary = { totalThisMonth: 0, topPages: [], avgSessionLength: 0 };

function ConversationsCard({ appId, onSummary }: { appId: string; onSummary?: (s: ConversationsSummary) => void }) {
  const [sessions, setSessions] = useState<ConversationSession[] | null>(null);
  const [totalSessions, setTotalSessions] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  // fără filtru implicit: „toate" înseamnă tot ce mai există (retenția de 90
  // de zile e garantată de purjarea nightly, nu de un `from` implicit aici -
  // vezi comentariul din fetchConversationSessions).
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  // filtru de canal: undefined = toate (nu se trimite deloc la server)
  const [channel, setChannel] = useState<"text" | "voice" | undefined>(undefined);

  // schimbarea filtrului de dată sau de canal invalidează paginarea curentă
  useEffect(() => setPage(1), [from, to, channel]);

  // Sumarul (banda de sus a tabului) se cere DOAR la primul fetch pentru un site:
  // e o cifră pe lună, care nu depinde nici de pagină, nici de filtrul de dată,
  // deci recalcularea lui la fiecare paginare ar fi muncă de server pe degeaba.
  const summaryAsked = useRef(false);
  useEffect(() => { summaryAsked.current = false; }, [appId]);

  // callbackul stă într-un ref, ca o funcție recreată la fiecare render în
  // părinte să NU redeclanșeze fetch-ul (ar fi o buclă de cereri).
  const onSummaryRef = useRef(onSummary);
  onSummaryRef.current = onSummary;

  useEffect(() => {
    const wantSummary = !summaryAsked.current;
    summaryAsked.current = true;
    setSessions(null);
    api
      .conversations(appId, { page, pageSize: CONV_PAGE_SIZE, from: dateInputToMs(from, false), to: dateInputToMs(to, true), summary: wantSummary, channel })
      .then((r) => {
        setSessions(r.sessions);
        setTotalSessions(r.totalSessions);
        setTotalPages(r.totalPages);
        if (wantSummary) onSummaryRef.current?.(r.summary ?? EMPTY_CONV_SUMMARY);
        if (r.page !== page) setPage(r.page); // serverul plafonează pagina cerută la totalPages
      })
      .catch(() => {
        setSessions([]); setTotalSessions(0); setTotalPages(1);
        if (wantSummary) onSummaryRef.current?.(EMPTY_CONV_SUMMARY);
      });
  }, [appId, from, to, channel, page]);

  const isDefaultRange = from === "" && to === "";

  return (
    // `conversations-card`: clasă STABILĂ, țintită de „Excluded content" al
    // app-ului consolei - conversațiile end-userilor nu intră în snapshotul
    // trimis la LLM când clientul întreabă asistentul consolei (registru G20).
    <div class="card conversations-card">
      <div class="card-head">
        <div>
          <h2>
            {t("conv.title")}
            {totalSessions > 0 && <span class="badge muted" style={{ marginLeft: "8px", verticalAlign: "middle" }}>{visitsLabel(totalSessions)}</span>}
          </h2>
          <p>{t("conv.sub")}</p>
        </div>
        <div class="row conv-filters" style={{ marginBottom: 0 }}>
          <div class="conv-channel-seg" role="group" aria-label={t("conv.channel.all")}>
            <button type="button" class={`conv-channel-btn${channel === undefined ? " active" : ""}`} onClick={() => setChannel(undefined)}>{t("conv.channel.all")}</button>
            <button type="button" class={`conv-channel-btn${channel === "text" ? " active" : ""}`} onClick={() => setChannel("text")}>{t("conv.channel.text")}</button>
            <button type="button" class={`conv-channel-btn${channel === "voice" ? " active" : ""}`} onClick={() => setChannel("voice")}>{t("conv.channel.voice")}</button>
          </div>
          <label>{t("conv.filterFrom")} <input type="date" value={from} onInput={(e) => setFrom((e.target as HTMLInputElement).value)} /></label>
          <label>{t("conv.filterTo")} <input type="date" value={to} onInput={(e) => setTo((e.target as HTMLInputElement).value)} /></label>
          {!isDefaultRange && <button type="button" class="btn ghost sm" onClick={() => { setFrom(""); setTo(""); }}>{t("conv.filterClear")}</button>}
        </div>
      </div>

      {sessions === null && (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <Skeleton h="96px" style={{ borderRadius: "13px" }} />
          <Skeleton h="96px" style={{ borderRadius: "13px" }} />
        </div>
      )}
      {sessions && sessions.length === 0 && <EmptyMini icon={<IconChat />} text={t("conv.empty")} />}
      {sessions && sessions.length > 0 && (
        <>
          <div class="conv-session-list">
            {sessions.map((s, i) => (
              <ConversationSessionRow
                key={s.sessionId ?? `legacy-${s.messages[0]?.id}`}
                session={s}
                index={totalSessions - ((page - 1) * CONV_PAGE_SIZE + i)}
              />
            ))}
          </div>
          {totalPages > 1 && <Pager page={page} totalPages={totalPages} onChange={setPage} />}
        </>
      )}
    </div>
  );
}

/**
 * Paginare standard - Prev / numere (cu elipsă la liste lungi) / Next.
 * Fereastra din jurul paginii curente ține 5 numere vizibile, plus prima și
 * ultima pagină întotdeauna ancorate, ca omul să știe mereu cât de departe e.
 */
function pagerWindow(page: number, totalPages: number): Array<number | "…"> {
  const span = 1; // pagini vizibile de fiecare parte a celei curente
  const items: Array<number | "…"> = [];
  const add = (n: number) => { if (!items.includes(n)) items.push(n); };
  add(1);
  if (page - span > 2) items.push("…");
  for (let n = Math.max(2, page - span); n <= Math.min(totalPages - 1, page + span); n++) add(n);
  if (page + span < totalPages - 1) items.push("…");
  if (totalPages > 1) add(totalPages);
  return items;
}

function Pager({ page, totalPages, onChange }: { page: number; totalPages: number; onChange: (p: number) => void }) {
  return (
    <nav class="pager" aria-label={t("pager.label")}>
      <button type="button" class="pager-btn" disabled={page <= 1} onClick={() => onChange(page - 1)} aria-label={t("pager.prev")}>
        <IconChevronLeft />
      </button>
      {pagerWindow(page, totalPages).map((it, i) =>
        it === "…" ? (
          <span class="pager-ellipsis" key={`e${i}`}>…</span>
        ) : (
          <button
            type="button"
            key={it}
            class={`pager-btn pager-num${it === page ? " active" : ""}`}
            aria-current={it === page ? "page" : undefined}
            onClick={() => onChange(it)}
          >
            {it}
          </button>
        ),
      )}
      <button type="button" class="pager-btn" disabled={page >= totalPages} onClick={() => onChange(page + 1)} aria-label={t("pager.next")}>
        <IconChevronRight />
      </button>
    </nav>
  );
}

/**
 * O sesiune afișată compact: prima întrebare e titlul (ea spune ce voia
 * vizitatorul; numărul de sesiune rămâne doar ca reper discret), plus data,
 * durata și numărul de mesaje. La click se desfășoară firul întreg.
 */
/**
 * Un chip = un rând al `agentActions` transformat în text scurt, sau `null`
 * pentru tipuri necunoscute / neredabile (ignorate tăcut, ca la nivel de
 * schemă). Formele agregabile (highlight/tooltip/scrollTo/waitForElement) se
 * colapsează în afara acestei funcții - vezi `agentActionChips`.
 */
function formatAgentAction(a: any): string | null {
  if (!a || typeof a !== "object" || typeof a.type !== "string") return null;
  switch (a.type) {
    case "add_to_cart": {
      const label = a.title ?? a.variantId ?? "";
      return a.quantity > 1 ? t("conv.act.addToCartQty", label, a.quantity) : t("conv.act.addToCart", label);
    }
    case "set_cart_quantity": {
      const label = a.title ?? a.variantId ?? "";
      return a.quantity === 0 ? t("conv.act.removedFromCart", label) : t("conv.act.setQuantity", label, a.quantity ?? "");
    }
    case "open_checkout":
      return t("conv.act.openCheckout");
    case "open_search": {
      // Intervalul de preț poate avea doar un capăt - nu randa niciodată „(–1500)".
      const range =
        a.minPrice !== undefined && a.maxPrice !== undefined
          ? t("conv.act.range.both", a.minPrice, a.maxPrice)
          : a.minPrice !== undefined
            ? t("conv.act.range.min", a.minPrice)
            : a.maxPrice !== undefined
              ? t("conv.act.range.max", a.maxPrice)
              : null;
      return range ? t("conv.act.searchedRange", a.query ?? "", range) : t("conv.act.searched", a.query ?? "");
    }
    case "navigate":
      return t("conv.act.navigate", a.path ?? "");
    case "show_products":
      return t("conv.act.showedProducts", Array.isArray(a.items) ? a.items.map((it: any) => it?.title).filter(Boolean).join(", ") : "");
    case "show_comparison":
      return t("conv.act.compared", Array.isArray(a.items) ? a.items.map((it: any) => it?.title).filter(Boolean).join(" vs ") : "");
    case "askUser":
      return t("conv.act.askedClarification");
    case "completeTask":
      return t("conv.act.taskCompleted");
    default:
      return null;
  }
}

/** Tipurile de ghidare UI se agregă într-un singur chip, indiferent de câte apar. */
const GUIDANCE_ACTION_TYPES = new Set(["highlight", "tooltip", "scrollTo", "waitForElement"]);

function agentActionChips(actions: unknown[] | null | undefined): string[] {
  if (!actions || actions.length === 0) return [];
  const chips: string[] = [];
  let guided = false;
  for (const a of actions) {
    const type = a && typeof a === "object" ? (a as any).type : undefined;
    if (typeof type === "string" && GUIDANCE_ACTION_TYPES.has(type)) {
      guided = true;
      continue;
    }
    const label = formatAgentAction(a);
    if (label) chips.push(label);
  }
  if (guided) chips.push(t("conv.act.guided"));
  return chips;
}

function ConversationSessionRow({ session, index }: { session: ConversationSession; index: number }) {
  const [open, setOpen] = useState(false);
  const count = session.messages.length;
  const preview = session.messages[0]?.question ?? "";
  const firstPath = session.messages[0]?.path ?? null;
  const hasVoice = session.messages.some((c) => c.channel === "voice");
  return (
    <div class={`conv-session${open ? " open" : ""}`}>
      <button type="button" class="conv-session-row" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span class="conv-session-main">
          <span class="conv-session-num" title={t("conv.session", index)}>{index}</span>
          <span class="conv-session-q">{preview || t("conv.noQuestion")}</span>
        </span>
        <span class="conv-session-meta">
          {hasVoice && <span class="badge muted conv-voice-badge" title={t("conv.voice")}><IconMic /> {t("conv.voice")}</span>}
          {firstPath && <span class="conv-session-path">{firstPath}</span>}
          {firstPath && <span class="conv-dot">·</span>}
          <span>{fmtDate(session.startedAt)}</span>
          <span class="conv-dot">·</span>
          <span>{count === 1 ? t("conv.msgsOne") : t("conv.msgs", count)}</span>
          <span class="conv-chevron"><IconChevronDown /></span>
        </span>
      </button>
      {open && (
        <div class="conv-session-body">
          {session.messages.map((c) => {
            const chips = agentActionChips(c.agentActions);
            return (
              <div class="conv-item" key={c.id}>
                {/* Drumul de dinaintea întrebării: pagini (mono) și click-uri
                    (eticheta vizibilă, între ghilimele) - povestea din spatele
                    întrebării, nu doar întrebarea. Absent pe rândurile vechi. */}
                {c.recentActions && c.recentActions.length > 0 && (
                  <div class="conv-trail">
                    <span class="conv-trail-lab">{t("conv.trail")}</span>
                    {c.recentActions.map((a, j) => (
                      <span class="conv-trail-step" key={j}>
                        {j > 0 && <span class="conv-trail-arrow" aria-hidden="true">→</span>}
                        {a.kind === "nav"
                          ? <span class="conv-trail-nav">{a.path}</span>
                          : <span class="conv-trail-act">“{a.label ?? a.path}”</span>}
                        {(a.n ?? 1) > 1 && <span class="conv-trail-n">×{a.n}</span>}
                      </span>
                    ))}
                  </div>
                )}
                <div class="conv-line q">
                  <span class="who q">Q</span>
                  <span class="tx">{c.question}</span>
                  {c.channel === "voice" && <span class="conv-line-mic" title={t("conv.voice")}><IconMic /></span>}
                </div>
                {/* Tagurile expresive ([warmly], [confidently]) rămân stocate brut - le
                    consumă sinteza vocală - dar aici, la randare, nu au ce căuta sub
                    ochii omului din panou. */}
                <div class="conv-line a"><span class="who a">A</span><span class="tx">{stripAllExpressiveTags(c.answer)}</span></div>
                {chips.length > 0 && (
                  <div class="conv-actions">
                    <span class="conv-actions-lab">{t("conv.actions.label")}</span>
                    {chips.map((label, j) => (
                      <span class="conv-act" key={j}>{label}</span>
                    ))}
                  </div>
                )}
                <div class="conv-meta">{fmtDate(c.createdAt)}{c.path ? ` · ${c.path}` : ""}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============================================================
//  AUTH
// ============================================================

/**
 * Buton "Sign in with Google" (GIS): scriptul e încărcat async din index.html,
 * deci așteptăm până apare window.google. Ascuns dacă backend-ul nu are
 * GOOGLE_CLIENT_ID setat (nimic de inițializat).
 */
function GoogleButton({ onCredential }: { onCredential: (credential: string) => void }) {
  const [clientId, setClientId] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.googleClientId().then((r) => setClientId(r.clientId || null)).catch(() => setClientId(null));
  }, []);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    function tryInit() {
      const google = (window as unknown as { google?: any }).google;
      if (!google?.accounts?.id) {
        if (!cancelled) setTimeout(tryInit, 100);
        return;
      }
      google.accounts.id.initialize({
        client_id: clientId,
        callback: (resp: { credential: string }) => onCredential(resp.credential),
      });
      if (ref.current) {
        google.accounts.id.renderButton(ref.current, { theme: "outline", size: "large", width: 320 });
      }
    }
    tryInit();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  if (!clientId) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "14px", marginBottom: "18px" }}>
      <div ref={ref} />
      <div class="row" style={{ width: "100%", alignItems: "center", gap: "10px" }}>
        <span style={{ flex: 1, height: "1px", background: "var(--line-2)" }} />
        <span class="muted" style={{ fontSize: "12px" }}>{t("auth.orEmail")}</span>
        <span style={{ flex: 1, height: "1px", background: "var(--line-2)" }} />
      </div>
    </div>
  );
}

// Starea formularului de firmă (B2B) - refolosită în register și în poarta de
// profil. addressLine2 e opțional; restul sunt obligatorii (backendul le cere).
interface BizState {
  companyName: string;
  taxId: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  postalCode: string;
  country: string;
}
const EMPTY_BIZ: BizState = { companyName: "", taxId: "", addressLine1: "", addressLine2: "", city: "", postalCode: "", country: "" };

function bizFromProfile(b: Me["business"]): BizState {
  return {
    companyName: b.companyName ?? "",
    taxId: b.taxId ?? "",
    addressLine1: b.addressLine1 ?? "",
    addressLine2: b.addressLine2 ?? "",
    city: b.city ?? "",
    postalCode: b.postalCode ?? "",
    country: b.country ?? "",
  };
}

function toBusinessInput(v: BizState): BusinessProfileInput {
  return {
    companyName: v.companyName.trim(),
    taxId: v.taxId.trim(),
    addressLine1: v.addressLine1.trim(),
    addressLine2: v.addressLine2.trim() || undefined,
    city: v.city.trim(),
    postalCode: v.postalCode.trim(),
    country: v.country,
  };
}

/** Câmpurile de firmă, controlate. Țară = select real (fără „Other" - o factură
 *  cere o țară validă). `required` pe input-uri lasă validarea nativă a formularului. */
// Lista de țări din formularul de firmă. A stat până pe 7 aug 2026 lângă
// PlanView (o folosea și pasul de facturare din checkout); componenta aia a
// dispărut, lista NU - `BusinessFields` o folosește la profilul de firmă, care
// n-are legătură cu cumpărarea. „XX" (Other) e filtrat acolo.
const BILLING_COUNTRIES: [string, string][] = [
  ["ES", "Spain"], ["AT", "Austria"], ["BE", "Belgium"], ["BG", "Bulgaria"], ["HR", "Croatia"],
  ["CY", "Cyprus"], ["CZ", "Czechia"], ["DK", "Denmark"], ["EE", "Estonia"], ["FI", "Finland"],
  ["FR", "France"], ["DE", "Germany"], ["GR", "Greece"], ["HU", "Hungary"], ["IE", "Ireland"],
  ["IT", "Italy"], ["LV", "Latvia"], ["LT", "Lithuania"], ["LU", "Luxembourg"], ["MT", "Malta"],
  ["NL", "Netherlands"], ["PL", "Poland"], ["PT", "Portugal"], ["RO", "Romania"], ["SK", "Slovakia"],
  ["SI", "Slovenia"], ["SE", "Sweden"], ["GB", "United Kingdom"], ["CH", "Switzerland"],
  ["NO", "Norway"], ["US", "United States"], ["CA", "Canada"], ["AU", "Australia"], ["XX", "Other"],
];

function BusinessFields({ v, set, disabled }: { v: BizState; set: (patch: Partial<BizState>) => void; disabled?: boolean }) {
  const bind = (k: keyof BizState) => (e: Event) => set({ [k]: (e.target as HTMLInputElement).value });
  return (
    <>
      <div class="field-row">
        <div style={{ flex: 1 }}>
          <label>{t("biz.companyName")}</label>
          <input value={v.companyName} onInput={bind("companyName")} required maxLength={200} disabled={disabled} placeholder="Acme S.L." />
        </div>
        <div style={{ flex: 1 }}>
          <label>{t("biz.taxId")}</label>
          <input value={v.taxId} onInput={bind("taxId")} required maxLength={50} disabled={disabled} placeholder="ESB12345678" />
        </div>
      </div>
      <label>{t("biz.addressLine1")}</label>
      <input value={v.addressLine1} onInput={bind("addressLine1")} required maxLength={200} disabled={disabled} placeholder="Calle Mayor 1" />
      <label>{t("biz.addressLine2")} <span class="muted" style={{ fontWeight: 400 }}>({t("biz.optional")})</span></label>
      <input value={v.addressLine2} onInput={bind("addressLine2")} maxLength={200} disabled={disabled} />
      <div class="field-row">
        <div style={{ flex: 1 }}>
          <label>{t("biz.city")}</label>
          <input value={v.city} onInput={bind("city")} required maxLength={100} disabled={disabled} placeholder="Madrid" />
        </div>
        <div style={{ flex: 1 }}>
          <label>{t("biz.postalCode")}</label>
          <input value={v.postalCode} onInput={bind("postalCode")} required maxLength={20} disabled={disabled} placeholder="28001" />
        </div>
      </div>
      <label>{t("biz.country")}</label>
      <select value={v.country} onChange={(e) => set({ country: (e.target as HTMLSelectElement).value })} required disabled={disabled}>
        <option value="" disabled>{t("biz.countrySelect")}</option>
        {BILLING_COUNTRIES.filter(([c]) => c !== "XX").map(([code, name]) => (
          <option value={code} key={code}>{name}</option>
        ))}
      </select>
    </>
  );
}

function AuthView({
  onDone,
  notice,
  preferRegister,
  fromDemo,
  prefillEmail,
}: {
  onDone: () => void;
  notice?: { kind: "ok" | "err" | "info"; text: string } | null;
  /** fără hash explicit, deschide „Sign up" (intenție de plan sau venit din demo, fără cont) */
  preferRegister?: boolean;
  /** venit din poarta demo: o linie care spune de ce trebuie un cont */
  fromDemo?: boolean;
  /** emailul de precompletat (după verificare) */
  prefillEmail?: string | null;
}) {
  const [mode, setModeState] = useState<AuthMode>(() => parseAuthMode(preferRegister ? "register" : "login"));
  // Fiecare tab are URL propriu (#/login, #/register, #/forgot) - link direct posibil,
  // și back/forward-ul browserului comută tab-ul (de-aici listenerul de hashchange).
  useEffect(() => {
    const on = () => setModeState(parseAuthMode());
    addEventListener("hashchange", on);
    return () => removeEventListener("hashchange", on);
  }, []);
  function setMode(m: AuthMode) {
    setModeState(m);
    setRegisterStep("account");
    setShowResend(false);
    navigate(`#/${m}`);
  }
  // Register în 2 pași: cont (email/parolă) apoi firmă - un singur
  // ecran cu tot formularul de firmă părea interminabil. Pasul e stare locală,
  // nu în hash: e un progres în interiorul tab-ului „register", nu o rută nouă.
  const [registerStep, setRegisterStep] = useState<"account" | "business">("account");
  const [email, setEmail] = useState(prefillEmail ?? "");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  // Nebifată implicit: consimțământul se DĂ, nu se presupune (Art. 7 GDPR;
  // backend-ul are același default `false`, admin.ts → registerSchema).
  const [marketingConsent, setMarketingConsent] = useState(false);
  // datele de firmă (B2B) - obligatorii la register (serviciul e pentru firme)
  const [biz, setBiz] = useState<BizState>(EMPTY_BIZ);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err" | "info"; text: string } | null>(notice ?? null);
  // După register (sau un login blocat de email neverificat) arătăm butonul de
  // retrimitere sub mesaj - altfel cine nu primește emailul (ex. inbox plin,
  // spam) rămâne fără nicio cale să-l ceară din nou fără să se logheze.
  const [showResend, setShowResend] = useState(false);

  async function submit(e: Event) {
    e.preventDefault();
    if (mode === "register" && registerStep === "account") {
      // Pasul 1 nu atinge API-ul - doar validează local și trece la firmă.
      // Enter-ul din câmpurile de cont avansează pasul, nu trimite formularul
      // incomplet (business-ul e încă gol în acest punct).
      if (password !== password2) {
        setMsg({ kind: "err", text: t("auth.mismatch") });
        return;
      }
      setMsg(null);
      setRegisterStep("business");
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      if (mode === "forgot") {
        await api.forgotPassword(email);
        setMsg({ kind: "info", text: t("auth.forgotSent") });
        return;
      }
      if (mode === "register") {
        // Nu logăm la înregistrare: contul e creat, dar accesul e blocat până
        // la verificarea emailului. Trimitem userul spre login cu instrucțiuni.
        // Backendul întoarce acum și `sessionToken` la register, dar NU-l stocăm
        // aici cu setKey(): contul e neverificat, deci orice cerere autentificată
        // (/me) ar da 403 email_not_verified, iar reloadMe() îl deslogă automat -
        // am intra direct în bucla login→verify pe care fluxul de mai jos o evită
        // prin design (mode="login" + mesaj „verifică emailul").
        const result = await api.register(email, password, toBusinessInput(biz), marketingConsent);
        setMode("login");
        setPassword("");
        setPassword2("");
        setBiz(EMPTY_BIZ);
        // Dacă register a trimis emailul de verificare, pornim cooldown-ul de
        // resend acum, ca timer-ul din UI să fie coerent cu cel din backend. Dacă
        // trimiterea a eșuat (emailSent: false), NU: nu există nimic de așteptat,
        // iar „Resend" trebuie să rămână la îndemână. Butonul se arată oricum
        // (ecranul de login e locul unde aterizează userul).
        const sent = result.emailSent !== false;
        if (sent) markVerificationSent(email);
        setShowResend(true);
        // Contul e creat oricum; despre cod spunem adevărul, fără să-l prezentăm
        // ca eșec al înregistrării.
        const base = sent ? t("auth.registerVerify") : t("auth.registerNoEmail");
        const ref = result.refCode;
        if (ref?.applied) {
          setMsg({ kind: sent ? "ok" : "info", text: `${base} ${t("auth.refCodeApplied", ref.benefit.code, ref.benefit.benefitValue)}` });
        } else if (ref) {
          setMsg({ kind: "info", text: `${base} ${t("auth.refCodeBad")}` });
        } else {
          setMsg({ kind: "info", text: base });
        }
        return;
      }
      const result = await api.login(email, password);
      setKey(result.sessionToken);
      onDone();
    } catch (err) {
      // Login cu parolă corectă dar email neverificat: retrimitem linkul și
      // explicăm. Nu emitem cheie (poarta din backend a respins cu 403).
      if (err instanceof ApiError && err.status === 403 && (err.data as { error?: string })?.error === "email_not_verified") {
        // Login cu email neverificat: cerem o retrimitere (backendul respectă
        // cooldown-ul de 5 min - dacă e prea devreme, nu pleacă un al doilea
        // email) și lăsăm butonul cu timer la îndemână pentru încă o încercare.
        // Rezultatul se citește: un resend căzut (emailSent: false) sau respins
        // (429) nu are voie să fie prezentat drept „am trimis un link nou".
        let sent = true;
        try {
          sent = (await api.resendVerification(email)).emailSent !== false;
        } catch {
          sent = false;
        }
        if (sent) markVerificationSent(email);
        setShowResend(true);
        setMsg({ kind: "info", text: sent ? t("auth.verifyFirst") : t("auth.verifyFirstNoEmail") });
        return;
      }
      setMsg({ kind: "err", text: err instanceof ApiError && err.status === 409 ? t("auth.emailTaken") : errText(err) });
    } finally {
      setBusy(false);
    }
  }

  async function submitGoogle(credential: string) {
    setBusy(true);
    setMsg(null);
    try {
      const result = await api.googleAuth(credential);
      setKey(result.sessionToken);
      onDone();
    } catch (err) {
      setMsg({ kind: "err", text: errText(err) });
      setBusy(false);
    }
  }

  const feats = ["auth.f1", "auth.f2", "auth.f3"] as const;
  return (
    <div class="auth-wrap">
      <div class="auth-brand">
        {/* .brand-lockup: pe mobil panoul se reduce la acest bloc - restul se ascunde,
            vezi styles.css. Fără el, ecranul de autentificare rămânea un formular
            gol, fără numele produsului nicăieri. */}
        {/* Link, nu doar marcă: de pe login nu exista nicio cale înapoi la site.
            Consola stă pe alt subdomeniu decât landing-ul (app. vs apex; pe staging ambele sub .staging), deci URL absolut - env-aware prin SITE_ORIGIN (vezi sus). */}
        <a class="row brand-lockup" href={`${SITE_ORIGIN}/`} style={{ gap: "12px" }}>
          <div class="mark"><img src={logoBadge} alt="" /></div>
          <strong style={{ fontSize: "17px" }}>AskMyWebsite<span style={{ color: "var(--brand-600)" }}>[AI]</span></strong>
        </a>
        <div>
          {/* Aceeași promisiune ca hero-ul de pe landing (deploy/landing/index.html):
              cine vine din „Client login" trebuie să recunoască pagina. */}
          <h2>Your website, with a built-in <span class="grad">AI&nbsp;concierge</span>.</h2>
          <p>One script tag adds an AI concierge that answers your visitors' questions and shows them where to click, live on the page - fewer support tickets, more sales, zero code changes.</p>
          <div class="feats">
            {feats.map((f) => (
              <div class="feat" key={f}>
                <span class="ic"><IconCheck /></span> {t(f)}
              </div>
            ))}
          </div>
        </div>
        <div style={{ color: "#7d78ad", fontSize: "12.5px" }}>
          © AskMyWebsite AI ·{" "}
          <a href={`${SITE_ORIGIN}/terms`} target="_blank" rel="noopener" style={{ color: "#7d78ad" }}>Terms</a> ·{" "}
          <a href={`${SITE_ORIGIN}/privacy`} target="_blank" rel="noopener" style={{ color: "#7d78ad" }}>Privacy</a> ·{" "}
          <a href={`${SITE_ORIGIN}/dpa`} target="_blank" rel="noopener" style={{ color: "#7d78ad" }}>DPA</a>
        </div>
      </div>

      <div class="auth-form">
        <div class="box">
          {/* Tab-urile există doar dacă e ceva de ales: cu înregistrările
              închise rămâne un singur mod, iar un tab „Register" care duce tot
              la login ar fi o promisiune falsă. */}
          {mode !== "forgot" && SIGNUPS_OPEN && (
            <div class="tabs">
              <button class={mode === "register" ? "active" : ""} onClick={() => { setMode("register"); setMsg(null); }}>{t("auth.register")}</button>
              <button class={mode === "login" ? "active" : ""} onClick={() => { setMode("login"); setMsg(null); }}>{t("auth.login")}</button>
            </div>
          )}
          <h3>
            {mode === "login" ? t("auth.h.login") : mode === "forgot" ? t("auth.h.forgot") : t("auth.h.register")}
          </h3>
          <p class="sub">
            {mode === "login" ? t("auth.sub.login") : mode === "forgot" ? t("auth.sub.forgot") : t("auth.sub.registerBiz")}
          </p>
          {/* Venit din poarta demo: landing-ul promite un demo, iar ecranul ăsta
              trebuie să spună de ce se cere un cont. O singură linie, doar pe Sign up. */}
          {fromDemo && mode === "register" && <p class="sub" style={{ fontWeight: 600 }}>{t("auth.fromDemo")}</p>}
          {mode !== "forgot" && <GoogleButton onCredential={submitGoogle} />}
          <form onSubmit={submit}>
            {/* Doar câmpurile care diferă între pași intră în slider - restul
                (tab-uri, titlu, Google, footer-ul de termeni) rămâne fix, ca
                trecerea între pași să nu miște tot ecranul. */}
            <div class="reg-step">
              {!(mode === "register" && registerStep === "business") && (
                <>
                  <label>{t("auth.email")}</label>
                  <input type="email" value={email} onInput={(e) => setEmail((e.target as HTMLInputElement).value)} required placeholder="you@company.com" />
                </>
              )}
              {(mode === "login" || (mode === "register" && registerStep === "account")) && (
                <>
                  <label>{t("auth.password")}</label>
                  <input type="password" value={password} minLength={8} onInput={(e) => setPassword((e.target as HTMLInputElement).value)} required placeholder="••••••••" />
                </>
              )}
              {mode === "register" && registerStep === "account" && (
                <>
                  <label>{t("auth.password2")}</label>
                  <input type="password" value={password2} minLength={8} onInput={(e) => setPassword2((e.target as HTMLInputElement).value)} required placeholder="••••••••" />
                  <label class="check">
                    <input
                      type="checkbox"
                      checked={marketingConsent}
                      onChange={(e) => setMarketingConsent((e.target as HTMLInputElement).checked)}
                    />
                    <span class="muted">{t("auth.marketingConsent")}</span>
                  </label>
                </>
              )}
              {mode === "register" && registerStep === "business" && (
                <BusinessFields v={biz} set={(patch) => setBiz((b) => ({ ...b, ...patch }))} disabled={busy} />
              )}
            </div>
            {msg && <Msg kind={msg.kind} text={msg.text} />}
            {showResend && email.trim() !== "" && (
              <p class="muted" style={{ marginTop: "10px", fontSize: "12px" }}>
                <ResendVerify email={email} />
              </p>
            )}
            {mode === "register" && registerStep === "business" ? (
              <div class="row" style={{ gap: "10px", marginTop: "18px" }}>
                <button
                  class="btn ghost"
                  style={{ flex: 1 }}
                  type="button"
                  disabled={busy}
                  onClick={() => { setRegisterStep("account"); setMsg(null); }}
                >
                  {t("auth.back")}
                </button>
                <button class="btn primary" style={{ flex: 1 }} disabled={busy} type="submit">
                  {busy ? "…" : t("auth.submitRegister")}
                </button>
              </div>
            ) : (
              <button class="btn primary block" style={{ marginTop: "18px" }} disabled={busy} type="submit">
                {busy ? "…" : mode === "login" ? t("auth.submitLogin") : mode === "forgot" ? t("auth.submitForgot") : t("auth.continue")}
              </button>
            )}
            {mode === "login" && (
              <p class="muted" style={{ marginTop: "12px", fontSize: "12px" }}>
                <a href="#" onClick={(e) => { e.preventDefault(); setMode("forgot"); setMsg(null); }}>{t("auth.forgotLink")}</a>
              </p>
            )}
            {/* Fără tab de register, cineva care ajunge aici fără cont rămâne
                într-un ecran fără ieșire. Linia asta e ieșirea. */}
            {mode === "login" && !SIGNUPS_OPEN && (
              <p class="muted" style={{ marginTop: "4px", fontSize: "12px" }}>
                Don't have an account? We are in private beta -{" "}
                <a href={`${SITE_ORIGIN}/contact`} target="_blank" rel="noopener">get in touch</a>.
              </p>
            )}
            {mode === "forgot" && (
              <p class="muted" style={{ marginTop: "12px", fontSize: "12px" }}>
                <a href="#" onClick={(e) => { e.preventDefault(); setMode("login"); setMsg(null); }}>{t("auth.backToLogin")}</a>
              </p>
            )}
            {mode === "register" && (
              <p class="muted" style={{ marginTop: "12px", fontSize: "12px" }}>
                By creating an account you agree to the{" "}
                <a href={`${SITE_ORIGIN}/terms`} target="_blank" rel="noopener">Terms of Service</a> and{" "}
                <a href={`${SITE_ORIGIN}/privacy`} target="_blank" rel="noopener">Privacy Policy</a>.
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

/**
 * Poarta „completează firma": afișată după login când profileComplete=false -
 * cazul tipic e un cont Google proaspăt (tokenul n-are datele de firmă). Blochează
 * accesul la dashboard până sunt completate. Prefill din me.business (dacă a apucat
 * să pună ceva). La succes, onDone reîncarcă /me → profileComplete devine true → Shell.
 */
function ProfileGate({ me, onDone, onLogout }: { me: Me; onDone: () => void; onLogout: () => void }) {
  const [biz, setBiz] = useState<BizState>(() => bizFromProfile(me.business));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "err"; text: string } | null>(null);

  async function submit(e: Event) {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      await api.updateProfile(toBusinessInput(biz));
      onDone();
    } catch (err) {
      setMsg({ kind: "err", text: errText(err) });
      setBusy(false);
    }
  }

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
      <div class="box" style={{ maxWidth: "460px", width: "100%" }}>
        <h3>{t("biz.gate.h")}</h3>
        <p class="sub">{t("biz.gate.sub")}</p>
        <form onSubmit={submit}>
          <BusinessFields v={biz} set={(patch) => setBiz((b) => ({ ...b, ...patch }))} disabled={busy} />
          {msg && <Msg kind={msg.kind} text={msg.text} />}
          <button class="btn primary block" style={{ marginTop: "18px" }} disabled={busy} type="submit">
            {busy ? "…" : t("biz.gate.submit")}
          </button>
          <p class="muted" style={{ marginTop: "12px", fontSize: "12px" }}>
            {me.email} · <a href="#" onClick={(e) => { e.preventDefault(); onLogout(); }}>{t("biz.gate.logout")}</a>
          </p>
        </form>
      </div>
    </div>
  );
}

function ResetPasswordView({ token, onDone }: { token: string; onDone: () => void }) {
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err" | "info"; text: string } | null>(null);

  async function submit(e: Event) {
    e.preventDefault();
    if (password !== password2) {
      setMsg({ kind: "err", text: t("auth.mismatch") });
      return;
    }
    setBusy(true);
    setMsg(null);
    try {
      await api.resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setMsg({
        kind: "err",
        text: err instanceof ApiError && err.status === 400 ? t("auth.resetInvalid") : errText(err),
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="auth-wrap">
      <div class="auth-brand">
        {/* Link, nu doar marcă: de pe login nu exista nicio cale înapoi la site.
            Consola stă pe alt subdomeniu decât landing-ul (app. vs apex; pe staging ambele sub .staging), deci URL absolut - env-aware prin SITE_ORIGIN (vezi sus). */}
        <a class="row brand-lockup" href={`${SITE_ORIGIN}/`} style={{ gap: "12px" }}>
          <div class="mark"><img src={logoBadge} alt="" /></div>
          <strong style={{ fontSize: "17px" }}>AskMyWebsite<span style={{ color: "var(--brand-600)" }}>[AI]</span></strong>
        </a>
      </div>
      <div class="auth-form">
        <div class="box">
          <h3>{t("auth.h.reset")}</h3>
          {done ? (
            <>
              <Msg kind="ok" text={t("auth.resetDone")} />
              <button class="btn primary block" style={{ marginTop: "18px" }} onClick={onDone}>
                {t("auth.backToLogin")}
              </button>
            </>
          ) : (
            <form onSubmit={submit}>
              <label>{t("auth.password")}</label>
              <input type="password" value={password} minLength={8} onInput={(e) => setPassword((e.target as HTMLInputElement).value)} required placeholder="••••••••" />
              <label>{t("auth.password2")}</label>
              <input type="password" value={password2} minLength={8} onInput={(e) => setPassword2((e.target as HTMLInputElement).value)} required placeholder="••••••••" />
              {msg && <Msg kind={msg.kind} text={msg.text} />}
              <button class="btn primary block" style={{ marginTop: "18px" }} disabled={busy} type="submit">
                {busy ? "…" : t("auth.submitReset")}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
//  OVERVIEW
// ============================================================


// ============================================================
//  SITES
// ============================================================

/** Grupează site-urile după client_label (§4, Agency+) - grupul „Unassigned” (fără etichetă) e mereu ultimul. */
function groupByClient(apps: AppTraffic[]): Array<{ label: string | null; apps: AppTraffic[] }> {
  const map = new Map<string | null, AppTraffic[]>();
  for (const a of apps) {
    const list = map.get(a.clientLabel) ?? [];
    list.push(a);
    map.set(a.clientLabel, list);
  }
  return [...map.entries()]
    .map(([label, list]) => ({ label, apps: list }))
    .sort((a, b) => (a.label === null ? 1 : b.label === null ? -1 : a.label.localeCompare(b.label)));
}

/**
 * Favicon citit din <head>-ul sitului la un read-page (vezi sdk/builder/dom.ts
 * captureFaviconUrl + builderService.readPage). Null până la primul read reușit,
 * sau dacă imaginea nu se încarcă (site mort, mixed-content etc.) - cade pe
 * inițiale în ambele cazuri.
 */
function SiteFavicon({ app }: { app: AppTraffic }) {
  const [broken, setBroken] = useState(false);
  // faviconUrl = pictograma reală a sitului (auto-detectată la scanare); dacă
  // lipsește, cade pe logo-ul de branding al widgetului - mai bine decât
  // inițialele, dacă omul chiar și-a încărcat unul.
  const logo = app.faviconUrl || app.widgetBranding?.logoUrl || null;
  const showImg = Boolean(logo) && !broken;
  return (
    <div
      class="favi"
      style={{
        width: "34px",
        height: "34px",
        borderRadius: "9px",
        fontSize: "13px",
        padding: showImg ? "6px" : 0,
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
        ...(showImg ? {} : { background: "var(--brand-grad)", color: "#fff", fontWeight: 800 }),
      }}
    >
      {showImg ? (
        <img
          src={logo!}
          alt=""
          style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
          onError={() => setBroken(true)}
        />
      ) : (
        (app.name || app.appId).slice(0, 2).toUpperCase()
      )}
    </div>
  );
}

/**
 * Mini-grafic de linie (fără axe/numere - overview dintr-o privire, gen
 * ticker de bursă) pentru `app.dailyCounts` (30 de zile, zero-umplute pe
 * backend). Linie plată gri când situl n-are încă trafic, ca să nu pară stricat.
 */
/**
 * Mini-trend pentru coloanele Trend/Sessions din tabelul de site-uri - control
 * rapid, nu un tablou de urmărit un target: doar bare (+ banda de weekend
 * pentru orientare) și un total pe ultimele N zile. Fără cifre/litere per zi
 * (zgomot la scara asta - rămân în tooltip) și fără procent de variație
 * (ar sugera un prag de urmărit, ceea ce nu e cazul aici). Consultat cu Kimi;
 * varianta aleasă e cea de mijloc dintre propunerea lui completă (cu Δ%) și
 * graficul mare DayChart neschimbat.
 */
function Sparkline({ data, unit }: { data: number[]; unit: string }) {
  const n = data.length;
  const today = new Date();
  const days = data.map((count, idx) => {
    const offset = n - 1 - idx; // 0 = azi (ultima intrare), crescând spre trecut
    const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - offset);
    const dow = d.getDay();
    return { day: ymd(d), count, weekday: WEEKDAY_LETTER[dow], isWeekend: dow === 0 || dow === 6 };
  });
  const max = Math.max(1, ...data);
  const total = data.reduce((s, v) => s + v, 0);
  const label = (k: string) => { const [, m, dd] = k.split("-"); return `${dd}.${m}`; };
  const range = `${label(days[0].day)} – ${label(days[days.length - 1].day)}`;
  return (
    <div class="spark" role="img" aria-label={`${range}: ${total} ${unit}`}>
      <div class="spark-bars">
        {days.map((d) => (
          <div key={d.day} class={`spark-col ${d.isWeekend ? "weekend" : ""}`} title={`${label(d.day)} (${d.weekday}) · ${d.count} ${unit}`}>
            <div class={`spark-bar ${d.count === 0 ? "z" : ""}`} style={{ height: `${Math.max(2, (d.count / max) * 100)}%` }} />
          </div>
        ))}
      </div>
      <span class="spark-total">{num(total)}</span>
    </div>
  );
}

/**
 * Perechea apex↔www a unui origin, dacă are sens. Pentru browser (și pentru
 * CORS-ul din runtime) `https://shop.com` și `https://www.shop.com` sunt origini
 * diferite; pentru client e același sit. Le ținem pe amândouă în ACELAȘI sit -
 * backendul refuză un al doilea sit pe același domeniu tocmai ca să nu ajungă
 * două widgeturi pe aceeași pagină.
 * Null pentru localhost, IP-uri și host-uri fără punct: acolo `www.` n-ar rezolva.
 */
function wwwCounterpart(origin: string): string | null {
  let u: URL;
  try {
    u = new URL(origin);
  } catch {
    return null;
  }
  const host = u.hostname;
  if (!host.includes(".") || /^[\d.]+$/.test(host)) return null;
  u.hostname = host.startsWith("www.") ? host.slice(4) : `www.${host}`;
  return u.origin;
}

/**
 * 409 „origin_taken" tradus în ceva acționabil: numele sitului care ține deja
 * domeniul. Null dacă eroarea e altceva - apelantul cade pe errText.
 */
function originTakenText(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const data = err.data as { error?: string; conflicts?: { origin: string; appName: string }[] } | null;
  const first = data?.error === "origin_taken" ? data.conflicts?.[0] : undefined;
  return first ? t("sites.originTaken", first.origin, first.appName || t("sites.untitled")) : null;
}

/**
 * 403 „live_site_limit" tradus: contul are deja atâtea site-uri pornite câte
 * permite planul. Apare după o coborâre de plan - mesajul brut („Error:
 * live_site_limit") n-ar spune omului nici cauza, nici ce poate face.
 * Null dacă eroarea e altceva - apelantul cade pe errText.
 */
function liveSiteLimitText(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.status !== 403) return null;
  const data = err.data as { error?: string; limit?: number } | null;
  if (data?.error !== "live_site_limit") return null;
  return t("sites.liveLimit", String(data.limit ?? 0));
}

/** Un domeniu fără schemă („example.com") devine un origin valid - cel mai comun mod de a-l scrie greșit. */
function normalizeOrigin(origin: string): string {
  return /^https?:\/\//i.test(origin) ? origin : `https://${origin}`;
}

/**
 * 400 „invalid request" de la POST /apps tradus în ceva pe care utilizatorul îl
 * poate remedia - schema zod refuză orice nu e un URL http(s), iar mesajul brut
 * (`Error: invalid request`) nu spune ce anume. Null dacă eroarea e altceva -
 * apelantul cade pe errText.
 */
function originInvalidText(err: unknown): string | null {
  if (!(err instanceof ApiError) || err.status !== 400) return null;
  const data = err.data as { error?: string } | null;
  return data?.error === "invalid request" ? t("sites.originInvalid") : null;
}

/**
 * „Latest AI reports": headline-ul celui mai recent raport AI per sit (max 3,
 * cele mai proaspete întâi), fiecare rând = link spre Insights-ul sitului.
 * A înlocuit teaserul „Coming soon" al unui raport GLOBAL care nu există (și
 * concura cu raportul per-site care există) - decizia lui Radu, 2 aug 2026.
 * Pe planurile fără `analytics` devine upsell-ul: vinde rapoartele reale.
 */
function LatestReportsCard({ apps, me }: { apps: AppTraffic[] | null; me: Me }) {
  const allowed = me.plan.features.includes("analytics");
  const withReports = (apps ?? [])
    .filter((a) => a.latestReportHeadline !== null)
    .sort((a, b) => (b.latestReportAt ?? 0) - (a.latestReportAt ?? 0))
    .slice(0, 3);

  return (
    <div class="stat hero-mini">
      <span class="lab">{t("ov.reports.title")}</span>

      {apps === null && (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "10px" }}>
          <Skeleton h="30px" style={{ borderRadius: "8px" }} />
          <Skeleton h="30px" style={{ borderRadius: "8px" }} />
        </div>
      )}

      {apps !== null && !allowed && (
        <>
          <p class="ov-reports-note">{t("ov.reports.locked")}</p>
          <button class="btn subtle sm" style={{ marginTop: "12px", width: "100%" }} onClick={() => goUpgrade()}>
            {upgradeLabel("insights.lockedCta")}
          </button>
        </>
      )}

      {apps !== null && allowed && withReports.length === 0 && (
        <p class="ov-reports-note">{t("ov.reports.empty")}</p>
      )}

      {apps !== null && allowed && withReports.length > 0 && (
        <div class="ov-reports">
          {withReports.map((a) => (
            <button
              type="button"
              class="ov-report-row"
              key={a.appId}
              title={a.latestReportHeadline ?? undefined}
              onClick={() => navigate(`#/dashboard/${a.appId}/insights`)}
            >
              <span class="ov-report-site">
                {a.name}
                {a.latestReportAt !== null && <span class="ov-report-date"> · {fmtDate(a.latestReportAt)}</span>}
              </span>
              <span class="ov-report-headline">{a.latestReportHeadline}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Butonul Go live / Take offline din coloana de acțiuni a listei de site-uri.
 * DOAR trigger-ul - modalul de confirmare trăiește ridicat în `DashboardView`,
 * NU aici. Motiv: `.table-wrap` (ancestor, are `backdrop-filter`) creează un
 * containing block nou pentru `position: fixed` în unele motoare de rendering,
 * deci un modal randat în interiorul tabelului nu se mai centrează pe
 * viewport - se centrează pe cutia lui `.table-wrap`. Toate celelalte modale
 * din fișier sunt frați ai `<table>`, nu descendenți; asta trebuie să rămână
 * la fel. `stopPropagation`: rândul are `onClick` de navigare.
 */
function RowLiveToggle({ app, onRequest }: { app: AppTraffic; onRequest: (app: AppTraffic) => void }) {
  return (
    <button
      class={`btn sm ${app.live ? "danger" : "ghost"}`}
      onClick={(e) => { e.stopPropagation(); onRequest(app); }}
    >
      {app.live ? t("sd.takeOffline") : t("sd.goLive")}
    </button>
  );
}

function DashboardView({ me, apps, reloadApps, reloadMe }: { me: Me; apps: AppTraffic[] | null; reloadApps: () => Promise<void>; reloadMe: () => void }) {
  const [creating, setCreating] = useState(false);
  const [showLimit, setShowLimit] = useState(false);
  const [name, setName] = useState("");
  const [origins, setOrigins] = useState("");
  const [addWww, setAddWww] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  // Go live/Take offline pe rând: un SINGUR modal ridicat aici (nu unul per
  // rând) - vezi comentariul de la RowLiveToggle pentru de ce trebuie să fie
  // frate cu `<table>`, nu descendent.
  const [toggleTarget, setToggleTarget] = useState<AppTraffic | null>(null);
  const [toggleBusy, setToggleBusy] = useState(false);
  const [toggleErr, setToggleErr] = useState<string | null>(null);
  // un singur /stats/daily pe tot contul: alimentează ȘI graficul de volum, ȘI pâlnia agregată
  const stats = useDailyStats();

  async function confirmToggle() {
    if (!toggleTarget) return;
    setToggleBusy(true);
    setToggleErr(null);
    try {
      if (!toggleTarget.live) {
        // la fel ca în pagina de detaliu: publică draftul curent înainte de a
        // aprinde flag-ul, altfel „Go live" ar servi un 404 pe /config.
        try {
          await api.publishBuilderDraft(toggleTarget.appId);
        } catch (e) {
          if (!(e instanceof ApiError) || e.status !== 409) throw e;
        }
      }
      await api.updateApp(toggleTarget.appId, { live: !toggleTarget.live });
      await reloadApps();
      reloadMe();
      setToggleTarget(null);
    } catch (e) {
      setToggleErr(liveSiteLimitText(e) ?? errText(e));
    } finally {
      setToggleBusy(false);
    }
  }

  const originList = origins.split("\n").map((o) => o.trim()).filter(Boolean).map(normalizeOrigin);
  // perechile care ar fi adăugate - și temeiul de a arăta comutatorul deloc
  const wwwPairs = originList
    .map((o) => [o, wwwCounterpart(o)] as const)
    .filter((p): p is readonly [string, string] => p[1] !== null && !originList.includes(p[1]));

  const atLimit = !me.isAdmin && me.plan.sitesUsed >= me.plan.sitesLimit;
  const usedPct = pct(me.plan.answersUsed, me.plan.answersLimit);
  // credit prepaid de „extra answers" - persistent, nu se resetează lunar (spre
  // deosebire de answersRemaining). Aceeași formulă ca în PlanView.
  const extraAvailable = Math.floor(
    (me.plan.overageCreditMicroUsd / 1_000_000 / me.overage.eurPerBlock) * me.overage.blockAnswers,
  );

  async function create(e: Event) {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      const { app } = await api.createApp(name, addWww ? [...originList, ...wwwPairs.map(([, pair]) => pair)] : originList);
      setName("");
      setOrigins("");
      setCreating(false);
      await reloadApps();
      reloadMe();
      navigate(`#/dashboard/${app.appId}/settings`);
    } catch (err) {
      setMsg(originTakenText(err) ?? originInvalidText(err) ?? siteCreateErrText(err) ?? errText(err));
    } finally {
      setBusy(false);
    }
  }

  async function openBuilder(appId: string) {
    try {
      const { url } = await api.builderToken(appId);
      window.open(url, "_blank");
    } catch {
      /* ignoră */
    }
  }

  function siteRow(app: AppTraffic) {
    return (
      <tr key={app.appId} style={{ cursor: "pointer" }} onClick={() => navigate(`#/dashboard/${app.appId}`)}>
        <td>
          <div class="usermini">
            <SiteFavicon app={app} />
            <div>
              {/* link adevărat, nu doar onClick pe <tr>: navigabil cu tastatura și
                  vizibil în snapshotul SDK-ului - altfel asistentul n-are cum să
                  evidențieze rândul și trimite omul greșit spre butonul Open (Agent) */}
              <div class="em">
                <a
                  href={`#/dashboard/${app.appId}`}
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: "inherit", textDecoration: "none" }}
                >
                  {app.name || app.appId}
                </a>
              </div>
              <div class="em2">{app.allowedOrigins[0] ?? app.appId}</div>
            </div>
          </div>
        </td>
        <td><span class={`badge ${app.live ? "ok" : "muted"}`}>{app.live ? t("sd.live") : t("sd.notLive")}</span></td>
        <td class="center"><Sparkline data={app.dailyCounts.slice(-7)} unit={t("chart.responses")} /></td>
        {/* „conversations", nu „sessions": dailySessions numără DOAR sesiunile care au
            pus o întrebare. Cifră + link spre tabul Conversations al sitului, NU al
            doilea sparkline: două mini-grafice pe rând erau același desen de două ori,
            iar unitatea se vedea doar la hover (feedback Radu, 2 aug 2026). */}
        <td class="center">
          <a
            class="sites-conv-link"
            href={`#/dashboard/${app.appId}/conversations`}
            onClick={(e) => e.stopPropagation()}
            title={t("sites.convLinkTitle")}
          >
            {num(app.dailySessions.slice(-7).reduce((s, n) => s + n, 0))}
          </a>
        </td>
        {/* DOAR consumul sitului - cota (answersLimit) e a CONTULUI, nu a sitului;
            un „97 / 2.500" per rând sugera alocație per sit și nu s-ar fi închis
            niciodată la adunare. Cota rămâne în cardul „Answers served". */}
        <td class="num">
          <span class="em">{num(app.answersThisMonth)}</span>
          <span class="muted"> {t("sites.tMonth")}</span>
          {app.answersTotal > app.answersThisMonth && <div class="em2">{num(app.answersTotal)} {t("sites.tTotal")}</div>}
        </td>
        <td class="num">
          <div class="sites-actions">
            <RowLiveToggle app={app} onRequest={(a) => { setToggleErr(null); setToggleTarget(a); }} />
            {app.allowedOrigins[0] && (
              <a
                class="btn ghost sm"
                href={app.allowedOrigins[0]}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                <IconExternal /> {t("sites.open")}
              </a>
            )}
            <button class="btn ghost sm" onClick={(e) => { e.stopPropagation(); void openBuilder(app.appId); }}>
              <IconExternal /> {t("sd.openBuilder")}
            </button>
          </div>
        </td>
      </tr>
    );
  }

  // grupare pe client doar când planul are §4 și există cel puțin o etichetă setată
  // (altfel un singur grup "Unassigned" ar fi zgomot pur pentru toată lumea)
  const canGroupByClient = me.plan.features.includes("clientManagement") && (apps ?? []).some((a) => a.clientLabel);
  const clientGroups = canGroupByClient ? groupByClient(apps ?? []) : null;

  return (
    <div class="content">
      {/* trafic global al contului */}
      <div class="grid stat-split3" style={{ marginBottom: "18px" }}>
        <div class="stat centered">
          <div class="l"><span class="dot" style={{ background: "#a855f7" }} /> {t("sd.answersServed")}</div>
          <div class="v">
            {num(me.plan.answersUsed)} <span class="vsub">/ {num(me.plan.answersLimit)}</span>
            {/* pool-ul de bonus (add-on „more messages") e separat de creditul de overage
                și de cota lunară - se servește după ce lunarele se termină, deci îl arătăm
                distinct ca omul să știe că are tampon peste „20 / 20". */}
            {me.plan.bonusAnswers > 0 && <span class="vbonus">{t("ov.bonusInline", num(me.plan.bonusAnswers))}</span>}
          </div>
          <div class="pbar" style={{ marginTop: "10px" }}><span style={{ width: `${usedPct}%` }} /></div>
          <div class="d" style={{ marginTop: "8px" }}>{t("ov.renewsOn", fmtDate(me.plan.answersResetAt))}</div>
          {extraAvailable > 0 && <div class="d" style={{ marginTop: "2px" }}>{t("sites.extraInline", num(extraAvailable))}</div>}
        </div>
        <DailyResponsesCard points={stats?.days ?? null} compact />
        <LatestReportsCard apps={apps} me={me} />
      </div>

      {apps === null && (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <Skeleton h="56px" style={{ borderRadius: "13px" }} />
          <Skeleton h="56px" style={{ borderRadius: "13px" }} />
        </div>
      )}

      {apps && apps.length === 0 && (
        <div class="card">
          <div class="empty">
            <div class="ic"><IconSites /></div>
            <h3>{t("sites.emptyH")}</h3>
            <p style={{ marginBottom: "16px" }}>{t("sites.emptyP")}</p>
            {!atLimit && <button class="btn primary" onClick={() => setCreating(true)}><IconPlus /> {t("sites.createFirst")}</button>}
          </div>
        </div>
      )}

      {apps && apps.length > 0 && (
        <div class="table-wrap">
          <table class="data sites-table">
            {/* Lățimi deterministe (table-layout: fixed): coloana de acțiuni are un px
                fix, destul pentru cele trei butoane pe UN rând fără să dea peste
                „Answers"; SITE ia restul (fără width → coloana flexibilă). Sub
                min-width-ul tabelului, `.table-wrap` derulează orizontal.
                470px = 404px măsurați în browser (Take offline + Open website +
                Open Agent + gap-uri + padding-ul celulei) + margine pentru variații
                de rendering al fontului - la 420px marginea era de doar 16px. */}
            <colgroup>
              <col />
              <col style={{ width: "104px" }} />
              <col style={{ width: "128px" }} />
              <col style={{ width: "132px" }} />
              <col style={{ width: "150px" }} />
              <col style={{ width: "470px" }} />
            </colgroup>
            <thead>
              <tr>
                <th>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <span>{t("sites.col.name")}</span>
                    {atLimit ? (
                      <button
                        class="btn primary sm"
                        style={{ textTransform: "none", letterSpacing: "normal", fontWeight: 600 }}
                        onClick={() => setShowLimit(true)}
                      >
                        <IconPlus /> {t("sites.buyExtra")}
                      </button>
                    ) : (
                      <button
                        class="btn ghost sm"
                        style={{ textTransform: "none", letterSpacing: "normal", fontWeight: 600 }}
                        onClick={() => setCreating(true)}
                      >
                        <IconPlus /> {t("sites.new")}
                      </button>
                    )}
                  </div>
                </th>
                <th>{t("sites.col.status")}</th>
                <th class="center">{t("sites.col.trend")}<span class="th-note">{t("sites.col.last7d")}</span></th>
                <th class="center">{t("sites.col.conversations")}<span class="th-note">{t("sites.col.last7d")}</span></th>
                <th class="num">{t("sites.col.answers")}</th>
                <th class="num" aria-label={t("sites.col.actions")}></th>
              </tr>
            </thead>
            <tbody>
              {clientGroups
                ? clientGroups.map((group) => (
                    <>
                      <tr class="group-row" key={`group-${group.label ?? "unassigned"}`}>
                        <td colSpan={6}>
                          <span class="group-label">{group.label ?? t("sites.unassigned")}</span>
                          <span class="muted">
                            {" "}· {group.apps.length === 1 ? t("sites.clientSite") : t("sites.clientSites", group.apps.length)}
                            {" "}· {t("sites.clientAnswers", num(group.apps.reduce((s, a) => s + a.answersThisMonth, 0)))}
                          </span>
                        </td>
                      </tr>
                      {group.apps.map(siteRow)}
                    </>
                  ))
                : apps.map(siteRow)}
            </tbody>
          </table>
        </div>
      )}

      {creating && (
        <Modal onClose={() => setCreating(false)}>
          <h3>{t("sites.new")}</h3>
          <form onSubmit={create}>
            <label for="new-site-name">{t("sites.name")}</label>
            <input id="new-site-name" value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)} required maxLength={100} placeholder={t("sites.namePh")} />
            <label for="new-site-origins">{t("sites.origins")}</label>
            <textarea id="new-site-origins" rows={3} value={origins} onInput={(e) => setOrigins((e.target as HTMLTextAreaElement).value)} required placeholder={t("sites.originsPh")} />
            <p class="muted" style={{ marginTop: "6px" }}>{t("sites.originsHint")}</p>
            {wwwPairs.length > 0 && (
              <label class="check" style={{ marginTop: "12px" }}>
                <input type="checkbox" checked={addWww} onChange={(e) => setAddWww((e.target as HTMLInputElement).checked)} />
                <span>
                  {t("sites.wwwToggle")}
                  <br />
                  <span class="muted" style={{ fontSize: "12px", fontWeight: 400 }}>
                    {t("sites.wwwToggleHint", wwwPairs[0][0], wwwPairs[0][1])}
                  </span>
                </span>
              </label>
            )}
            {msg && <Msg kind="err" text={msg} />}
            <div class="actions">
              <button class="btn ghost" type="button" onClick={() => setCreating(false)}>{t("common.cancel")}</button>
              <button class="btn primary" disabled={busy} type="submit">{t("sites.create")}</button>
            </div>
          </form>
        </Modal>
      )}

      {showLimit && (
        <Modal onClose={() => setShowLimit(false)}>
          <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
            <div class="empty" style={{ padding: 0 }}><div class="ic" style={{ margin: 0 }}><IconPlan /></div></div>
            <div>
              <h3>{t("sites.limitTitle")}</h3>
              {/* sub un grant, `sitesLimit` vine din planul grantat - deci numele
                  planului din mesaj trebuie să fie tot ăla, altfel iese „Free
                  permite 3 site-uri". */}
              <p>{me.plan.sitesLimit === 1 ? t("sites.limitBody1", effectivePlanLabel(me)) : t("sites.limitBodyN", effectivePlanLabel(me), me.plan.sitesLimit)}</p>
            </div>
          </div>
          <div class="actions">
            <button class="btn ghost" onClick={() => setShowLimit(false)}>{t("common.cancel")}</button>
            <button class="btn primary" onClick={() => { setShowLimit(false); goUpgrade(); }}>{upgradeLabel("sites.upgrade")}</button>
          </div>
        </Modal>
      )}

      {toggleTarget && (
        <Modal onClose={() => { if (!toggleBusy) setToggleTarget(null); }}>
          <h3>{toggleTarget.live ? t("sites.offlineConfirmTitle") : t("sites.liveConfirmTitle")}</h3>
          <p>
            {toggleTarget.live
              ? t("sites.offlineConfirmBody", toggleTarget.name || toggleTarget.appId)
              : t("sites.liveConfirmBody", toggleTarget.name || toggleTarget.appId)}
          </p>
          {toggleErr && <Msg kind="err" text={toggleErr} />}
          <div class="actions">
            <button class="btn ghost" disabled={toggleBusy} onClick={() => setToggleTarget(null)}>{t("common.cancel")}</button>
            <button class={`btn ${toggleTarget.live ? "solid-danger" : "primary"}`} disabled={toggleBusy} onClick={() => void confirmToggle()}>
              {toggleBusy ? "…" : toggleTarget.live ? t("sd.takeOffline") : t("sd.goLive")}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============================================================
//  SITE DETAIL
// ============================================================

interface KnowledgePage {
  id: string;
  urlPattern: string;
  title: string;
  /** de la scan v2: la ce întrebări răspunde pagina. Configurile vechi n-au câmpul. */
  topics?: string[];
  /** nu se mai extrag la scanare; rămân pe configurile vechi și pe ce a ales manual owner-ul */
  elements: { id: string; label: string; purpose?: string }[];
}

function KnowledgeView({ appId }: { appId: string }) {
  const [pages, setPages] = useState<KnowledgePage[] | null | "none">(null);
  useEffect(() => {
    void (async () => {
      try {
        const { configs } = await api.listConfigs(appId);
        const published = configs.find((c) => c.status === "published");
        if (!published) return setPages("none");
        const config = await api.getConfig(appId, published.version);
        setPages((config.body as { pages: KnowledgePage[] }).pages);
      } catch {
        setPages("none");
      }
    })();
  }, [appId]);

  async function openBuilder() {
    try {
      const { url } = await api.builderToken(appId);
      window.open(url, "_blank");
    } catch {
      /* ignoră */
    }
  }

  return (
    <div class="card">
      <div class="card-head">
        <h2>{t("kn.title")}</h2>
        {Array.isArray(pages) && <span class="muted">{pages.length} {t("kn.pages")}</span>}
      </div>
      {pages === null && <Skeleton h="72px" style={{ borderRadius: "10px" }} />}
      {pages === "none" && (
        <EmptyMini icon={<IconBook />} text={t("kn.empty")} cta={{ label: t("sd.openBuilder"), onClick: () => void openBuilder() }} />
      )}
      {Array.isArray(pages) && (
        <>
          <div class="klist">
            {pages.map((page) => (
              <div class="kpage" key={page.id}>
                <div class="row spread">
                  <strong style={{ fontSize: "13.5px" }}>{page.title}</strong>
                  <span class="muted">{page.urlPattern}</span>
                </div>
                {(page.topics ?? []).length > 0 && (
                  <>
                    <p class="muted" style={{ margin: "6px 0 2px" }}>{t("kn.topics")}</p>
                    <div class="ktopics">
                      {(page.topics ?? []).map((topic) => (
                        <span class="ktopic" key={topic}>{topic}</span>
                      ))}
                    </div>
                  </>
                )}
                {page.elements.map((el) => (
                  <div class="krow" key={el.id}><span class="kdot" /><span><b>{el.label}</b>{el.purpose ? <span class="muted"> - {el.purpose}</span> : null}</span></div>
                ))}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Card de listă cu add/remove persistate imediat - folosit de Excluded URLs și
 * Excluded content (settings-ul sitului). `onSave` primește lista următoare și
 * aruncă la eșec; părintele își rescrie starea din răspunsul serverului
 * (normalizare + dedup), de-aia componenta nu ține lista, doar inputul.
 */
function ExclusionListCard({
  title,
  sub,
  placeholder,
  hint,
  emptyText,
  values,
  onSave,
}: {
  title: string;
  sub: string;
  placeholder: string;
  hint: string;
  emptyText: string;
  values: string[];
  onSave: (next: string[]) => Promise<void>;
}) {
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  async function persist(next: string[], clearInput: boolean) {
    setBusy(true); setMsg(null);
    try {
      await onSave(next);
      if (clearInput) setInput("");
    } catch (err) { setMsg(errText(err)); } finally { setBusy(false); }
  }
  return (
    <div class="card">
      <div class="card-head">
        <div><h2>{title}</h2><p>{sub}</p></div>
      </div>
      <form
        class="row"
        style={{ gap: "8px" }}
        onSubmit={(e) => {
          e.preventDefault();
          const v = input.trim();
          if (v && !values.includes(v)) void persist([...values, v], true);
        }}
      >
        <input
          aria-label={placeholder}
          value={input}
          onInput={(e) => setInput((e.target as HTMLInputElement).value)}
          maxLength={200}
          placeholder={placeholder}
          style={{ flex: "1", marginTop: 0, fontFamily: "ui-monospace, Menlo, monospace" }}
        />
        <button class="btn primary sm" type="submit" disabled={busy || !input.trim()}>
          {t("sd.excludedAdd")}
        </button>
      </form>
      {values.length === 0 ? (
        <p class="muted" style={{ marginTop: "12px", fontSize: "13px" }}>{emptyText}</p>
      ) : (
        <div class="row" style={{ gap: "8px", flexWrap: "wrap", marginTop: "12px" }}>
          {values.map((v) => (
            <span key={v} style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "var(--line-2)", color: "var(--ink-2)", padding: "5px 7px 5px 11px", borderRadius: "8px", fontSize: "12.5px", fontFamily: "ui-monospace, Menlo, monospace" }}>
              {v}
              <button
                type="button"
                aria-label={`Remove ${v}`}
                disabled={busy}
                onClick={() => void persist(values.filter((x) => x !== v), false)}
                style={{ border: "none", background: "none", cursor: "pointer", color: "var(--ink-3)", fontSize: "14px", lineHeight: 1, padding: "2px" }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <p class="muted" style={{ marginTop: "10px", fontSize: "12px" }}>{hint}</p>
      {msg && <Msg kind="err" text={msg} />}
    </div>
  );
}

// ============================================================
//  CONVERSATIONS (primul tab al unui site - materia primă, pe orice plan)
// ============================================================

/**
 * Tabul „Conversations": banda de sumar sus, apoi istoricul complet. Ne-gated
 * în întregime - endpointul `/apps/:appId/conversations` nu mai cere feature-ul
 * `analytics`, deci aici nu există nicio verificare de plan.
 *
 * Sumarul NU are fetch propriu: îl aduce primul fetch al cardului de dedesubt
 * (`summary: true`), care oricum se face la montare. Un al doilea apel ar fi
 * cerut aceleași date de două ori.
 */
function ConversationsTab({ app }: { app: AppTraffic }) {
  const [summary, setSummary] = useState<ConversationsSummary | null>(null);
  return (
    <>
      <ConversationsSummaryStrip summary={summary} />
      <ConversationsCard appId={app.appId} onSummary={setSummary} />
    </>
  );
}

/**
 * Trei cifre compacte deasupra istoricului. Cât timp sumarul nu a venit se văd
 * skeletoane; când vine gol (sit fără trafic) banda dispare complet - cardul de
 * dedesubt spune deja „No conversations yet", iar trei zerouri unul lângă altul
 * ar arăta a defecțiune, nu a sit nou.
 */
function ConversationsSummaryStrip({ summary }: { summary: ConversationsSummary | null }) {
  if (summary === null) {
    return (
      <div class="grid conv-sum-strip" style={{ marginBottom: "18px" }}>
        <div class="stat"><Skeleton h="13px" w="55%" /><Skeleton h="26px" style={{ marginTop: "10px" }} /></div>
        <div class="stat"><Skeleton h="13px" w="55%" /><Skeleton h="26px" style={{ marginTop: "10px" }} /></div>
        <div class="stat"><Skeleton h="13px" w="55%" /><Skeleton h="26px" style={{ marginTop: "10px" }} /></div>
      </div>
    );
  }
  if (summary.totalThisMonth === 0 && summary.topPages.length === 0) return null;

  const topPages = summary.topPages.slice(0, 3);
  return (
    <div class="grid conv-sum-strip" style={{ marginBottom: "18px" }}>
      <div class="stat">
        <div class="l"><span class="dot" style={{ background: "#6366f1" }} /> {t("conv.sum.month")}</div>
        <div class="v">{num(summary.totalThisMonth)}</div>
        <div class="d">{questionsLabel(summary.totalThisMonth)}</div>
      </div>
      <div class="stat">
        <div class="l"><span class="dot" style={{ background: "#a855f7" }} /> {t("conv.sum.avg")}</div>
        <div class="v">{summary.avgSessionLength.toFixed(1)}</div>
        <div class="d">{t("conv.sum.avgVal", summary.avgSessionLength.toFixed(1))}</div>
      </div>
      <div class="stat">
        <div class="l"><span class="dot" style={{ background: "#8b5cf6" }} /> {t("conv.sum.pages")}</div>
        {topPages.length === 0 && <div class="d" style={{ marginTop: "9px" }}>{t("conv.sum.pagesEmpty")}</div>}
        {topPages.length > 0 && (
          <ul class="conv-sum-pages">
            {topPages.map((p) => (
              <li key={p.path}>
                <span class="conv-sum-path" title={p.path}>{p.path || "/"}</span>
                <span class="conv-sum-n">{num(p.count)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ============================================================
//  INSIGHTS (tab-ul de interpretare - „a plecat vizitatorul cu ce a venit?")
// ============================================================

/**
 * Tabul „Insights": casa a tot ce înseamnă interpretare, pe trei etaje.
 *
 *  1. Frază-erou (ne-gated, din /stats/daily) + raportul AI săptămânal.
 *  2. Trei întrebări: e folosit asistentul? ajută cu adevărat? ce repari pe sit?
 *  3. „Details", colapsat implicit: cotă, volum zilnic, arhiva de rapoarte.
 *
 * A înlocuit taburile „Statistics" + „Progress", care puneau două pâlnii
 * suprapuse una lângă alta. Fetch-uri: /stats/daily (ne-gated) plus /insights și
 * /ai-summaries (gated - nu se cer deloc când planul n-are `analytics`).
 * Raportul curent e prima înregistrare din arhivă, singura sursă care aduce și
 * perioada acoperită; /insights.summary rămâne fallback dacă arhiva n-a venit.
 */
function InsightsTab({ app, me }: { app: AppTraffic; me: Me }) {
  const stats = useDailyStats(app.appId);
  const allowed = me.plan.features.includes("analytics");
  const [insights, setInsights] = useState<InsightsResponse | null>(null);
  const [reports, setReports] = useState<AiSummaryRecord[] | null>(null);
  const [paywall, setPaywall] = useState(!allowed);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    if (!allowed) { setPaywall(true); return; }
    setInsights(null); setReports(null); setError(null); setPaywall(false);
    // Gating-ul e client-side, dar prindem defensiv și un 403 real dinspre
    // server: tot paywall, nu eroare generică.
    const onErr = (err: unknown) => {
      if (err instanceof ApiError && err.status === 403) setPaywall(true);
      else setError(errText(err));
    };
    api.insights(app.appId).then(setInsights).catch(onErr);
    api
      .aiSummaries(app.appId)
      .then((r) => setReports(r.summaries))
      .catch((err) => { onErr(err); setReports([]); });
  };
  useEffect(load, [app.appId, allowed]);

  const gated = paywall || !allowed;
  const insightsLoading = !gated && !error && insights === null;
  const reportsLoading = !gated && reports === null;
  // targetMissing nu e în `funnel` - îl agregăm din paginile cu fricțiune (cea mai
  // bună aproximare din insights; top 10, dar suficient pentru o ramură vizuală).
  const targetMissing = (insights?.frictionPages ?? []).reduce((s, p) => s + p.targetMissing, 0);

  const latest = reports?.[0] ?? null;
  const summary = latest?.body ?? insights?.summary ?? null;
  const generatedAt = latest?.generatedAt ?? insights?.summaryGeneratedAt ?? null;

  return (
    <>
      {error && <Msg kind="err" text={error} />}

      {/* Etajul 1: propoziția care rezumă 30 de zile, apoi raportul AI */}
      <InsightsHero funnel={stats?.funnel ?? null} claims={stats?.claims ?? null} />

      <div style={{ marginTop: "18px" }}>
        <CurrentReportCard
          appId={app.appId}
          me={me}
          summary={summary}
          generatedAt={generatedAt}
          periodStart={latest?.periodStart ?? null}
          periodEnd={latest?.periodEnd ?? null}
          loading={insightsLoading || reportsLoading}
          paywall={gated}
          onRan={load}
        />
      </div>

      {/* Etajul 2a: adopția - ne-gated, vine din același /stats/daily ca eroul */}
      <div style={{ marginTop: "18px" }}>
        <VisitorFunnelCard funnel={stats?.funnel ?? null} />
      </div>

      {/* Etajul 2b + 2c: ghidarea și ce e de reparat pe sit - gated */}
      <InsightsSection
        appId={app.appId}
        insights={insights}
        summary={summary}
        paywall={gated}
        loading={insightsLoading}
        targetMissing={targetMissing}
      />

      {/* Arhiva: clientul a plătit generarea fiecărui raport, deci le vede pe toate.
          Volumul și cota au plecat în tabul „Dashboard" al sitului (SiteOverviewTab). */}
      <div style={{ marginTop: "18px" }}>
        <ReportArchiveCard rows={reports} loading={reportsLoading} paywall={gated} />
      </div>
    </>
  );
}

/** Presară span-uri `.insights-hero-line-extra` peste marcajele {0}, {1}... dintr-un șablon i18n. */
function heroHighlight(template: string, values: (string | number)[]) {
  return template.split(/(\{\d+\})/).map((part, i) => {
    const m = /^\{(\d+)\}$/.exec(part);
    if (!m) return part;
    return <span class="insights-hero-line-extra" key={i}>{values[Number(m[1])]}</span>;
  });
}

/**
 * Fraza-erou: aceleași cifre ca pâlnia de dedesubt, dar citite ca o propoziție.
 * Ne-gated (vine din /stats/daily). Când totul e zero afișăm mesajul obișnuit de
 * „încă nu sunt date", nu o frază cu trei zerouri în ea.
 */
function InsightsHero({ funnel, claims }: { funnel: SessionFunnel | null; claims: ClaimCounts | null }) {
  if (funnel === null) {
    return (
      <div class="insights-hero">
        <Skeleton h="22px" w="90%" />
        <Skeleton h="22px" w="65%" style={{ marginTop: "10px" }} />
      </div>
    );
  }
  const { impressions, opens, asked, guided, reached } = funnel.totals;
  if (impressions === 0 && opens === 0 && asked === 0 && guided === 0) {
    return <div class="insights-hero"><p class="insights-hero-empty">{t("funnel.empty")}</p></div>;
  }
  return (
    <div class="insights-hero">
      <p class="insights-hero-line">
        {heroHighlight(tRaw("insights.hero"), [num(impressions), num(opens), num(asked)])}
        {guided > 0 && <>{" "}{heroHighlight(tRaw("insights.heroGuided"), [num(guided), num(reached)])}</>}
        {claims !== null && claims.verified + claims.corrected > 0 && (
          <>{" "}{heroHighlight(tRaw("insights.heroClaims"), [num(claims.verified + claims.corrected), num(claims.corrected)])}</>
        )}
      </p>
    </div>
  );
}

/**
 * Tabul „Dashboard" al unui SIT (id de rută „overview", ca să nu se bată cap
 * în cap cu ruta contului `#/dashboard`): activitatea ca volum și resursele
 * rămase, în prim-plan - cota de răspunsuri, volumul zilnic și pulsul ultimelor
 * 30 de zile. Cerut explicit de Radu (2 aug 2026): cifrele astea stăteau
 * îngropate într-o secțiune colapsată la coada tabului Insights.
 * Totul ne-gated (aceleași surse ca vechiul tab Statistics).
 */
function SiteOverviewTab({ app, me }: { app: AppTraffic; me: Me }) {
  const stats = useDailyStats(app.appId);
  const totals = stats?.funnel.totals ?? null;
  const sitePct = pct(app.answersThisMonth, me.plan.answersLimit);
  const extraAvailable = Math.floor(
    (me.plan.overageCreditMicroUsd / 1_000_000 / me.overage.eurPerBlock) * me.overage.blockAnswers,
  );

  const pulse: Array<{ label: string; value: number; color: string }> = totals
    ? [
        { label: t("so.pageViews"), value: totals.impressions, color: "#6366f1" },
        { label: t("so.opened"), value: totals.opens, color: "#8b5cf6" },
        { label: t("so.asked"), value: totals.asked, color: "#a855f7" },
      ]
    : [];

  return (
    <>
      <div class="grid stat-split">
        <div class="stat centered">
          <div class="l"><span class="dot" style={{ background: "#a855f7" }} /> {t("sd.answersServed")}</div>
          <div class="v">
            {num(app.answersThisMonth)} <span class="vsub">/ {num(me.plan.answersLimit)}</span>
            {/* pool-ul de bonus e la nivel de cont, nu de sit - dar cardul e „referința"
                pentru consistență cu cel din overview-ul contului, deci îl arătăm și aici. */}
            {me.plan.bonusAnswers > 0 && <span class="vbonus">{t("ov.bonusInline", num(me.plan.bonusAnswers))}</span>}
          </div>
          <div class="pbar" style={{ marginTop: "10px" }}><span style={{ width: `${sitePct}%` }} /></div>
          <div class="d" style={{ marginTop: "8px" }}>{t("ov.renewsOn", fmtDate(me.plan.answersResetAt))}</div>
          {extraAvailable > 0 && <div class="d" style={{ marginTop: "2px" }}>{t("sites.extraInline", num(extraAvailable))}</div>}
        </div>
        <DailyResponsesCard points={stats?.days ?? null} compact />
      </div>

      {/* Pulsul ultimelor 30 de zile - aceleași totaluri ca pâlnia din Insights,
          aici doar ca cifre de volum, fără interpretare (aia e treaba Insights). */}
      <div class="grid conv-sum-strip" style={{ marginTop: "18px" }}>
        {totals === null &&
          [0, 1, 2].map((i) => (
            <div class="stat" key={i}><Skeleton h="13px" w="55%" /><Skeleton h="26px" style={{ marginTop: "10px" }} /></div>
          ))}
        {totals !== null &&
          pulse.map((p) => (
            <div class="stat" key={p.label}>
              <div class="l"><span class="dot" style={{ background: p.color }} /> {p.label}</div>
              <div class="v">{num(p.value)}</div>
              <div class="d">{t("so.last30")}</div>
            </div>
          ))}
      </div>
    </>
  );
}

/** Cheia de localStorage care ține minte că raportul X a fost colapsat pe situl
 *  dat. Valoarea e `generatedAt`-ul raportului colapsat: un raport NOU are alt
 *  `generatedAt`, deci nu se potrivește și cardul se deschide singur - exact
 *  semantica cerută („ține minte decizia, dar raport nou = iar deschis").
 *  Declarată în cookies.html alături de `amwai_theme` (preferință funcțională). */
const reportCollapseKey = (appId: string): string => `amwai_report_collapsed_${appId}`;

/**
 * Raportul curent, ca pagină, nu ca teaser: card lat, fără înălțime fixă și fără
 * `overflow:hidden`, cu blocurile de text limitate la o măsură de lectură
 * (`max-width` în ch) ca pe ecran lat rândurile să nu curgă la 200 de caractere.
 * „Run now" e doar pentru super-admin (același semnal ca restul UI-ului admin).
 *
 * Colapsabil DOAR când există un raport de ascuns: paywall-ul, skeletonul și
 * starea goală rămân mereu vizibile (un teaser colapsat n-ar mai vinde nimic).
 */
function CurrentReportCard({
  appId,
  me,
  summary,
  generatedAt,
  periodStart,
  periodEnd,
  loading,
  paywall,
  onRan,
}: {
  appId: string;
  me: Me;
  summary: AiSummaryBody | null;
  generatedAt: number | null;
  periodStart: number | null;
  periodEnd: number | null;
  loading: boolean;
  paywall: boolean;
  onRan: () => void;
}) {
  const [running, setRunning] = useState(false);
  const [runMsg, setRunMsg] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);

  // Starea inițială vine din localStorage abia când știm CE raport arătăm:
  // colapsat doar dacă exact raportul memorat e încă cel curent.
  useEffect(() => {
    if (generatedAt === null) { setCollapsed(false); return; }
    try { setCollapsed(localStorage.getItem(reportCollapseKey(appId)) === String(generatedAt)); }
    catch { setCollapsed(false); }
  }, [appId, generatedAt]);

  const collapsible = !paywall && !loading && summary !== null;
  const toggle = (): void => {
    setCollapsed((v) => {
      const next = !v;
      // preferință pur cosmetică - un storage plin/blocat nu trebuie să strice nimic
      try {
        if (next && generatedAt !== null) localStorage.setItem(reportCollapseKey(appId), String(generatedAt));
        else localStorage.removeItem(reportCollapseKey(appId));
      } catch { /* ignorat deliberat */ }
      return next;
    });
  };

  async function run() {
    setRunning(true); setRunMsg(null);
    try {
      const res = await api.runAiSummary(appId);
      if ("skipped" in res) setRunMsg(t("insights.notEnough"));
      else onRan(); // re-fetch insights + arhivă → raportul canonic
    } catch (err) { setRunMsg(errText(err)); } finally { setRunning(false); }
  }

  const open = !collapsible || !collapsed;
  return (
    <div class={`card${collapsible ? ` conv-collapsible${open ? " open" : ""}` : ""}`}>
      <div class="card-head">
        {collapsible ? (
          <button type="button" class="conv-head-toggle" aria-expanded={open} onClick={toggle}>
            <span class="conv-chevron"><IconChevronDown /></span>
            <span class="conv-head-txt">
              <h2>{t("insights.aiTitle")}</h2>
              <p>{t("progress.current.sub")}</p>
            </span>
          </button>
        ) : (
          <div><h2>{t("insights.aiTitle")}</h2><p>{t("progress.current.sub")}</p></div>
        )}
        {me.isAdmin && !paywall && (
          <button class="btn ghost sm" disabled={running} onClick={() => void run()}>
            {running ? t("insights.running") : <><IconSpark /> {t("insights.runNow")}</>}
          </button>
        )}
      </div>

      {paywall && (
        <EmptyMini icon={<IconSpark />} text={t("insights.locked")} cta={{ label: upgradeLabel("insights.lockedCta"), onClick: () => goUpgrade() }} />
      )}

      {!paywall && loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <Skeleton h="22px" w="70%" />
          <Skeleton h="14px" />
          <Skeleton h="14px" w="85%" />
          <Skeleton h="14px" w="60%" />
        </div>
      )}

      {!paywall && !loading && summary && open && (
        <div class="progress-report">
          <div class="progress-report-meta">
            {generatedAt !== null && <span>{t("insights.generated", fmtDate(generatedAt))}</span>}
            {periodStart !== null && periodEnd !== null && (
              <span>{t("progress.period", fmtDate(periodStart), fmtDate(periodEnd))}</span>
            )}
          </div>
          <ReportBody summary={summary} />
        </div>
      )}

      {!paywall && !loading && !summary && (
        <EmptyMini icon={<IconSpark />} text={t("insights.summaryEmpty")} />
      )}

      {runMsg && <div style={{ marginTop: "10px" }}><Msg kind="info" text={runMsg} /></div>}
    </div>
  );
}

/** Corpul unui raport AI - folosit identic de raportul curent și de detaliul din arhivă. */
function ReportBody({ summary }: { summary: AiSummaryBody }) {
  return (
    <>
      <p class="progress-report-headline">{summary.headline}</p>
      {summary.guidance.comment && <p class="progress-report-comment">{summary.guidance.comment}</p>}

      {summary.recommendations.length > 0 && (
        <>
          <h4 class="insights-report-h">{t("insights.report.recommendations")}</h4>
          <ul class="insights-recs">{summary.recommendations.map((r, i) => <li key={i}>{r}</li>)}</ul>
        </>
      )}

      {summary.frictions.length > 0 && (
        <>
          <h4 class="insights-report-h">{t("insights.report.friction")}</h4>
          {summary.frictions.map((f, i) => (
            <div key={i} class="insights-report-item">
              <div class="insights-report-item-head">
                <span class="mono">{f.path || "(no page)"}</span>
                <span class="badge muted">{f.signal}</span>
              </div>
              <p class="progress-report-detail">{f.detail}</p>
            </div>
          ))}
        </>
      )}

      {summary.unmet.length > 0 && (
        <>
          <h4 class="insights-report-h">{t("insights.report.unmet")}</h4>
          {summary.unmet.map((u, i) => (
            <div key={i} class="insights-report-item">
              <div class="insights-report-item-head">
                <span>{u.theme}</span>
                <span class="badge muted">{t("insights.unmet.approx", num(u.approxCount))}</span>
              </div>
              {u.examples.length > 0 && (
                <ul class="insights-examples">{u.examples.map((ex, j) => <li key={j}>{ex}</li>)}</ul>
              )}
            </div>
          ))}
        </>
      )}
    </>
  );
}

/**
 * Arhiva rapoartelor: ACORDEON inline, ca lista de conversații - fiecare rând se
 * desfășoară pe loc, cu textul raportului înăuntru (cerut de Radu, 2 aug 2026;
 * înainte era tabel + un panou de detaliu comun sub el, care sărea vizual).
 * Cel mai recent e marcat „Latest": e același pe care îl arată cardul de sus,
 * listat aici pentru continuitatea istoricului.
 */
function ReportArchiveCard({ rows, loading, paywall }: { rows: AiSummaryRecord[] | null; loading: boolean; paywall: boolean }) {
  const [openId, setOpenId] = useState<number | null>(null);
  const toggle = (id: number) => setOpenId((cur) => (cur === id ? null : id));

  return (
    <div class="card">
      <div class="card-head"><div><h2>{t("progress.archive.title")}</h2><p>{t("progress.archive.sub")}</p></div></div>

      {paywall && (
        <EmptyMini icon={<IconSpark />} text={t("insights.locked")} cta={{ label: upgradeLabel("insights.lockedCta"), onClick: () => goUpgrade() }} />
      )}

      {!paywall && loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <Skeleton h="44px" style={{ borderRadius: "10px" }} />
          <Skeleton h="44px" style={{ borderRadius: "10px" }} />
        </div>
      )}

      {!paywall && !loading && rows !== null && rows.length === 0 && (
        <EmptyMini icon={<IconSpark />} text={t("progress.archive.empty")} />
      )}

      {!paywall && !loading && rows !== null && rows.length > 0 && (
        <>
          <div class="conv-session-list">
            {rows.map((r, i) => (
              <div class={`conv-session${openId === r.id ? " open" : ""}`} key={r.id}>
                <button type="button" class="conv-session-row" onClick={() => toggle(r.id)} aria-expanded={openId === r.id}>
                  <span class="conv-session-main">
                    <span class="archive-period">
                      {fmtDate(r.periodStart)} – {fmtDate(r.periodEnd)}
                      {i === 0 && <span class="badge plan" style={{ marginLeft: "6px" }}>{t("progress.latest")}</span>}
                    </span>
                    <span class="conv-session-q">{r.body.headline}</span>
                  </span>
                  <span class="conv-session-meta">
                    <span>{t("insights.generated", fmtDate(r.generatedAt))}</span>
                    <span class="conv-chevron"><IconChevronDown /></span>
                  </span>
                </button>
                {openId === r.id && (
                  <div class="conv-session-body">
                    <div class="progress-report"><ReportBody summary={r.body} /></div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {rows.length === 1 && <p class="insights-meta">{t("progress.archive.onlyOne")}</p>}
        </>
      )}
    </div>
  );
}

/**
 * Etajele 2b + 2c din tabul Insights: „ajută cu adevărat?" (funnelul de ghidare)
 * și „ce repari pe sit?" (fricțiune + nevoi neacoperite, sub un titlu de grup).
 * Tot blocul e gated pe `analytics` - paywall client-side, cu preview blurat.
 */
function InsightsSection({
  appId,
  insights,
  summary,
  paywall,
  loading,
  targetMissing,
}: {
  appId: string;
  insights: InsightsResponse | null;
  /** același raport pe care îl arată cardul-erou - o singură sursă, ca „nevoile
   *  neacoperite" să nu poată diverge de raportul citit deasupra */
  summary: AiSummaryBody | null;
  paywall: boolean;
  loading: boolean;
  targetMissing: number;
}) {
  if (paywall) {
    return (
      <div class="card" style={{ marginTop: "18px" }}>
        <div class="card-head"><div><h2>{t("insights.friction.title")}</h2><p>{t("insights.friction.sub")}</p></div></div>
        <div class="blurred-preview" aria-hidden="true">
          <div class="insights-friction-row">
            <div class="insights-friction-path">/pricing</div>
            <div class="insights-metrics">
              <FrictionMetric label={t("insights.m.questions")} value={42} max={42} kind="questions" />
              <FrictionMetric label={t("insights.m.fallbacks")} value={11} max={42} kind="fallbacks" />
              <FrictionMetric label={t("insights.m.abandoned")} value={7} max={42} kind="abandoned" />
              <FrictionMetric label={t("insights.m.targetMissing")} value={3} max={42} kind="target" />
            </div>
          </div>
        </div>
        <EmptyMini icon={<IconChart />} text={t("insights.locked")} cta={{ label: upgradeLabel("insights.lockedCta"), onClick: () => goUpgrade() }} />
      </div>
    );
  }

  return (
    <div style={{ marginTop: "18px" }}>
      <GuidanceFunnelCard funnel={insights?.funnel ?? null} targetMissing={targetMissing} loading={loading} />

      <div class="insights-group" style={{ marginTop: "18px" }}>
        <div class="insights-group-head">
          <h2>{t("insights.q3.title")}</h2>
          <p>{t("insights.q3.sub")}</p>
        </div>
        <div class="grid insights-grid">
          <FrictionCard appId={appId} pages={insights?.frictionPages ?? []} loading={loading} />
          <UnmetNeedsCard summary={summary} loading={loading} />
        </div>
      </div>
    </div>
  );
}

const FRICTION_METRICS = [
  { key: "questions", label: "insights.m.questions", tip: "insights.tip.questions", kind: "questions" },
  { key: "fallbacks", label: "insights.m.fallbacks", tip: "insights.tip.fallbacks", kind: "fallbacks" },
  { key: "abandoned", label: "insights.m.abandoned", tip: "insights.tip.abandoned", kind: "abandoned" },
  { key: "targetMissing", label: "insights.m.targetMissing", tip: "insights.tip.targetMissing", kind: "target" },
] as const;

/** O bară-metrică din rândul de fricțiune: etichetă + bară proporțională + număr. */
function FrictionMetric({ label, tip, value, max, kind }: { label: string; tip?: string; value: number; max: number; kind: string }) {
  const width = max > 0 && value > 0 ? Math.max(6, (value / max) * 100) : 0;
  return (
    <div class="insights-metric" title={`${label}: ${num(value)}`}>
      <span class="insights-metric-lab">{label}{tip && <> <InfoTip text={tip} /></>}</span>
      <span class="insights-bar"><span class={`insights-bar-fill m-${kind}`} style={{ width: `${width}%` }} /></span>
      <span class="insights-metric-n">{num(value)}</span>
    </div>
  );
}

/**
 * „Where visitors get stuck": top pagini cu fricțiune, bare per metrică.
 * Fiecare cale e un buton spre tabul Conversations al aceluiași sit: „ce e de
 * reparat pe /pricing" se verifică citind conversațiile, nu privind barele.
 */
function FrictionCard({ appId, pages, loading }: { appId: string; pages: InsightsResponse["frictionPages"]; loading: boolean }) {
  const rows = pages.slice(0, 7);
  // scară comună tuturor barelor din card, ca metricele să fie comparabile vizual
  const max = Math.max(1, ...rows.flatMap((p) => [p.questions, p.fallbacks, p.abandoned, p.targetMissing]));
  return (
    <div class="card">
      <div class="card-head"><div><h2>{t("insights.friction.title")}</h2><p>{t("insights.friction.sub")}</p></div></div>
      {loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <Skeleton h="52px" style={{ borderRadius: "10px" }} />
          <Skeleton h="52px" style={{ borderRadius: "10px" }} />
          <Skeleton h="52px" style={{ borderRadius: "10px" }} />
        </div>
      )}
      {!loading && rows.length === 0 && <EmptyMini icon={<IconChart />} text={t("insights.friction.empty")} />}
      {!loading && rows.map((p) => (
        <div class="insights-friction-row" key={p.path || "(no page)"}>
          <button
            type="button"
            class="insights-friction-path"
            title={t("insights.friction.viewConv", p.path || "(no page)")}
            onClick={() => navigate(`#/dashboard/${appId}/conversations`)}
          >
            {/* titlul paginii când există (oamenii citesc titluri, nu path-uri);
                path-ul rămâne alături, mic - el e cheia tehnică a rândului */}
            <span class="insights-friction-path-txt">{p.title ?? (p.path || "(no page)")}</span>
            {p.title !== null && <span class="insights-friction-path-sub">{p.path || "/"}</span>}
            <span class="insights-friction-path-go" aria-hidden="true"><IconChat /></span>
          </button>
          <div class="insights-metrics">
            {FRICTION_METRICS.map((m) => (
              <FrictionMetric key={m.key} label={t(m.label)} tip={t(m.tip)} value={p[m.key]} max={max} kind={m.kind} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * „Does the assistant actually help?": funnel CSS pur asked → guided → reached
 * + ramuri. Disclaimerul de scară (`funnel.guidanceScale`) stă AICI, nu în
 * pâlnia de adopție: el vorbește despre episoade de ghidare, care sunt exact
 * cifrele din acest card.
 */
function GuidanceFunnelCard({ funnel, targetMissing, loading }: { funnel: InsightsResponse["funnel"] | null; targetMissing: number; loading: boolean }) {
  const steps = funnel
    ? [
        { label: t("insights.funnel.asked"), tip: t("insights.tip.asked"), value: funnel.asked, cls: "s1" },
        { label: t("insights.funnel.guided"), tip: t("insights.tip.guided"), value: funnel.guided, cls: "s2" },
        { label: t("insights.funnel.reached"), tip: t("insights.tip.reached"), value: funnel.reached, cls: "s3" },
      ]
    : [];
  const asked = funnel?.asked ?? 0;
  return (
    <div class="card">
      <div class="card-head"><div><h2>{t("insights.q2.title")}</h2><p>{t("insights.q2.sub")}</p></div></div>
      {loading && <Skeleton h="150px" style={{ borderRadius: "10px" }} />}
      {!loading && asked === 0 && <EmptyMini icon={<IconChart />} text={t("insights.funnel.empty")} />}
      {!loading && asked > 0 && (
        <>
          <div class="insights-funnel">
            {steps.map((s) => (
              <div class="insights-funnel-step" key={s.label}>
                <div class="insights-funnel-top">
                  <span class="insights-funnel-name">{s.label} <InfoTip text={s.tip} /></span>
                  <span class="insights-funnel-count">{num(s.value)} <span class="muted">· {t("insights.funnel.ofAsked", pct(s.value, asked))}</span></span>
                </div>
                <div class="insights-funnel-track"><div class={`insights-funnel-bar ${s.cls}`} style={{ width: `${Math.max(3, pct(s.value, asked))}%` }} /></div>
              </div>
            ))}
          </div>
          <div class="insights-branches">
            <div class="insights-chip abandoned">
              <span class="insights-chip-n">{num(funnel?.abandoned ?? 0)}</span>
              <span>{t("insights.funnel.abandoned")} <InfoTip text={t("insights.tip.abandoned")} /></span>
            </div>
            <div class="insights-chip target">
              <span class="insights-chip-n">{num(targetMissing)}</span>
              <span>{t("insights.funnel.targetMissing")} <InfoTip text={t("insights.tip.targetMissing")} /></span>
            </div>
          </div>

          <p class="funnel-guidance-note">{t("funnel.guidanceScale")}</p>
        </>
      )}
    </div>
  );
}

/** „What visitors can't find": temele neacoperite din rezumatul AI. */
function UnmetNeedsCard({ summary, loading }: { summary: AiSummaryBody | null; loading: boolean }) {
  const unmet = summary?.unmet ?? [];
  return (
    <div class="card">
      <div class="card-head"><div><h2>{t("insights.unmet.title")}</h2><p>{t("insights.unmet.sub")}</p></div></div>
      {loading && (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <Skeleton h="44px" style={{ borderRadius: "10px" }} />
          <Skeleton h="44px" style={{ borderRadius: "10px" }} />
        </div>
      )}
      {!loading && unmet.length === 0 && <EmptyMini icon={<IconSpark />} text={t("insights.unmet.empty")} />}
      {!loading && unmet.map((u, i) => (
        <div class="insights-unmet-item" key={i}>
          <div class="insights-unmet-head">
            <span class="insights-unmet-theme">{u.theme}</span>
            <span class="badge muted">{t("insights.unmet.approx", num(u.approxCount))}</span>
          </div>
          {u.examples.length > 0 && (
            <div class="insights-unmet-examples">{u.examples.map((ex, j) => <span key={j}>“{ex}”</span>)}</div>
          )}
        </div>
      ))}
    </div>
  );
}

function SiteDetail({
  app,
  me,
  tab,
  actionMsg,
  onChanged,
  onAppPatched,
  onDeleted,
}: {
  app: AppTraffic;
  me: Me;
  /** din hash-ul rutei (#/dashboard/{appId}/{tab}) - ca un refresh să rămână pe secțiunea curentă */
  tab: SiteTab;
  /** eroare de la Go live/Take offline sau Open configurator (acțiuni din topbar) */
  actionMsg: string | null;
  onChanged: () => void;
  /** situl întors de un PATCH, dus în listă pe loc (fără drumul asincron prin `onChanged`) */
  onAppPatched: (app: AdminApp) => void;
  onDeleted: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [name, setName] = useState(app.name);
  const [clientLabel, setClientLabel] = useState(app.clientLabel ?? "");
  // bifă „app de demo" - admin-only, salvată separat (rută de consolă), nu prin `save`
  const [demoOn, setDemoOn] = useState(app.isDemo);
  const [demoBusy, setDemoBusy] = useState(false);
  // bifă „modul de voce" - admin-only, ca demo-ul: rută de consolă, nu `save`
  const [voiceOn, setVoiceOn] = useState(app.voiceEnabled);
  const [voiceBusy, setVoiceBusy] = useState(false);
  // Excluded URLs + Excluded content: persistate imediat la add/remove (CRUD pe
  // listă, nu prin formularul `save`) - starea se rescrie din răspunsul
  // serverului, care normalizează (URL complet → path+hash, dedup).
  const [excludedUrls, setExcludedUrls] = useState<string[]>(app.excludedUrls);
  const [excludedSelectors, setExcludedSelectors] = useState<string[]>(app.excludedSelectors);
  const [saved, setSaved] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const setTab = (t: SiteTab) => navigate(`#/dashboard/${app.appId}/${t}`);
  const clientManagementAllowed = me.plan.features.includes("clientManagement");

  // Cât timp scriptul încă nu a "sunat acasă" (synced=false), reîmprospătăm
  // discret ca badge-ul din Install să treacă singur pe "Synced" imediat ce
  // vizitatorul lipește snippetul - fără să ceară un refresh manual.
  useEffect(() => {
    if (app.synced) return;
    const id = setInterval(onChanged, 4000);
    return () => clearInterval(id);
  }, [app.appId, app.synced]);

  const snippet = `<script\n  src="${location.origin}/sdk.js"\n  data-app-id="${app.appId}"\n  data-public-key="${app.publicKey}">\n</script>`;

  async function save(e: Event) {
    e.preventDefault();
    setBusy(true); setMsg(null); setSaved(false);
    try {
      await api.updateApp(app.appId, {
        name,
        ...(clientManagementAllowed ? { clientLabel: clientLabel.trim() || null } : {}),
      });
      setSaved(true);
      onChanged();
      setTimeout(() => setSaved(false), 1800);
    } catch (err) { setMsg(errText(err)); } finally { setBusy(false); }
  }
  async function saveExcludedUrls(next: string[]) {
    const { app: updated } = await api.updateApp(app.appId, { excludedUrls: next });
    setExcludedUrls(updated.excludedUrls);
    onChanged();
  }
  async function saveExcludedSelectors(next: string[]) {
    const { app: updated } = await api.updateApp(app.appId, { excludedSelectors: next });
    setExcludedSelectors(updated.excludedSelectors);
    onChanged();
  }
  async function doDelete() {
    setBusy(true);
    try {
      await api.deleteApp(app.appId);
      onDeleted();
    } catch (err) { setMsg(errText(err)); setBusy(false); setConfirmDel(false); }
  }

  return (
    <div class="content">
      {/* numele, badge-ul și acțiunile Go live/Open configurator trăiesc acum sus, în topbar */}
      {actionMsg && <Msg kind="err" text={actionMsg} />}
      {msg && <Msg kind="err" text={msg} />}

      <div class="site-tabs">
        <div class="site-tab-rail">
          <button type="button" class={`site-tab-btn ${tab === "overview" ? "active" : ""}`} aria-current={tab === "overview" ? "page" : undefined} onClick={() => setTab("overview")}><IconChart /> <span class="site-tab-lbl">{t("sd.tab.overview")}</span></button>
          <button type="button" class={`site-tab-btn ${tab === "insights" ? "active" : ""}`} aria-current={tab === "insights" ? "page" : undefined} onClick={() => setTab("insights")}><IconTrendUp /> <span class="site-tab-lbl">{t("sd.tab.insights")}</span></button>
          <button type="button" class={`site-tab-btn ${tab === "conversations" ? "active" : ""}`} aria-current={tab === "conversations" ? "page" : undefined} onClick={() => setTab("conversations")}><IconChat /> <span class="site-tab-lbl">{t("sd.tab.conversations")}</span></button>
          <button type="button" class={`site-tab-btn ${tab === "widget" ? "active" : ""}`} aria-current={tab === "widget" ? "page" : undefined} onClick={() => setTab("widget")}><IconPalette /> <span class="site-tab-lbl">{t("sd.tab.widget")}</span></button>
          <button type="button" class={`site-tab-btn ${tab === "settings" ? "active" : ""}`} aria-current={tab === "settings" ? "page" : undefined} onClick={() => setTab("settings")}><IconGear /> <span class="site-tab-lbl">{t("sd.tab.settings")}</span></button>
        </div>

        <div class="site-tab-panel">
          {tab === "overview" && <SiteOverviewTab app={app} me={me} />}

          {tab === "insights" && <InsightsTab app={app} me={me} />}

          {tab === "conversations" && <ConversationsTab app={app} />}

          {tab === "widget" && (
            <>
              <BrandingCard appId={app.appId} initial={app.widgetBranding} me={me} onSaved={onAppPatched} />
              <div style={{ marginTop: "18px" }}><AgentNotesCard appId={app.appId} initial={app.agentNotes} /></div>
              <div style={{ marginTop: "18px" }}><VoiceMinutesCard appId={app.appId} initial={app.voiceMinutesPerVisitor} /></div>
              <div style={{ marginTop: "18px" }}><KnowledgeView appId={app.appId} /></div>
            </>
          )}

          {tab === "settings" && (
            <>
              <div class="grid site-settings-grid">
                <div class="card">
                  <div class="card-head"><h2>{t("sd.settings")}</h2></div>
                  <form onSubmit={save}>
                    <div class="field-row" style={{ marginTop: 0 }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ marginTop: 0 }} for={`${app.appId}-site-name`}>{t("sites.name")}</label>
                        <input id={`${app.appId}-site-name`} value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)} maxLength={100} />
                      </div>
                      {clientManagementAllowed && (
                        <div style={{ flex: 1 }}>
                          <label style={{ marginTop: 0 }} for={`${app.appId}-client-label`}>{t("sd.clientLabel")}</label>
                          <input
                            id={`${app.appId}-client-label`}
                            value={clientLabel}
                            onInput={(e) => setClientLabel((e.target as HTMLInputElement).value)}
                            maxLength={100}
                            placeholder={t("sd.clientLabelPh")}
                          />
                        </div>
                      )}
                    </div>

                    <label>{t("sd.domains")}</label>
                    <div class="row" style={{ gap: "8px", flexWrap: "wrap" }}>
                      {app.allowedOrigins.map((o) => (
                        <span key={o} style={{ background: "var(--line-2)", color: "var(--ink-2)", padding: "5px 11px", borderRadius: "8px", fontSize: "12.5px", fontFamily: "ui-monospace, Menlo, monospace" }}>{o}</span>
                      ))}
                    </div>
                    <p class="muted" style={{ marginTop: "8px", fontSize: "12px" }}>{t("sd.domainsNote")}</p>

                    {me.isAdmin && (
                      <>
                        <label class="check" style={{ marginTop: "16px" }}>
                          <input
                            type="checkbox"
                            checked={demoOn}
                            disabled={demoBusy}
                            onChange={async (e) => {
                              const v = (e.target as HTMLInputElement).checked;
                              setDemoBusy(true); setMsg(null);
                              try {
                                await api.adminSetDemo(app.appId, v);
                                setDemoOn(v);
                                onChanged();
                              } catch (err) { setMsg(errText(err)); } finally { setDemoBusy(false); }
                            }}
                          />
                          <span class="muted">{t("sd.demoFlag")}</span>
                        </label>
                        <p class="muted" style={{ marginTop: "6px", fontSize: "12px" }}>{t("sd.demoFlagHint")}</p>

                        <label class="check" style={{ marginTop: "12px" }}>
                          <input
                            type="checkbox"
                            checked={voiceOn}
                            disabled={voiceBusy}
                            onChange={async (e) => {
                              const v = (e.target as HTMLInputElement).checked;
                              setVoiceBusy(true); setMsg(null);
                              try {
                                await api.adminSetVoice(app.appId, v);
                                setVoiceOn(v);
                                onChanged();
                              } catch (err) { setMsg(errText(err)); } finally { setVoiceBusy(false); }
                            }}
                          />
                          <span class="muted">{t("sd.voiceFlag")}</span>
                        </label>
                        <p class="muted" style={{ marginTop: "6px", fontSize: "12px" }}>{t("sd.voiceFlagHint")}</p>
                      </>
                    )}

                    <button class="btn primary sm" style={{ marginTop: "16px" }} disabled={busy} type="submit">
                      {saved ? <><IconCheck /> {t("sd.saved")}</> : t("sd.save")}
                    </button>
                  </form>
                </div>

                <div class="card">
                  <div class="card-head">
                    <h2>{t("sd.install")}</h2>
                    <span class={`badge ${app.synced ? "ok" : "warn pulse"}`}>{app.synced ? t("sd.synced") : t("sd.syncPending")}</span>
                  </div>
                  <p class="muted" style={{ marginTop: "-4px", marginBottom: "12px" }}>{t("sd.installSub")}</p>
                  <InstallSteps app={app} />
                  <div class="snippet">{snippet}</div>
                  <button class="btn ghost sm" style={{ marginTop: "12px" }} onClick={() => void copyText(snippet).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); })}>
                    {copied ? <><IconCheck /> {t("sd.copied")}</> : <><IconCopy /> {t("sd.copy")}</>}
                  </button>
                  <p class="muted" style={{ marginTop: "12px", marginBottom: 0, fontSize: "12px" }}>{t("sd.installBrandHint")}</p>
                </div>
              </div>

              <FirewallSection />

              {/* Lată cât „Danger zone", nu înghesuită într-o coloană a cardului
                  Install - e propriul ei subiect (politica de confidențialitate a
                  clientului), nu un detaliu de instalare. Ascunsă pe app-urile de
                  demo ale casei (isDemo) - nu au un operator real care să publice
                  o politică de confidențialitate. */}
              {!app.isDemo && <PrivacyPassageSection voice={app.voiceEnabled} />}

              <div class="grid site-settings-grid" style={{ marginTop: "18px" }}>
                <ExclusionListCard
                  title={t("sd.excluded")}
                  sub={t("sd.excludedSub")}
                  placeholder={t("sd.excludedPh")}
                  hint={t("sd.excludedHint")}
                  emptyText={t("sd.excludedEmpty")}
                  values={excludedUrls}
                  onSave={saveExcludedUrls}
                />
                <ExclusionListCard
                  title={t("sd.selExcluded")}
                  sub={t("sd.selExcludedSub")}
                  placeholder={t("sd.selExcludedPh")}
                  hint={t("sd.selExcludedHint")}
                  emptyText={t("sd.selExcludedEmpty")}
                  values={excludedSelectors}
                  onSave={saveExcludedSelectors}
                />
              </div>

              <div class="card" style={{ marginTop: "18px", borderColor: "var(--danger-bg)" }}>
                <div class="card-head">
                  <div><h2 style={{ color: "var(--danger)" }}>{t("sd.danger")}</h2><p>{t("sd.dangerSub")}</p></div>
                  <button class="btn danger" onClick={() => setConfirmDel(true)}><IconTrash /> {t("sd.delete")}</button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {confirmDel && (
        <Modal onClose={() => setConfirmDel(false)}>
          <h3>{t("sd.delTitle")}</h3>
          <p>{t("sd.delBody", name || app.appId)}</p>
          <div class="actions">
            <button class="btn ghost" onClick={() => setConfirmDel(false)}>{t("common.cancel")}</button>
            <button class="btn solid-danger" disabled={busy} onClick={() => void doDelete()}><IconTrash /> {t("sd.delConfirm")}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/**
 * Un buton de copiat pentru un bloc de text lung, în stilul butonului deja
 * folosit pentru snippet-ul de instalare (btn ghost sm + IconCopy/IconCheck) -
 * refolosit aici ca să nu apară un al doilea pattern vizual de „copy" pe
 * aceeași pagină.
 */
/**
 * Prompt pentru AI-ul de infrastructură al clientului - construit la runtime,
 * nu în @ai-guidance/shared ca PRIVACY_PASSAGE_AI_PROMPT, fiindcă domeniul de
 * alolist e per-instalare (`location.origin`), nu un text static identic
 * pentru toată lumea. Același ton ca promptul de privacy: context, pași
 * numerotați, DE CE la final - un AI de infra fără motiv riscă să adauge
 * domeniul unde nu trebuie (ex. inbound) sau să-l scoată la următoarea
 * „curățare" de config fiindcă nu înțelege la ce ține.
 */
function firewallAiPrompt(origin: string): string {
  return `I've added a third-party AI chat widget and site-scanning tool to my website (AskMyWebsiteAI). It makes outbound requests from visitors' browsers to a single domain, and I need help making sure my firewall/WAF and Content Security Policy don't block it.

Please do the following, in order:

1. Find where my site's firewall, WAF, or reverse-proxy config allows or denies outbound requests (if any such rule exists), and add an allow rule for this domain: ${origin}
2. Find where my site sets its Content-Security-Policy header (server config, middleware, meta tag, or CDN/edge rules), and add ${origin} to these three directives - create them if they don't exist yet:
   - script-src: loads the widget (${origin}/sdk.js) and the on-page configurator I use to edit it (${origin}/builder.js)
   - connect-src: the chat and site-scanning requests themselves (${origin}/v1/apps/…)
   - img-src: the widget's logo, if I set a custom one (${origin}/v1/apps/…/logo)
3. Tell me exactly which file(s) or config you changed, so I can review before deploying.

Context, in case it matters for how you configure this: no INBOUND access is needed anywhere - this service never connects to my server. Everything happens in the visitor's browser, reaching out to that one domain, from pages of my own site where I've placed the widget.`;
}

/**
 * Colapsabilă, ÎNCHISĂ implicit - aceeași logică de plasament ca
 * PrivacyPassageSection: se intră o dată, la instalare, pe un sit din spatele
 * unui firewall/WAF/CSP, nu la fiecare vizită în Settings. Două coloane: stânga
 * = omul editează singur configul, dreapta = deleagă unei AI de infra (prompt
 * cu domeniul deja completat din `location.origin`, ca omul să nu tot repete
 * un „bla bla" generic la fiecare client cu setup diferit).
 */
function FirewallSection() {
  const [open, setOpen] = useState(false);
  return (
    <div class={`card conv-collapsible${open ? " open" : ""}`} style={{ marginTop: "18px" }}>
      <div class="card-head">
        <button type="button" class="conv-head-toggle" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          <span class="conv-chevron"><IconChevronDown /></span>
          <span class="conv-head-txt">
            <h2><span style={{ display: "inline-flex", width: "16px", height: "16px", verticalAlign: "-3px", marginRight: "2px" }}><IconShield /></span>{t("sd.fw.title")}</h2>
            <p>{t("sd.fw.sub")}</p>
          </span>
        </button>
      </div>
      {open && (
        <div class="grid cols-2" style={{ marginTop: "14px" }}>
          <div class="fw-manual">
            <p class="muted" style={{ fontSize: "12.5px" }}>{t("sd.fw.manualIntro")}</p>
            <p style={{ marginTop: "12px", fontSize: "12.5px" }}>{t("sd.fw.allow")}: <code>{location.origin}</code></p>
            <p style={{ marginTop: "12px", marginBottom: "6px", fontSize: "12.5px" }}>{t("sd.fw.cspIntro")}</p>
            <ul class="fw-manual-list">
              <li><code>script-src</code> - {t("sd.fw.script")} (<code>/sdk.js</code>, <code>/builder.js</code>)</li>
              <li><code>connect-src</code> - {t("sd.fw.connect")} (<code>/v1/apps/…</code>)</li>
              <li><code>img-src</code> - {t("sd.fw.img")} (<code>/v1/apps/…/logo</code>)</li>
            </ul>
            <p class="muted" style={{ marginTop: "12px", fontSize: "12px" }}>{t("sd.fw.inbound")}</p>
          </div>
          <div>
            <p class="muted" style={{ fontSize: "12.5px" }}>{t("sd.fw.aiIntro")}</p>
            <PrivacyCopyBlock label={t("sd.fw.promptLabel")} value={firewallAiPrompt(location.origin)} />
          </div>
        </div>
      )}
    </div>
  );
}

function PrivacyCopyBlock({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ marginTop: "14px" }}>
      <p style={{ fontWeight: 600, fontSize: "13px", marginBottom: "6px" }}>{label}</p>
      <div class="snippet" style={{ whiteSpace: "pre-wrap", maxHeight: "220px", overflowY: "auto" }}>{value}</div>
      <button
        class="btn ghost sm"
        style={{ marginTop: "8px" }}
        onClick={() => void copyText(value).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); })}
      >
        {copied ? <><IconCheck /> {t("sd.copied")}</> : <><IconCopy /> {t("sd.privacy.copy")}</>}
      </button>
    </div>
  );
}

/**
 * Pasajul pe care clientul îl adaugă în PROPRIA lui politică de confidențialitate
 * (el e operatorul, art. 13 GDPR e obligația lui) - text din @ai-guidance/shared,
 * afișat neschimbat. Detalii: docs/privacy-passage.md.
 *
 * Două coloane, DOUĂ căi separate spre același rezultat, nu trei blocuri la rând:
 * stânga = omul editează singur (pasaj + unde-l pune), dreapta = omul deleagă
 * unei AI editarea politicii lui (promptul). Amestecate într-o singură coloană,
 * cineva care vrea calea AI ar citi întâi pasajul brut fără niciun motiv.
 *
 * Colapsabil, ÎNCHIS implicit (colapsat = starea inițială, nu doar o opțiune):
 * se intră rar aici - de obicei o dată, la instalare - spre deosebire de
 * Settings/Install de deasupra. Fetch nu există pentru cardul ăsta (text
 * static din @ai-guidance/shared), deci nu e nimic de amânat la deschidere,
 * doar spațiul vertical pe care-l ocupă închis.
 */
function PrivacyPassageSection({ voice }: { voice?: boolean }) {
  const [open, setOpen] = useState(false);
  // App cu voce → pasajul poartă și paragraful ElevenLabs; promptul AI se
  // compune pe același text, ca cele două coloane să nu se contrazică.
  const passage = voice ? `${PRIVACY_PASSAGE}\n\n${PRIVACY_PASSAGE_VOICE}` : PRIVACY_PASSAGE;
  const aiPrompt = voice ? buildPrivacyPassageAiPrompt(passage) : PRIVACY_PASSAGE_AI_PROMPT;
  return (
    <div class={`card privacy-passage-card conv-collapsible${open ? " open" : ""}`} style={{ marginTop: "18px" }}>
      <div class="card-head">
        <button type="button" class="conv-head-toggle" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          <span class="conv-chevron"><IconChevronDown /></span>
          <span class="conv-head-txt">
            <h2>{t("sd.privacy.title")}</h2>
            <p>{t("sd.privacy.sub")}</p>
          </span>
        </button>
      </div>
      {open && (
        <div class="grid cols-2" style={{ marginTop: "14px" }}>
          <div>
            <p class="muted" style={{ fontSize: "12.5px" }}>{t("sd.privacy.manualIntro")}</p>
            <PrivacyCopyBlock label={t("sd.privacy.passageLabel")} value={passage} />
            {/* Notă pentru om, NU text de lipit - fără PrivacyCopyBlock/Copy: nimeni
                nu inserează ghidarea de plasare, cuvânt cu cuvânt, în propria politică.
                Promptul din dreapta poartă deja același text („Placement guidance:"
                din PRIVACY_PASSAGE_AI_PROMPT), deci un buton de Copy aici ar fi
                redundant ȘI ar sugera greșit că e conținut de politică. */}
            <p style={{ fontWeight: 600, fontSize: "13px", marginTop: "16px", marginBottom: "4px" }}>{t("sd.privacy.placementLabel")}</p>
            <p class="muted" style={{ fontSize: "12.5px" }}>{PRIVACY_PASSAGE_PLACEMENT}</p>
          </div>
          <div>
            <p class="muted" style={{ fontSize: "12.5px" }}>{t("sd.privacy.aiIntro")}</p>
            <PrivacyCopyBlock label={t("sd.privacy.promptLabel")} value={aiPrompt} />
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
//  PLAN & BILLING
// ============================================================

// Pagina de plan MINIMĂ (24 oct 2026, redeschiderea vânzării - vezi
// docs/plan-relansare-abonamente.md, Pas B simplificat). Deliberat mai puțin
// decât ce exista până pe 7 aug 2026 (păstrat în git, commit 00be62f~1):
//  - trei carduri de plan + Checkout găzduit de Stripe pentru prima plată
//    (pasul de țară decide IGIC-ul canar, ca înainte);
//  - upgrade/downgrade pe abonamentul existent prin `changePlan` (proration
//    cu dată neschimbată, respectiv la finalul perioadei) - NU prin portal:
//    portalul are `subscription_update` blocat tocmai ca să nu ocolească
//    politica asta (scripts/syncStripePortal.ts);
//  - card, facturi, anulare: portalul Stripe.
// Fără add-on-uri self-serve (extra answers / extra sites: la cerere, prin
// admin) și fără cod promo / oferta de lansare (FOUNDING30 a expirat pe 1 sep).
// Toate revin din codul vechi când există cerere.
function PlanView({ me, reloadMe }: { me: Me; reloadMe: () => void }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err" | "info"; text: string } | null>(null);
  // Așteptăm confirmarea webhook-ului: backendul scrie planul DOAR la webhook,
  // deci facem poll pe /me până îl vedem schimbat, cu plafon de 30s. Două
  // momente: întoarcerea din Checkout și upgrade-ul pe cardul salvat.
  const [confirming, setConfirming] = useState<null | { kind: "checkout" } | { kind: "resume" } | { kind: "upgrade"; planId: string; label: string }>(null);
  const confirmingSince = useRef(0);
  // Pasul de dinainte de Checkout: țara (+ CP la Spania) decide IGIC-ul canar
  // pe factură - Stripe află adresa abia ÎN checkout, prea târziu pentru rată.
  const [billingAsk, setBillingAsk] = useState<Plan | null>(null);
  const [billCountry, setBillCountry] = useState("");
  const [billPostal, setBillPostal] = useState("");
  const { confirmNode, confirm } = useConfirm();
  // Preselectăm din profilul de firmă (obligatoriu la register): popup-ul e o
  // confirmare, nu o cerere de date noi. Sincronizat la fiecare deschidere.
  useEffect(() => {
    if (!billingAsk) return;
    setBillCountry(me.business.country ?? "");
    setBillPostal(me.business.postalCode ?? "");
  }, [billingAsk]);

  // Planul ales pe landing (?plan=): deschidem direct pasul de țară pentru el.
  // Doar fără abonament - cine are deja unul decide upgrade/downgrade, cu confirmări.
  useEffect(() => {
    const wanted = consumePlanIntent();
    if (!wanted || me.plan.hasStripeSubscription) return;
    const plan = me.plans.find((p) => p.id === wanted && !p.contactOnly);
    if (plan && !grantCovers(plan)) setBillingAsk(plan);
  }, []);

  // Întoarcerea din Checkout (#/plan?checkout=success|cancelled).
  useEffect(() => {
    const m = location.hash.match(/\?checkout=(success|cancelled)/);
    if (!m) return;
    // scoatem parametrul din hash fără navigare, ca un refresh să nu re-declanșeze
    history.replaceState(null, "", `${location.pathname}${location.search}#/plan`);
    if (m[1] === "cancelled") {
      setMsg({ kind: "info", text: t("plan.checkoutCancelled") });
      return;
    }
    confirmingSince.current = Date.now();
    setConfirming({ kind: "checkout" });
  }, []);

  useEffect(() => {
    if (!confirming) return;
    // „Gata" nu e răspunsul API-ului, ci apariția schimbării în /me.
    const done =
      confirming.kind === "checkout"
        ? me.plan.hasStripeSubscription
        : confirming.kind === "resume"
          ? !me.plan.cancelAtPeriodEnd
          : me.plan.plan === confirming.planId;
    if (done) {
      setConfirming(null);
      setMsg({
        kind: "ok",
        text:
          confirming.kind === "checkout"
            ? t("plan.checkoutSuccess")
            : confirming.kind === "resume"
              ? t("plan.resumed")
              : t("plan.upgraded", confirming.label),
      });
      return;
    }
    if (Date.now() - confirmingSince.current > 30_000) {
      setConfirming(null);
      setMsg({ kind: "err", text: t("plan.confirmTimeout") });
      return;
    }
    const id = setTimeout(reloadMe, 2000);
    return () => clearTimeout(id);
  }, [confirming, me]);

  /** Planul acordat de echipă acoperă deja cardul ăsta → nu e nimic de plătit
   *  (backend-ul refuză oricum cu `plan_granted`; aici doar nu arătăm butonul). */
  function grantCovers(plan: Plan): boolean {
    if (!me.plan.grantedPlan) return false;
    const granted = me.plans.find((p) => p.id === me.plan.grantedPlan);
    return granted ? granted.priceEur >= plan.priceEur : false;
  }

  async function confirmBilling() {
    if (!billingAsk || !billCountry) return;
    if (billCountry === "ES" && !/^\d{5}$/.test(billPostal.trim())) return;
    const billing = { country: billCountry, ...(billCountry === "ES" ? { postalCode: billPostal.trim() } : {}) };
    setBusy(true);
    setMsg(null);
    try {
      const { url } = await api.checkout(billingAsk.id, billing);
      location.href = url; // Checkout găzduit de Stripe - cardul nu atinge paginile noastre
    } catch (err) {
      setMsg({ kind: "err", text: billingErrText(err) });
      setBusy(false);
      setBillingAsk(null);
    }
  }

  async function choose(plan: Plan) {
    setMsg(null);
    // Backendul refuză oricum (409 subscription_ending): un abonament care se
    // încheie nu primește schimbări de plan. Spunem asta ÎNAINTE de dialogul de confirmare.
    if (me.plan.cancelAtPeriodEnd) {
      setMsg({ kind: "err", text: t("errb.subscription_ending") });
      return;
    }
    if (!me.plan.hasStripeSubscription) {
      setBillingAsk(plan);
      return;
    }
    // Confirmarea ÎNAINTE de setBusy, altfel cardurile rămân blocate cât stă modalul.
    const isUpgrade = plan.priceEur > me.plan.priceEur;
    const ok = await confirm(
      isUpgrade
        ? { title: t("plan.upgradeTitle", plan.label), body: t("plan.upgradeConfirm", plan.label), confirmLabel: t("plan.upgradeBtn", plan.label) }
        : { title: t("plan.downgradeTitle", plan.label), body: t("plan.downgradeConfirm", plan.label), warn: t("plan.downgradeWarn"), confirmLabel: t("plan.downgradeBtn", plan.label) },
    );
    if (!ok) return;
    setBusy(true);
    try {
      const res = await api.changePlan(plan.id);
      if (res.effective === "now") {
        // Upgrade: Stripe a facturat diferența, dar `accounts.plan` se schimbă la webhook.
        confirmingSince.current = Date.now();
        setConfirming({ kind: "upgrade", planId: plan.id, label: plan.label });
      } else {
        // Downgrade: `pending_plan` e scris sincron; data comutării = `renewsAt`.
        setMsg({
          kind: "ok",
          text: me.plan.renewsAt ? t("plan.downgradeScheduled", plan.label, fmtDate(me.plan.renewsAt)) : t("plan.downgradeScheduledSoon", plan.label),
        });
      }
      reloadMe();
    } catch (err) {
      setMsg({ kind: "err", text: billingErrText(err) });
    } finally {
      setBusy(false);
    }
  }

  async function cancelDowngrade() {
    setMsg(null);
    setBusy(true);
    try {
      await api.changePlan(me.plan.plan); // re-alegerea planului curent anulează downgrade-ul
      setMsg({ kind: "ok", text: t("plan.downgradeCancelled") });
      reloadMe();
    } catch (err) {
      setMsg({ kind: "err", text: billingErrText(err) });
    } finally {
      setBusy(false);
    }
  }

  async function openPortal() {
    setMsg(null);
    try {
      const { url } = await api.billingPortal();
      location.href = url;
    } catch (err) {
      setMsg({ kind: "err", text: billingErrText(err) });
    }
  }

  // „Resume subscription": cere la Stripe întoarcerea anulării programate. Starea
  // din /me se schimbă abia când sosește webhook-ul, deci același poll ca la plată.
  async function resumeSubscription() {
    setMsg(null);
    setBusy(true);
    try {
      await api.resumeSubscription();
      confirmingSince.current = Date.now();
      setConfirming({ kind: "resume" });
      reloadMe();
    } catch (err) {
      setMsg({ kind: "err", text: billingErrText(err) });
    } finally {
      setBusy(false);
    }
  }

  const activated = hasActiveSubscription(me.plan);
  const answersPct = pct(me.plan.answersUsed, me.plan.answersLimit);
  const sitesPct = pct(me.plan.sitesUsed, me.plan.sitesLimit);
  const locked = busy || Boolean(confirming);

  return (
    <div class="content">
      <div class="center-wide">
        {confirming && (
          <div style={{ marginBottom: "18px" }}>
            <Msg
              kind="info"
              text={
                confirming.kind === "checkout"
                  ? t("plan.confirming")
                  : confirming.kind === "resume"
                    ? t("plan.resumeWait")
                    : t("plan.applyingUpgrade", confirming.label)
              }
            />
          </div>
        )}
        {msg && (
          <div style={{ marginBottom: "18px" }}>
            <Msg kind={msg.kind} text={msg.text} />
          </div>
        )}
        {/* Abonament anulat (portal Stripe / ștergerea contului) dar încă plătit
            până la scadență: clientul trebuie să vadă că se OPREȘTE, nu că se reînnoiește. */}
        {me.plan.hasStripeSubscription && me.plan.cancelAtPeriodEnd && me.plan.renewsAt && !confirming && (
          <div class="msg warn" style={{ marginBottom: "18px", display: "flex", gap: "12px", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap" }}>
            <span>{t("plan.endingBanner", fmtDate(me.plan.renewsAt))}</span>
            <button class="btn primary sm" disabled={locked} onClick={() => void resumeSubscription()}>{t("plan.resume")}</button>
          </div>
        )}

        {/* consumul curent, ca să alegi planul în cunoștință de cauză */}
        <div class="stats" style={{ marginBottom: "48px" }}>
          <div class="stat">
            <div class="l"><span class="dot" style={{ background: "#818cf8" }} /> {t("plan.current")}</div>
            <div class="v" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>
                {me.plan.planLabel}
                {/* Planul afișat rămâne cel PLĂTIT (onest față de factură); grantul
                    e explicat separat. */}
                {me.plan.grantedPlan && (
                  <span class="badge plan" style={{ marginLeft: "8px", verticalAlign: "middle" }}>
                    {t("plan.grantedBadge", planLabelFor(me, me.plan.grantedPlan))}
                  </span>
                )}
              </span>
              {me.plan.priceEur > 0 && <span style={{ fontSize: "25px", fontWeight: 750 }}>€{me.plan.priceEur}<span style={{ fontSize: "15px", color: "var(--ink-3)", fontWeight: 600 }}>{t("plan.perMonth")}</span></span>}
            </div>
            {me.plan.hasStripeSubscription && (
              <div class="d" style={{ marginTop: "8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                {renewalLine(me.plan) ? <span>{renewalLine(me.plan)}</span> : <span />}
                <a href="#" onClick={(e) => { e.preventDefault(); void openPortal(); }}>{t("plan.manageBilling")}</a>
              </div>
            )}
          </div>
          <div class="stat">
            <div class="l"><span class="dot" style={{ background: "#a855f7" }} /> {t("plan.answersLeft")}</div>
            <div class="v">{num(me.plan.answersRemaining)} <span class="vsub">/ {num(me.plan.answersLimit)}</span></div>
            <div class="pbar" style={{ marginTop: "8px" }}><span style={{ width: `${100 - answersPct}%` }} /></div>
          </div>
          <div class="stat">
            <div class="l"><span class="dot" style={{ background: "#22c55e" }} /> {t("ov.sitesUsed")}</div>
            <div class="v">{num(me.plan.sitesUsed)} <span class="vsub">/ {num(me.plan.sitesLimit)}</span></div>
            <div class="pbar" style={{ marginTop: "8px" }}><span style={{ width: `${sitesPct}%` }} /></div>
          </div>
        </div>

        {/* Aceleași carduri ca pe landing (deploy/landing → .pricing-grid / .plan-card). */}
        <div class="pricing-grid" style={{ marginBottom: "12px" }}>
          {me.plans.map((plan) => {
            // „current" = planul e și ACTIVAT (abonament sau scadență pusă de noi);
            // un cont pus manual pe un plan plătit fără activare nu pare cumpărat.
            const current = plan.id === me.plan.plan && activated;
            const covered = !current && !plan.contactOnly && grantCovers(plan);
            return (
              <div class={`card plan-card ${current ? "current" : ""}`} key={plan.id}>
                {current && <span class="plan-badge is-current">{t("plan.current")}</span>}
                <div class="plan-head">
                  <span class="plan-name">{plan.label}</span>
                  <span class="plan-price">
                    {plan.contactOnly ? (
                      <span class="amount">{t("plan.customPrice")}</span>
                    ) : (
                      <><span class="amount">€{plan.priceEur}</span> <span class="period">{t("plan.perMonth")}</span></>
                    )}
                  </span>
                </div>
                <img class="plan-icon" src={PLAN_ICON[plan.id]} width="136" height="136" alt="" loading="lazy" />
                <p class="plan-tagline">{plan.tagline}</p>
                <ul class="plan-highlights">
                  {plan.highlights.map((h) => (
                    <li key={h}>{h}</li>
                  ))}
                </ul>
                {me.plan.pendingPlan === plan.id && me.plan.renewsAt && (
                  <p class="plan-pending">{t("plan.pendingBadge", fmtDate(me.plan.renewsAt))}</p>
                )}
                {current ? (
                  me.plan.pendingPlan ? (
                    <button class="btn ghost block plan-cta" disabled={locked} onClick={() => void cancelDowngrade()}>{t("plan.cancelDowngrade")}</button>
                  ) : (
                    <button class="btn ghost block plan-cta" disabled>{t("plan.currentBtn")}</button>
                  )
                ) : plan.contactOnly ? (
                  <a class="btn ghost block plan-cta" href={`${SITE_ORIGIN}/contact?email=${encodeURIComponent(me.email)}`} target="_blank" rel="noreferrer">{t("plan.contactUs")}</a>
                ) : covered ? (
                  <button class="btn ghost block plan-cta" disabled title={t("plan.grantedCoveredHint")}>{t("plan.grantedCovered")}</button>
                ) : me.plan.pendingPlan === plan.id ? (
                  <button class="btn ghost block plan-cta" disabled>{t("plan.choose", plan.label)}</button>
                ) : (
                  <button class="btn primary block plan-cta" disabled={locked} onClick={() => void choose(plan)}>
                    {me.plan.hasStripeSubscription ? t("plan.choose", plan.label) : t("plan.subscribe", plan.label)}
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <p class="muted" style={{ marginTop: "12px", fontSize: "12.5px", textAlign: "center" }}>
          {t("plan.renews", fmtDate(me.plan.answersResetAt))} {t("plan.b2bNote")}
        </p>
        {me.plan.hasStripeSubscription && (
          <p class="muted" style={{ marginTop: "6px", fontSize: "12.5px", textAlign: "center" }}>{t("plan.portalNote")}</p>
        )}
        {/* Nu există cumpărare self-serve de răspunsuri / site-uri în plus (decizie
            24 oct 2026, vezi comentariul PlanView): oferta reală e „upgrade sau scrie-ne",
            pentru ORICE plan - bannerul de cotă „Upgrade or add answers" duce aici. */}
        <p class="muted" style={{ marginTop: "6px", fontSize: "12.5px", textAlign: "center" }}>
          {t("plan.addonsNote")}{" "}
          <a href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(t("plan.addonsMailSubject"))}&body=${encodeURIComponent(me.email)}`}>{t("plan.addonsLink")}</a>{" "}
          {t("plan.addonsNoteEnd")}
        </p>

        {confirmNode}

        {billingAsk && (
          <Modal onClose={() => setBillingAsk(null)}>
            <h3>{t("plan.billingTitle")}</h3>
            <p>{t("plan.billingNote")}</p>
            <label>{t("plan.billingCountry")}</label>
            <select value={billCountry} onChange={(e) => setBillCountry((e.target as HTMLSelectElement).value)}>
              <option value="" disabled>-</option>
              {BILLING_COUNTRIES.filter(([code]) => code !== "XX").map(([code, name]) => (
                <option value={code} key={code}>{name}</option>
              ))}
            </select>
            {billCountry === "ES" && (
              <div>
                <label>{t("plan.billingPostal")}</label>
                <input value={billPostal} onInput={(e) => setBillPostal((e.target as HTMLInputElement).value)} placeholder="38001" maxLength={5} />
              </div>
            )}
            <div class="actions">
              <button class="btn ghost" onClick={() => setBillingAsk(null)}>{t("plan.billingCancel")}</button>
              <button
                class="btn primary"
                disabled={busy || !billCountry || (billCountry === "ES" && !/^\d{5}$/.test(billPostal.trim()))}
                onClick={() => void confirmBilling()}
              >
                {t("plan.billingContinue")}
              </button>
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
}

/** Butonul de copiat cu feedback scurt - codul și linkul de referral. */
function CopyBox({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      <label style={{ marginTop: 0 }}>{label}</label>
      <button
        type="button"
        class="help-mail"
        style={{ width: "100%", justifyContent: "space-between", fontFamily: "ui-monospace, SF Mono, Menlo, monospace" }}
        onClick={() => void copyText(value).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); })}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{value}</span>
        <span class="ic">{copied ? <IconCheck /> : <IconCopy />}</span>
      </button>
    </div>
  );
}

const REF_CODE_RE = /^[A-Z0-9-]{4,24}$/;

/**
 * Alegerea codului. Clientul îl scrie singur („RADU20") - de-aia e un formular,
 * nu un cod primit de-a gata: memorabil înseamnă reclamă, random înseamnă zgomot.
 */
function CodePicker({ current, onDone }: { current: string | null; onDone: (code: string) => void }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const valid = REF_CODE_RE.test(code);

  async function submit(e: Event) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const result = await api.claimReferralCode(code);
      onDone(result.code);
    } catch (e2) {
      // erorile de business au mesaj propriu; restul cad pe textul generic
      const reason = e2 instanceof ApiError ? (e2.data as { error?: string })?.error : undefined;
      const known = ["code_taken", "code_reserved", "invalid_format", "same_code"];
      setErr(reason && known.includes(reason) ? t(`ref.err.${reason}` as never) : errText(e2));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit}>
      <div class="row" style={{ alignItems: "flex-start", gap: "12px" }}>
        <div style={{ flex: "1 1 220px", maxWidth: "320px" }}>
          <input
            value={code}
            maxLength={24}
            required
            autoFocus
            placeholder={t("ref.pickPlaceholder")}
            style={{ textTransform: "uppercase", fontFamily: "ui-monospace, SF Mono, Menlo, monospace", letterSpacing: ".04em" }}
            // majuscule pe măsură ce scrie: codul așa trăiește peste tot (link,
            // materiale, DB), deci n-are rost să-l lăsăm să creadă altceva
            onInput={(e) => { setCode((e.target as HTMLInputElement).value.toUpperCase()); setErr(null); }}
          />
          <p class="muted" style={{ marginTop: "6px", fontSize: "12px" }}>{t("ref.pickHint")}</p>
        </div>
        <button class="btn primary" disabled={busy || !valid} type="submit" style={{ marginTop: "2px" }}>
          {busy ? "…" : current ? t("ref.changeBtn") : t("ref.pickBtn")}
        </button>
      </div>
      {err && <Msg kind="err" text={err} />}
    </form>
  );
}

function ReferralView() {
  const [info, setInfo] = useState<ReferralInfo | null>(null);
  const [msg, setMsg] = useState<{ kind: "err" | "ok"; text: string } | null>(null);
  const [changing, setChanging] = useState(false);

  async function reload() {
    setInfo(await api.referral());
  }
  useEffect(() => {
    reload().catch((err) => setMsg({ kind: "err", text: errText(err) }));
  }, []);

  function onClaimed(previous: string | null, code: string) {
    setChanging(false);
    setMsg(previous ? { kind: "ok", text: t("ref.changed", code, previous) } : { kind: "ok", text: t("ref.claimed", code) });
    void reload();
  }

  if (msg?.kind === "err" && !info) return <div class="content"><Msg kind="err" text={msg.text} /></div>;
  if (!info) {
    return (
      <div class="content">
        <Skeleton h="120px" style={{ borderRadius: "16px", marginBottom: "18px" }} />
        <Skeleton h="180px" style={{ borderRadius: "16px" }} />
      </div>
    );
  }

  const steps = [
    t("ref.how1"),
    t("ref.how2", info.discountPercent),
    t("ref.how3", info.commissionPercent),
    t("ref.how4", info.holdDays),
  ];

  return (
    <div class="content">
      <div class="center-wide referral-view">
        {msg && <div style={{ marginBottom: "14px" }}><Msg kind={msg.kind} text={msg.text} /></div>}

        <div class="stats ref-stats" style={{ marginTop: "0", order: "1" }}>
          <div class="stat">
            <div class="l"><span class="dot" style={{ background: "#818cf8" }} /> {t("ref.signups")}</div>
            <div class="v">{num(info.signups)}</div>
          </div>
          <div class="stat">
            <div class="l"><span class="dot" style={{ background: "#a855f7" }} /> {t("ref.converted")}</div>
            <div class="v">{num(info.converted)}</div>
          </div>
          <div class="stat">
            <div class="l"><span class="dot" style={{ background: "#f59e0b" }} /> {t("ref.pending")}</div>
            <div style={{ display: "flex", alignItems: "flex-start", gap: "8px", marginTop: "7px" }}>
              <div class="v">{eur(info.pendingMicroEur, 2)}</div>
              <IconInfo />
            </div>
          </div>
          <div class="stat">
            <div class="l"><span class="dot" style={{ background: "#22c55e" }} /> {t("ref.available")}</div>
            <div class="v">{eur(info.availableMicroEur, 2)}</div>
          </div>
        </div>

        <div class="grid cols-2" style={{ marginTop: "18px", gap: "18px", order: "2" }}>
          <div class="card">
            <div class="card-head"><h2>{t("ref.how")}</h2></div>
            <div class="steps">
              {steps.map((s, i) => (
                <div class="step" key={s}><span class="n">{i + 1}</span> {s}</div>
              ))}
            </div>
            <p class="muted" style={{ marginTop: "14px", fontSize: "12.5px" }}>{t("ref.payoutNote")}</p>
          </div>

          <div class="card">
            <div class="card-head"><h2>{t("ref.earningsTitle")}</h2></div>
            {info.earnings.length === 0 ? (
              <EmptyMini icon={<IconSpark />} text={t("ref.earningsEmpty")} />
            ) : (
              <div class="table-wrap">
                <table class="data">
                  <thead>
                    <tr>
                      <th>{t("ref.col.date")}</th>
                      <th class="num">{t("ref.col.amount")}</th>
                      <th>{t("ref.col.status")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {info.earnings.map((e) => (
                      <tr key={e.id}>
                        <td>{fmtDate(e.createdAt)}</td>
                        <td class="num">{eur(e.amountMicroEur, 2)}</td>
                        <td>
                          {e.status === "available" ? (
                            <span class="badge ok">{t("ref.status.available")}</span>
                          ) : e.status === "cancelled" ? (
                            <span class="badge muted">{t("ref.status.cancelled")}</span>
                          ) : (
                            <span class="badge warn">{t("ref.status.pending", fmtDate(e.availableAt))}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div class="card ref-link" style={{ order: "3" }}>
          {info.code === null || changing ? (
            <>
              <div class="card-head"><div><h2>{t("ref.pickTitle")}</h2><p>{t("ref.pickSub")}</p></div></div>
              {changing && <Msg kind="info" text={t("ref.changeWarn")} />}
              <CodePicker current={info.code} onDone={(code) => onClaimed(info.code, code)} />
              {changing && (
                <button class="btn subtle sm" style={{ marginTop: "12px" }} onClick={() => setChanging(false)}>
                  {t("ref.changeCancel", info.code ?? "")}
                </button>
              )}
            </>
          ) : (
            <>
              <div class="card-head">
                <div><h2>{t("ref.yourLink")}</h2><p>{t("ref.sub")}</p></div>
                <button class="btn subtle sm" onClick={() => setChanging(true)}>{t("ref.change")}</button>
              </div>
              <div class="grid cols-2" style={{ gap: "14px" }}>
                <CopyBox value={info.code} label={t("ref.yourCode")} />
                <CopyBox value={info.link ?? ""} label={t("ref.yourLink")} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
//  ADMIN CONSOLE
// ============================================================

const RENEWAL_GRACE_MS = 5 * 24 * 60 * 60 * 1000;

function isOverdue(renewsAt: number): boolean {
  return Date.now() > renewsAt;
}

/** Zile restante, incluzând perioada de grație de 5 zile - doar informativ, nu blochează nimic. */
function renewalHint(renewsAt: number): string {
  const overdueMs = Date.now() - renewsAt;
  if (overdueMs <= 0) return t("ac.renewalOk");
  const days = Math.ceil(overdueMs / (24 * 60 * 60 * 1000));
  return overdueMs <= RENEWAL_GRACE_MS ? t("ac.renewalGrace", String(days)) : t("ac.renewalOverdue", String(days));
}

function AdminConsole() {
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [rows, setRows] = useState<AdminAccountRow[] | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const [grantFor, setGrantFor] = useState<AdminAccountRow | null>(null);
  const { confirmNode, promptText } = useConfirm();

  async function reload() {
    const [ov, acc] = await Promise.all([api.adminOverview(), api.adminAccounts()]);
    setOverview(ov);
    setRows(acc.accounts);
    setPlans(acc.plans);
  }
  useEffect(() => { reload().catch((err) => setMsg({ kind: "err", text: errText(err) })); }, []);

  async function changePlan(row: AdminAccountRow, plan: PlanId) {
    try {
      await api.adminSetPlan(row.id, plan);
      setMsg({ kind: "ok", text: t("ac.planChanged", plan) });
      await reload();
    } catch (err) { setMsg({ kind: "err", text: errText(err) }); }
  }

  async function markPaid(row: AdminAccountRow) {
    try {
      const result = await api.adminMarkPaid(row.id);
      // suma efectiv încasată, nu prețul de listă: la prima plată cu cod, cele
      // două diferă, iar factura o scriem manual după ce scrie aici
      setMsg({ kind: "ok", text: t("ac.markPaidCharged", (result.chargedMicroEur / 1_000_000).toFixed(2), row.email) });
      await reload();
    } catch (err) { setMsg({ kind: "err", text: errText(err) }); }
  }

  async function extendTrial(row: AdminAccountRow) {
    const value = await promptText({
      title: t("ac.extendTrialTitle", row.email),
      body: t("ac.extendTrialBody"),
      confirmLabel: t("ac.extendTrialBtn"),
      input: { label: t("ac.extendTrialLabel"), placeholder: "2026-08-15" },
    });
    if (!value) return;
    const trialEnd = dateInputToMs(value, true);
    if (trialEnd === undefined) { setMsg({ kind: "err", text: t("ac.extendTrialBadDate") }); return; }
    try {
      const result = await api.adminExtendTrial(row.id, trialEnd);
      setMsg({ kind: "ok", text: t("ac.extendTrialDone", row.email, fmtDate(result.trialEnd)) });
      await reload();
    } catch (err) { setMsg({ kind: "err", text: errText(err) }); }
  }

  const chart = (rows ?? [])
    .filter((r) => r.monthCostMicroUsd > 0)
    .sort((a, b) => b.monthCostMicroUsd - a.monthCostMicroUsd)
    .slice(0, 8);
  const chartMax = chart.reduce((m, r) => Math.max(m, r.monthCostMicroUsd), 0) || 1;

  return (
    <div class="content">
      {confirmNode}
      {msg && <Msg kind={msg.kind} text={msg.text} />}

      <div class="stats">
        <Stat label={t("ac.clients")} value={overview ? num(overview.totalAccounts) : "-"} dot="#818cf8" sub={`${overview?.totalApps ?? "-"} ${t("ac.sites").toLowerCase()}`} />
        <Stat label={t("ac.mrr")} value={overview ? `€${num(overview.mrrEur)}` : "-"} dot="#22c55e" />
        <Stat label={t("ac.responses")} value={overview ? num(overview.monthResponses) : "-"} dot="#a855f7" />
        <Stat label={t("ac.tokens")} value={overview ? num(overview.monthTokens) : "-"} dot="#f59e0b" />
        <Stat label={t("ac.aicost")} value={overview ? eur(overview.monthCostMicroUsd) : "-"} dot="#ef4444" />
        <Stat label={t("ac.margin")} value={overview ? eur(overview.monthMarginMicroUsd, 2) : "-"} dot="#12805c" />
      </div>

      <div class="card" style={{ marginTop: "20px" }}>
        <div class="card-head"><h2>{t("ac.chartTitle")}</h2></div>
        {chart.length === 0 && <p class="muted">{t("ac.chartEmpty")}</p>}
        {chart.map((r) => (
          <div class="chart-row" key={r.id}>
            <div class="cl" title={r.email}>{r.email}</div>
            <div class="ctrack"><div class="cfill" style={{ width: `${Math.max(3, (r.monthCostMicroUsd / chartMax) * 100)}%` }} /></div>
            <div class="cv">{eur(r.monthCostMicroUsd)}</div>
          </div>
        ))}
      </div>

      <div class="section-title">{t("ac.tableTitle")}</div>
      <div class="table-wrap">
        <table class="data">
          <thead>
            <tr>
              <th>{t("ac.col.client")}</th>
              <th>{t("ac.col.plan")}</th>
              <th class="num">{t("ac.col.scans")}</th>
              <th class="num">{t("ac.col.answers")}</th>
              <th class="num">{t("ac.col.cost")}</th>
              <th class="num">{t("ac.col.margin")}</th>
              <th>{t("ac.col.renewal")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows === null && <tr><td colSpan={8} class="spin">{t("common.loading")}</td></tr>}
            {rows?.map((r) => (
              <tr key={r.id}>
                <td>
                  <div class="em">{r.email} {r.isAdmin && <span class="badge plan" style={{ marginLeft: "4px" }}>admin</span>}</div>
                  <div class="em2">#{r.id}</div>
                  {r.appliedCode && (
                    <div class="em2" style={{ marginTop: "4px" }}>
                      <span class="badge plan" style={{ marginRight: "4px" }}>{r.appliedCode.code}</span>
                      {r.appliedCode.consumedAt === null ? (
                        <span class="badge warn">{t("ac.codePending")}</span>
                      ) : (
                        <span class="badge ok">{t("ac.codeConsumed")}</span>
                      )}
                      {!r.appliedCode.codeActive && <span class="badge muted" style={{ marginLeft: "4px" }}>{t("ac.codeInactive")}</span>}
                    </div>
                  )}
                </td>
                <td>
                  <select class="mini-select" value={r.plan} disabled={r.isAdmin} onChange={(e) => void changePlan(r, (e.target as HTMLSelectElement).value as PlanId)}>
                    {plans.map((p) => <option value={p.id} key={p.id}>{p.label}</option>)}
                  </select>
                  <span class="muted" style={{ marginLeft: "6px" }}>{num(r.sites)} {r.sites === 1 ? t("ac.siteOne") : t("ac.siteMany")}</span>
                  {r.extraSites > 0 && <span class="badge ok" style={{ marginLeft: "6px" }} title={t("ac.extraSitesHint")}>{t("ac.extraSitesTag", num(r.extraSites))}</span>}
                  {/* grantul e o stare invizibilă altfel: planul din select rămâne
                      cel plătit, deci fără badge nimeni n-ar ghici de ce contul are
                      capabilități peste el. */}
                  {r.grantedPlan && <span class="badge plan" style={{ marginLeft: "6px" }} title={t("ac.grantHint", r.planLabel)}>{t("ac.grantTag", plans.find((p) => p.id === r.grantedPlan)?.label ?? r.grantedPlan)}</span>}
                </td>
                <td class="num" title={t("ac.scansHint")}>{num(r.siteScans)} <span class="muted">/ {num(r.manualPageScans)}</span></td>
                <td class="num">{num(r.answersUsed)} <span class="muted">/ {num(r.answersLimit)}</span>{r.bonusAnswers > 0 && <span class="badge ok" style={{ marginLeft: "6px" }} title={t("ac.bonusHint")}>{t("ac.bonusTag", num(r.bonusAnswers))}</span>}</td>
                <td class="num" title={t("ac.costHint")}>{eur(r.scanCostMicroUsd, 2)} <span class="muted">/ {eur(r.messageCostMicroUsd, 2)}</span></td>
                <td class="num" style={{ color: r.monthMarginMicroUsd >= 0 ? "var(--ok)" : "var(--danger)", fontWeight: 700 }}>{eur(r.monthMarginMicroUsd, 2)}</td>
                <td>
                  {r.trialEndsAt && r.trialEndsAt > Date.now() ? (
                    <div>
                      <div><span class="badge plan pulse">{t("ac.trialBadge")}</span> {fmtDate(r.trialEndsAt)}</div>
                      <button class="btn ghost sm" style={{ marginTop: "4px" }} onClick={() => void extendTrial(r)}>{t("ac.extendTrial")}</button>
                    </div>
                  ) : r.renewsAt === null ? (
                    <span class="muted">{t("ac.renewalNever")}</span>
                  ) : (
                    <span style={isOverdue(r.renewsAt) ? { color: "var(--danger)", fontWeight: 700 } : undefined} title={renewalHint(r.renewsAt)}>
                      {fmtDate(r.renewsAt)}
                    </span>
                  )}
                </td>
                <td class="num">
                  {/* Discountul trebuie văzut ÎNAINTE de mark-paid: factura o
                      emitem manual, iar suma reală e cea de aici, nu prețul de listă. */}
                  {r.firstPaymentDiscount && (
                    <div class="badge warn" style={{ marginBottom: "6px" }} title={t(
                      "ac.discountHint",
                      r.firstPaymentDiscount.code,
                      ((r.priceEur * (100 - r.firstPaymentDiscount.percent)) / 100).toFixed(2),
                      String(r.priceEur),
                      String(r.firstPaymentDiscount.percent),
                    )}>
                      −{r.firstPaymentDiscount.percent}% → €{((r.priceEur * (100 - r.firstPaymentDiscount.percent)) / 100).toFixed(2)}
                    </div>
                  )}
                  <button class="btn ghost sm" onClick={() => setGrantFor(r)}>{t("ac.addons")}</button>{" "}
                  <button class="btn ghost sm" onClick={() => void markPaid(r)}>{t("ac.markPaid")}</button>{" "}
                  <button class="btn ghost sm" onClick={() => void enterImpersonation(r.id)}>{t("ac.impersonate")}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {grantFor && (
        <AddonsModal row={grantFor} plans={plans} onClose={() => setGrantFor(null)} onDone={() => { setGrantFor(null); setMsg({ kind: "ok", text: t("ac.addonsDone") }); void reload(); }} />
      )}
    </div>
  );
}

/**
 * Coduri promo/partner + comisioanele datorate. Codurile de referral NU se
 * creează de aici - și le generează clienții singuri din „Refer & earn"; aici
 * apar doar în listă, odată folosite.
 */
function AdminCodesView() {
  const [codes, setCodes] = useState<AdminCodeRow[] | null>(null);
  const [earnings, setEarnings] = useState<AdminEarningRow[] | null>(null);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const [code, setCode] = useState("");
  const [type, setType] = useState<"promo" | "partner">("promo");
  const [benefitType, setBenefitType] = useState<BenefitType>("percent_first_payment");
  const [benefitValue, setBenefitValue] = useState("20");
  const [commission, setCommission] = useState("");
  const [owner, setOwner] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [expires, setExpires] = useState("");
  const { confirmNode, promptText } = useConfirm();

  async function reload() {
    const [c, e] = await Promise.all([api.adminCodes(), api.adminEarnings()]);
    setCodes(c.codes);
    setEarnings(e.earnings);
  }
  useEffect(() => { reload().catch((err) => setMsg({ kind: "err", text: errText(err) })); }, []);

  async function create(e: Event) {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      const created = await api.adminCreateCode({
        code: code.trim(),
        type,
        benefitType,
        benefitValue: Number(benefitValue),
        commissionPercent: commission ? Number(commission) : undefined,
        maxUses: maxUses ? Number(maxUses) : null,
        // input[type=date] dă ziua; expirarea e la finalul ei, nu la 00:00
        expiresAt: expires ? dateInputToMs(expires, true) ?? null : null,
        ownerAccountId: owner ? Number(owner) : null,
      });
      setMsg({ kind: "ok", text: t("cd.created", created.code) });
      setCode("");
      setOwner("");
      setMaxUses("");
      setExpires("");
      setCommission("");
      await reload();
    } catch (err) {
      setMsg({ kind: "err", text: errText(err) });
    } finally {
      setBusy(false);
    }
  }

  async function deactivate(row: AdminCodeRow) {
    try {
      await api.adminDeactivateCode(row.id);
      setMsg({ kind: "ok", text: t("cd.deactivated", row.code) });
      await reload();
    } catch (err) { setMsg({ kind: "err", text: errText(err) }); }
  }

  async function cancel(row: AdminEarningRow) {
    // Peste hold, banii au fost deja promiși referrer-ului - de aceea avertismentul
    // roșu și tonul „danger" (backend-ul cere oricum `force`, altfel 409). Motivul
    // și avertismentul stau în ACELAȘI dialog: nativ erau două popupuri înlănțuite,
    // iar al doilea apărea abia după ce scriseseși motivul.
    const force = row.status === "available";
    const reason = await promptText({
      title: t("cd.cancelTitle"),
      body: t("cd.cancelBody", row.code),
      warn: force ? t("cd.cancelForce") : undefined,
      tone: force ? "danger" : "primary",
      confirmLabel: t("cd.cancelBtn"),
      input: { label: t("cd.cancelPrompt"), placeholder: t("cd.cancelPlaceholder") },
    });
    if (!reason) return;
    try {
      await api.adminCancelEarning(row.id, reason, row.status === "available");
      setMsg({ kind: "ok", text: t("cd.cancelled") });
      await reload();
    } catch (err) { setMsg({ kind: "err", text: errText(err) }); }
  }

  // Etichete pentru select-ul din formular…
  const benefitLabel: Record<BenefitType, string> = {
    percent_first_payment: t("cd.benefit.percent"),
    credit_eur: t("cd.benefit.credit"),
    trial_days: t("cd.benefit.trialDays"),
  };
  // …și pentru tabel, unde valoarea afișată își poartă deja unitatea („20%"),
  // deci eticheta n-o mai repetă.
  const benefitValueText = (c: AdminCodeRow): string =>
    c.benefitType === "percent_first_payment" ? `${c.benefitValue}%` : c.benefitType === "credit_eur" ? `€${c.benefitValue}` : `${c.benefitValue}`;

  return (
    <div class="content">
      {confirmNode}
      {msg && <Msg kind={msg.kind} text={msg.text} />}

      <div class="card">
        <div class="card-head"><div><h2>{t("cd.newTitle")}</h2><p>{t("cd.sub")}</p></div></div>
        <form onSubmit={create} class="row" style={{ alignItems: "flex-end", gap: "12px", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 140px" }}>
            <label style={{ marginTop: 0 }}>{t("cd.code")}</label>
            <input value={code} maxLength={24} required placeholder="LAUNCH50" style={{ textTransform: "uppercase" }}
              onInput={(e) => setCode((e.target as HTMLInputElement).value.toUpperCase())} />
          </div>
          <div style={{ flex: "0 1 190px" }}>
            <label style={{ marginTop: 0 }}>{t("cd.type")}</label>
            <select value={type} onChange={(e) => setType((e.target as HTMLSelectElement).value as "promo" | "partner")}>
              <option value="promo">{t("cd.type.promo")}</option>
              <option value="partner">{t("cd.type.partner")}</option>
            </select>
          </div>
          <div style={{ flex: "0 1 190px" }}>
            <label style={{ marginTop: 0 }}>{t("cd.benefit")}</label>
            <select value={benefitType} onChange={(e) => setBenefitType((e.target as HTMLSelectElement).value as BenefitType)}>
              {(Object.keys(benefitLabel) as BenefitType[]).map((b) => (
                <option value={b} key={b}>{benefitLabel[b]}</option>
              ))}
            </select>
          </div>
          <div style={{ flex: "0 1 90px" }}>
            <label style={{ marginTop: 0 }}>{t("cd.value")} <span class="muted">({t(`cd.short.${benefitType}` as const)})</span></label>
            <input type="number" min={1} max={benefitType === "trial_days" ? 365 : undefined} value={benefitValue} required
              onInput={(e) => setBenefitValue((e.target as HTMLInputElement).value)} />
          </div>
          <div style={{ flex: "0 1 110px" }}>
            <label style={{ marginTop: 0 }}>{t("cd.commission")}</label>
            <input type="number" min={0} max={100} value={commission} placeholder="0"
              onInput={(e) => setCommission((e.target as HTMLInputElement).value)} />
          </div>
          <div style={{ flex: "0 1 120px" }}>
            <label style={{ marginTop: 0 }} title={t("cd.ownerHint")}>{t("cd.owner")}</label>
            <input type="number" min={1} value={owner} placeholder="-"
              onInput={(e) => setOwner((e.target as HTMLInputElement).value)} />
          </div>
          <div style={{ flex: "0 1 110px" }}>
            <label style={{ marginTop: 0 }} title={t("cd.maxUsesHint")}>{t("cd.maxUses")}</label>
            <input type="number" min={1} value={maxUses} placeholder="∞"
              onInput={(e) => setMaxUses((e.target as HTMLInputElement).value)} />
          </div>
          <div style={{ flex: "0 1 150px" }}>
            <label style={{ marginTop: 0 }}>{t("cd.expires")}</label>
            <input type="date" value={expires} onInput={(e) => setExpires((e.target as HTMLInputElement).value)} />
          </div>
          <button class="btn primary" disabled={busy} type="submit">{t("cd.create")}</button>
        </form>
      </div>

      <div class="section-title">{t("cd.codesTitle")}</div>
      <div class="table-wrap">
        <table class="data">
          <thead>
            <tr>
              <th>{t("cd.code")}</th>
              <th>{t("cd.type")}</th>
              <th>{t("cd.benefit")}</th>
              <th class="num">{t("cd.commission")}</th>
              <th>{t("cd.col.owner")}</th>
              <th class="num">{t("cd.col.uses")}</th>
              <th>{t("cd.col.status")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {codes === null && <tr><td colSpan={8} class="spin">{t("common.loading")}</td></tr>}
            {codes?.length === 0 && <tr><td colSpan={8}><p class="muted">{t("cd.codesEmpty")}</p></td></tr>}
            {codes?.map((c) => (
              <tr key={c.id}>
                <td><div class="em">{c.code}</div>{c.expiresAt && <div class="em2">{fmtDate(c.expiresAt)}</div>}</td>
                <td><span class="badge plan">{c.type}</span></td>
                <td>{benefitValueText(c)} <span class="muted">{t(`cd.short.${c.benefitType}` as const)}</span></td>
                <td class="num">{c.commissionPercent ? `${c.commissionPercent}%` : "-"}</td>
                <td>{c.ownerEmail ?? <span class="muted">-</span>}</td>
                <td class="num">{num(c.currentUses)} <span class="muted">/ {c.maxUses === null ? t("cd.unlimited") : num(c.maxUses)}</span></td>
                <td><span class={`badge ${c.active ? "ok" : "muted"}`}>{c.active ? t("cd.active") : t("cd.inactive")}</span></td>
                <td class="num">
                  {c.active && <button class="btn ghost sm" onClick={() => void deactivate(c)}>{t("cd.deactivate")}</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div class="section-title">{t("cd.earningsTitle")}</div>
      <div class="table-wrap">
        <table class="data">
          <thead>
            <tr>
              <th>{t("cd.col.referrer")}</th>
              <th>{t("cd.code")}</th>
              <th>{t("cd.col.referred")}</th>
              <th class="num">{t("cd.col.amount")}</th>
              <th>{t("cd.col.available")}</th>
              <th>{t("cd.col.status")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {earnings === null && <tr><td colSpan={7} class="spin">{t("common.loading")}</td></tr>}
            {earnings?.length === 0 && <tr><td colSpan={7}><p class="muted">{t("cd.earningsEmpty")}</p></td></tr>}
            {earnings?.map((e) => (
              <tr key={e.id}>
                <td><div class="em">{e.ownerEmail ?? "-"}</div><div class="em2">#{e.ownerAccountId}</div></td>
                <td>{e.code}</td>
                <td class="muted">#{e.referredAccountId}</td>
                <td class="num" style={{ fontWeight: 700 }}>{eur(e.amountMicroEur, 2)}</td>
                <td>{fmtDate(e.availableAt)}</td>
                <td>
                  {e.status === "available" ? (
                    <span class="badge ok">{t("ref.status.available")}</span>
                  ) : e.status === "cancelled" ? (
                    <span class="badge muted" title={e.cancelledReason ?? undefined}>{t("ref.status.cancelled")}</span>
                  ) : (
                    <span class="badge warn">{t("ref.pending")}</span>
                  )}
                </td>
                <td class="num">
                  {e.status !== "cancelled" && <button class="btn ghost sm" onClick={() => void cancel(e)}>{t("cd.cancel")}</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value, dot, sub }: { label: string; value: string; dot: string; sub?: string }) {
  return (
    <div class="stat">
      <div class="l"><span class="dot" style={{ background: dot }} /> {label}</div>
      <div class="v">{value}</div>
      {sub && <div class="d">{sub}</div>}
    </div>
  );
}

function AddonsModal({ row, plans, onClose, onDone }: { row: AdminAccountRow; plans: Plan[]; onClose: () => void; onDone: () => void }) {
  // add-on-uri activabile manual (ulterior prin Stripe). „More messages" e un pool
  // one-time consumabil; „extra sites" e un boost persistent al limitei de site-uri.
  const [messages, setMessages] = useState("500");
  const [sites, setSites] = useState("0");
  // Grantul de plan: deschide capabilitățile ȘI volumele unui plan superior fără
  // să atingă planul plătit. "" = fără grant (revocare, dacă exista unul).
  const [granted, setGranted] = useState<string>(row.grantedPlan ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const grantChanged = (row.grantedPlan ?? "") !== granted;
  async function apply(e: Event) {
    e.preventDefault();
    const bonusAnswers = Math.trunc(Number(messages)) || 0;
    const extraSites = Math.trunc(Number(sites)) || 0;
    if (bonusAnswers === 0 && extraSites === 0 && !grantChanged) return setErr(t("ac.addonsEmpty"));
    setBusy(true); setErr(null);
    try {
      if (grantChanged) {
        const res = await api.adminSetGrantedPlan(row.id, (granted || null) as PlanId | null);
        // Revocarea nu e blocată, dar nu are voie să treacă tăcut: dacă lasă
        // clientul peste limite, adminul află ACUM, nu din reclamația lui.
        if (res.overLimit.sites > 0 || res.overLimit.answers > 0) {
          setBusy(false);
          return setErr(t("ac.grantOverLimit", num(res.overLimit.sites), num(res.overLimit.answers)));
        }
      }
      if (bonusAnswers !== 0 || extraSites !== 0) {
        await api.adminGrantAddons(row.id, { bonusAnswers, extraSites });
      }
      onDone();
    } catch (e2) { setErr(errText(e2)); setBusy(false); }
  }
  return (
    <Modal onClose={onClose}>
      <h3>{t("ac.addonsTitle", row.email)}</h3>
      <p class="muted">{t("ac.addonsCurrent", num(row.bonusAnswers), num(row.extraSites))}</p>
      <form onSubmit={apply}>
        <label>{t("ac.grantPlan")}</label>
        <select value={granted} onChange={(e) => setGranted((e.target as HTMLSelectElement).value)}>
          <option value="">{t("ac.grantNone")}</option>
          {plans.filter((p) => p.id !== "free").map((p) => <option value={p.id} key={p.id}>{p.label}</option>)}
        </select>
        <p class="muted" style={{ fontSize: "12.5px", marginTop: "4px" }}>{t("ac.grantHint", row.planLabel)}</p>
        <label>{t("ac.addonsMessages")}</label>
        <input type="number" step={100} value={messages} onInput={(e) => setMessages((e.target as HTMLInputElement).value)} />
        <label>{t("ac.addonsSites")}</label>
        <input type="number" step={1} value={sites} onInput={(e) => setSites((e.target as HTMLInputElement).value)} />
        {err && <Msg kind="err" text={err} />}
        <div class="actions">
          <button class="btn ghost" type="button" onClick={onClose}>{t("common.cancel")}</button>
          <button class="btn primary" disabled={busy} type="submit">{t("ac.addonsApply")}</button>
        </div>
      </form>
    </Modal>
  );
}

// ---------- tickete de suport (consola de admin) ----------

function fmtDateTime(ms: number): string {
  return new Date(ms).toLocaleString();
}

const TICKET_STATUSES = ["open", "pending", "closed"] as const;
type TicketStatus = (typeof TICKET_STATUSES)[number];

function statusBadgeClass(status: string): string {
  return status === "open" ? "plan" : status === "pending" ? "warn" : "muted";
}

/** yyyy-mm-dd local, pentru valoarea implicită din input[type=date] (o lună în urmă). */
function isoDateLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function defaultTicketFrom(): string {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return isoDateLocal(d);
}
const DEFAULT_TICKET_STATUSES: TicketStatus[] = ["open", "pending"];

/** Durata de la deschidere: până la închidere dacă e closedAt, altfel până acum. */
function ticketDuration(row: Ticket): string {
  const end = row.closedAt ?? Date.now();
  return fmtDuration(end - row.createdAt);
}

function TicketsView({ reloadMe }: { reloadMe: () => void }) {
  const [tickets, setTickets] = useState<Ticket[] | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<TicketDetail | null>(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  // Filtre rapide pe status (toggle, multi-select) + interval pe ultima
  // activitate. Implicit: Open + Pending, ultima lună până acum. `to` gol
  // înseamnă fără limită superioară - vezi dateInputToMs mai jos: altfel un
  // "acum" fixat la randare ar rata tichetele apărute chiar în minutul curent.
  const [statusFilter, setStatusFilter] = useState<Set<TicketStatus>>(new Set(DEFAULT_TICKET_STATUSES));
  const [from, setFrom] = useState(defaultTicketFrom());
  const [to, setTo] = useState("");

  function toggleStatusFilter(s: TicketStatus) {
    setStatusFilter((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s); else next.add(s);
      return next;
    });
  }

  const isDefaultFilter =
    statusFilter.size === DEFAULT_TICKET_STATUSES.length &&
    DEFAULT_TICKET_STATUSES.every((s) => statusFilter.has(s)) &&
    from === defaultTicketFrom() &&
    !to;

  async function reloadList() {
    // set gol = adminul a scos toate statusurile din filtru → nimic de arătat;
    // nu are sens un request pentru un rezultat cunoscut dinainte.
    if (statusFilter.size === 0) { setTickets([]); return; }
    const { tickets: rows } = await api.adminTickets({
      statuses: [...statusFilter],
      from: dateInputToMs(from, false),
      to: dateInputToMs(to, true),
    });
    setTickets(rows);
  }
  useEffect(() => {
    reloadList().catch((err) => setMsg({ kind: "err", text: errText(err) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, from, to]);

  async function toggleTicket(id: number) {
    // acordeon: al doilea click pe același rând închide expandarea
    if (selectedId === id) { setSelectedId(null); setDetail(null); return; }
    setSelectedId(id);
    setDetail(null);
    setReply("");
    try {
      const { ticket } = await api.adminTicket(id);
      setDetail(ticket);
    } catch (err) { setMsg({ kind: "err", text: errText(err) }); }
  }

  async function refreshOpen(id: number) {
    const [{ ticket }] = await Promise.all([api.adminTicket(id), reloadList()]);
    setDetail(ticket);
    reloadMe(); // actualizează badge-ul de tickete deschise din navigație
  }

  async function sendReply(e: Event) {
    e.preventDefault();
    if (!selectedId || !reply.trim()) return;
    setBusy(true);
    try {
      await api.adminReplyTicket(selectedId, reply.trim());
      setReply("");
      await refreshOpen(selectedId);
    } catch (err) { setMsg({ kind: "err", text: errText(err) }); }
    setBusy(false);
  }

  async function changeStatus(id: number, status: TicketStatus) {
    try {
      await api.adminSetTicketStatus(id, status);
      await refreshOpen(id);
    } catch (err) { setMsg({ kind: "err", text: errText(err) }); }
  }

  const COLS = 6;
  return (
    <div class="content">
      {msg && <Msg kind={msg.kind} text={msg.text} />}
      <div class="row conv-filters">
        <div class="status-filter-chips">
          {TICKET_STATUSES.map((s) => (
            <button
              type="button"
              key={s}
              class={`badge ${statusBadgeClass(s)} status-filter-chip${statusFilter.has(s) ? " active" : ""}`}
              onClick={() => toggleStatusFilter(s)}
            >
              {t(`tk.status.${s}` as never)}
            </button>
          ))}
        </div>
        <label>{t("tk.filterFrom")} <input type="date" value={from} onInput={(e) => setFrom((e.target as HTMLInputElement).value)} /></label>
        <label>{t("tk.filterTo")} <input type="date" value={to} onInput={(e) => setTo((e.target as HTMLInputElement).value)} placeholder={t("tk.filterToPlaceholder")} /></label>
        {!isDefaultFilter && (
          <button
            class="btn subtle sm"
            onClick={() => { setStatusFilter(new Set(DEFAULT_TICKET_STATUSES)); setFrom(defaultTicketFrom()); setTo(""); }}
          >
            {t("tk.filterClear")}
          </button>
        )}
      </div>
      <div class="table-wrap">
        <table class="data tickets-table">
          <thead>
            <tr>
              <th class="tk-num">{t("tk.col.num")}</th>
              <th>{t("tk.col.from")}</th>
              <th>{t("tk.col.subject")}</th>
              <th>{t("tk.col.status")}</th>
              <th>{t("tk.col.last")}</th>
              <th class="num">{t("tk.col.duration")}</th>
            </tr>
          </thead>
          <tbody>
            {tickets === null && <tr><td colSpan={COLS} class="spin">{t("common.loading")}</td></tr>}
            {tickets?.length === 0 && (
              <tr><td colSpan={COLS}><EmptyMini icon={<IconTicket />} text={t(isDefaultFilter ? "tk.empty" : "tk.emptyFiltered")} /></td></tr>
            )}
            {tickets?.map((row) => (
              <>
                <tr key={row.id} class={row.id === selectedId ? "active" : ""} onClick={() => void toggleTicket(row.id)} style={{ cursor: "pointer" }}>
                  <td class="tk-num">#{row.id}</td>
                  <td>{row.fromEmail}</td>
                  <td>
                    {row.source === "feedback" && <span class="badge info tk-src">{t("tk.source.feedback")}</span>}
                    {/* prefixul rămâne în subiect pentru emailurile de răspuns; aici îl spune eticheta */}
                    {row.source === "feedback" ? row.subject.replace(/^Feedback: /, "") : row.subject}
                  </td>
                  <td><span class={`badge ${statusBadgeClass(row.status)}`}>{t(`tk.status.${row.status}` as never)}</span></td>
                  <td class="muted">{fmtDateTime(row.lastMessageAt)}</td>
                  <td class="num muted">{ticketDuration(row)}</td>
                </tr>
                {row.id === selectedId && (
                  <tr class="ticket-detail-row" key={`${row.id}-detail`}>
                    <td colSpan={COLS}>
                      {!detail ? (
                        <Skeleton h="140px" style={{ borderRadius: "12px" }} />
                      ) : (
                        <div class="ticket-detail">
                          <div class="ticket-detail-head">
                            <span class="muted">
                              {t("tk.ticketNo", String(detail.id))}
                              {detail.context && ` · ${detail.context.plan} · ${t(detail.context.kind === "voice" ? "tk.fb.voice" : "tk.fb.text")} · ${detail.context.screen}`}
                            </span>
                            <label class="ticket-status-select">
                              {t("tk.col.status")}:
                              <select class="mini-select" value={detail.status} onChange={(e) => void changeStatus(detail.id, (e.target as HTMLSelectElement).value as TicketStatus)}>
                                {TICKET_STATUSES.map((s) => <option value={s} key={s}>{t(`tk.status.${s}` as never)}</option>)}
                              </select>
                            </label>
                          </div>
                          <div class="ticket-thread">
                            {detail.messages.map((m) => (
                              <div key={m.id} class={`ticket-msg ${m.direction}`}>
                                <div class="ticket-msg-head">
                                  <strong>{m.fromEmail}</strong>
                                  <span class="muted">{fmtDateTime(m.createdAt)}</span>
                                </div>
                                <div class="ticket-msg-body">{m.body}</div>
                              </div>
                            ))}
                          </div>
                          <form onSubmit={sendReply}>
                            <textarea
                              rows={4}
                              placeholder={t("tk.replyPlaceholder")}
                              value={reply}
                              onInput={(e) => setReply((e.target as HTMLTextAreaElement).value)}
                            />
                            <div class="actions">
                              <button class="btn primary" disabled={busy || !reply.trim()} type="submit">{t("tk.send")}</button>
                            </div>
                          </form>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================================================
//  SHELL + ROOT
// ============================================================

// ---------- activity (audit log) - INTERN, doar super-admin ----------
//
// Deliberat fără versiune pentru client. Un cont = o singură identitate (fără
// echipe/seats - vezi db/schema.ts), deci „cine a făcut ce pe contul tău" se
// reduce la „ce ai făcut tu", ceea ce clientul știe deja. Valoarea reală e la
// noi: să putem răspunde la ticketul „nu îmi merge" și la „ce s-a întâmplat cu
// contul lui X". Vezi docs/plan-audit.md → grupul 3c.

/** Acțiunile de filtrat. Le ținem aici, nu în audit.ts: e doar ordinea din UI. */
const AUDIT_ACTIONS = [
  "auth.login",
  "auth.login_failed",
  "auth.register",
  "auth.password_reset_completed",
  "app.create",
  "app.update",
  "app.delete",
  "config.publish",
  "config.rollback",
  "builder.session_started",
  "plan.change",
  "billing.mark_paid",
  "credit.add",
  "addons.grant",
  "account.delete",
] as const;

/** Eticheta acțiunii; cade pe numele brut dacă lipsește traducerea (acțiuni vechi). */
function actionLabel(action: string): string {
  const key = `act.a.${action}`;
  const label = t(key as never);
  return label === key ? action : label;
}

/**
 * Cine a făcut acțiunea, în cuvinte. Distincția care contează: `admin` =
 * cineva de la NOI a atins contul clientului. Se evidențiază - la un ticket,
 * prima întrebare e „a schimbat clientul ceva, sau am schimbat noi?".
 */
function actorLabel(entry: AuditEntry): { text: string; highlight: boolean } {
  switch (entry.actorType) {
    case "account":
      return { text: t("act.byClient"), highlight: false };
    case "admin":
      return { text: t("act.byUs"), highlight: true };
    case "system":
      return { text: t("act.bySystem"), highlight: false };
    case "anonymous":
      return { text: t("act.byAnon"), highlight: false };
    default:
      return { text: t("act.byUnknown"), highlight: false };
  }
}

/**
 * `meta` în text citibil. Deliberat conservator: perechi cheie→valoare, nu
 * JSON brut. Ce nu știm să formatăm, nu inventăm - omitem.
 */
function metaSummary(entry: AuditEntry): string | null {
  const m = entry.meta;
  // rândurile de dinainte de 0022 n-au meta, doar text liber
  if (!m) return entry.detail;
  const parts: string[] = [];
  const push = (v: unknown) => {
    if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") parts.push(String(v));
  };
  if (typeof m.name === "string") push(m.name);
  if (typeof m.version === "string") push(m.version);
  if (m.from !== undefined && m.to !== undefined && (typeof m.from !== "object" || m.from === null)) {
    parts.push(`${m.from ?? "-"} → ${m.to ?? "-"}`);
  }
  if (typeof m.method === "string") push(m.method);
  if (typeof m.reason === "string") push(m.reason);
  if (typeof m.amountMicroUsd === "number") parts.push(`€${(m.amountMicroUsd / 1_000_000).toFixed(2)}`);
  if (m.changes && typeof m.changes === "object") {
    // `enabled` e kill switch-ul, `live` comută publicarea, `allowedOrigins`
    // decide cine consumă cota (S-15) - doar numele câmpului n-ar spune dacă
    // site-ul a fost pornit sau OPRIT, adică exact ce vrei să afli.
    for (const [field, change] of Object.entries(m.changes as Record<string, unknown>)) {
      const c = change as { from?: unknown; to?: unknown };
      if (typeof c?.to === "boolean") parts.push(`${field}: ${c.from} → ${c.to}`);
      else if (Array.isArray(c?.to)) parts.push(`${field}: ${(c.to as unknown[]).length}`);
      else parts.push(field);
    }
  }
  return parts.length > 0 ? parts.join(" · ") : null;
}

function ActivityRow({ entry, onPickEmail }: { entry: AuditEntry; onPickEmail?: (email: string) => void }) {
  const actor = actorLabel(entry);
  const meta = metaSummary(entry);
  // contul purjat (sau evenimentul fără cont) nu mai are email - arătăm id-ul,
  // ca rândul să rămână urmăribil fără să reinventăm PII-ul șters
  const who = entry.accountEmail ?? (entry.accountId !== null ? `#${entry.accountId}` : null);
  return (
    <div class="act-row">
      <div class="act-main">
        {who &&
          (entry.accountEmail && onPickEmail ? (
            // click pe cont → filtrează feedul pe el: drumul cel mai scurt de la
            // „văd ceva ciudat" la „ce s-a mai întâmplat pe contul ăsta?"
            <button class="linkbtn act-acct" onClick={() => onPickEmail(entry.accountEmail!)} title={t("act.filterByThis")}>
              {who}
            </button>
          ) : (
            <span class="act-acct">{who}</span>
          ))}
        <span class="act-action">{actionLabel(entry.action)}</span>
        <span class={actor.highlight ? "badge warn" : "act-actor"}>{actor.text}</span>
        {meta && <span class="act-meta">{meta}</span>}
      </div>
      <div class="act-side">
        <span class="act-time">{fmtDateTime(entry.createdAt)}</span>
        {entry.ip && <span class="act-ip">{t("act.ip", entry.ip)}</span>}
      </div>
    </div>
  );
}

/** Vederea internă de audit (super-admin). Fără gating de plan: nu e un feature. */
function AdminAuditView() {
  const [entries, setEntries] = useState<AuditEntry[] | null>(null);
  const [action, setAction] = useState("");
  // `email` e ce se caută; `emailInput` e ce se tastează - altfel am cere la
  // fiecare literă. Se aplică pe Enter sau pe blur.
  const [emailInput, setEmailInput] = useState("");
  const [email, setEmail] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [done, setDone] = useState(false);

  const query = {
    action: action || undefined,
    email: email || undefined,
    from: dateInputToMs(from, false),
    to: dateInputToMs(to, true),
  };

  useEffect(() => {
    setEntries(null);
    setDone(false);
    api
      .adminAudit({ ...query, limit: 50 })
      .then((r) => {
        setEntries(r.entries);
        setDone(r.entries.length < 50);
      })
      .catch(() => setEntries([]));
  }, [action, email, from, to]);

  async function loadMore() {
    if (!entries || entries.length === 0) return;
    // keyset: continuăm de la ultimul id primit (vezi backend services/audit.ts)
    const r = await api.adminAudit({ ...query, before: entries[entries.length - 1].id, limit: 50 });
    setEntries([...entries, ...r.entries]);
    if (r.entries.length < 50) setDone(true);
  }

  function filterByEmail(value: string) {
    setEmailInput(value);
    setEmail(value);
  }

  const filtered = action || email || from || to;

  // Fără card-head: titlul + subtitlul vin din topbar (TITLES.activity), ca la
  // celelalte vederi de nivel înalt - altfel ar apărea de două ori pe ecran.
  return (
    <div class="content">
      <div class="card">
        <div class="row conv-filters">
          <label>
            {t("act.filterAccount")}{" "}
            <input
              type="search"
              placeholder={t("act.filterAccountPlaceholder")}
              value={emailInput}
              onInput={(e) => setEmailInput((e.target as HTMLInputElement).value)}
              onBlur={() => setEmail(emailInput.trim())}
              onKeyDown={(e) => {
                if (e.key === "Enter") setEmail(emailInput.trim());
              }}
            />
          </label>
          <label>
            {t("act.filterAction")}{" "}
            <select value={action} onChange={(e) => setAction((e.target as HTMLSelectElement).value)}>
              <option value="">{t("act.filterAll")}</option>
              {AUDIT_ACTIONS.map((a) => <option key={a} value={a}>{actionLabel(a)}</option>)}
            </select>
          </label>
          <label>{t("act.filterFrom")} <input type="date" value={from} onInput={(e) => setFrom((e.target as HTMLInputElement).value)} /></label>
          <label>{t("act.filterTo")} <input type="date" value={to} onInput={(e) => setTo((e.target as HTMLInputElement).value)} /></label>
          {filtered && (
            <button
              class="btn subtle sm"
              onClick={() => { setAction(""); setEmailInput(""); setEmail(""); setFrom(""); setTo(""); }}
            >
              {t("act.filterClear")}
            </button>
          )}
        </div>

        {entries === null && (
          <>
            <Skeleton h="44px" style={{ borderRadius: "10px", marginBottom: "8px" }} />
            <Skeleton h="44px" style={{ borderRadius: "10px", marginBottom: "8px" }} />
            <Skeleton h="44px" style={{ borderRadius: "10px" }} />
          </>
        )}
        {entries && entries.length === 0 && <EmptyMini icon={<IconShield />} text={t("act.empty")} />}
        {entries && entries.length > 0 && (
          <>
            <div class="act-list">
              {entries.map((e) => <ActivityRow key={e.id} entry={e} onPickEmail={filterByEmail} />)}
            </div>
            {!done && <button class="btn subtle sm" onClick={() => void loadMore()}>{t("act.more")}</button>}
          </>
        )}
      </div>
    </div>
  );
}

// ==================== Content Hub (News / Research / Blog), etapa E2 ====================

const CONTENT_STATUS_TONE: Record<ContentStatus, string> = {
  draft: "muted",
  in_review: "warn",
  scheduled: "plan",
  published: "ok",
  archived: "muted",
};

/** Cheia de i18n pentru un status - o funcție, ca statusurile să nu ceară string literal la fiecare apel. */
function statusLabel(status: ContentStatus): string {
  return t(`content.status.${status}` as never);
}

/** Lista unei secțiuni: tabel cu status, titlu, dată; „New item" în topbar-ul propriu. */
function ContentListView({ kind }: { kind: ContentKind }) {
  const [items, setItems] = useState<ContentItem[] | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const { confirmNode, confirm } = useConfirm();

  async function reload() {
    const res = await api.contentList(kind);
    setItems(res.items);
  }
  useEffect(() => { setItems(null); reload().catch((err) => setMsg(errText(err))); }, [kind]);

  // Ștergere fizică - DOAR pentru drafturi (backendul refuză orice altceva).
  // Cea mai rapidă cale de curățat orfanii lăsați de bug-ul „New item" vechi,
  // fără să deschizi fiecare articol în editor.
  async function del(it: ContentItem) {
    const ok = await confirm({
      title: t("content.delete"),
      body: t("content.delete.confirm", it.title || t("content.untitled")),
      tone: "danger",
      confirmLabel: t("content.delete"),
    });
    if (!ok) return;
    try {
      await api.contentDelete(it.id);
      await reload();
    } catch (err) {
      setMsg(errText(err));
    }
  }

  return (
    <div class="content">
      <div class="content-list-head">
        {/* Doar navigație - NU creează nimic pe server. Articolul ia ființă la
            primul Save/Generate din editor (vezi ContentEditorView), altfel
            fiecare click aici lăsa un draft „untitled" orfan în bază. */}
        <button class="btn primary" onClick={() => navigate(`#/content/${kind}/new`)}>{t("content.new")}</button>
      </div>
      {msg && <div class="msg err">{msg}</div>}
      {!items && <Skeleton h="220px" style={{ borderRadius: "16px" }} />}
      {items && items.length === 0 && (
        <EmptyMini icon={<IconDoc />} text={t("content.empty")} />
      )}
      {items && items.length > 0 && (
        <div class="card table-wrap">
          <table class="data content-table">
            <thead>
              <tr>
                <th>{t("content.col.title")}</th>
                <th>{t("content.col.status")}</th>
                <th>{t("content.col.seo")}</th>
                <th>{t("content.col.updated")}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} class="content-row" onClick={() => navigate(`#/content/${kind}/${it.id}`)}>
                  <td>
                    <div class="content-row-title">{it.title || t("content.untitled")}</div>
                    <div class="content-row-slug">/{kind}/{it.slug}</div>
                  </td>
                  <td><span class={`badge ${CONTENT_STATUS_TONE[it.status]}`}>{statusLabel(it.status)}</span></td>
                  <td><SeoDots item={it} /></td>
                  <td class="muted">{fmtDateTime(it.updatedAt)}</td>
                  <td class="content-row-actions">
                    {/* Doar draft: orice altceva se retrage cu Archive, din editor. */}
                    {it.status === "draft" && (
                      <button class="linkbtn danger" onClick={(e) => { e.stopPropagation(); void del(it); }}>{t("content.delete")}</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {confirmNode}
    </div>
  );
}

/** Punctele SEO din listă: verde/galben pe patru semnale, fără text - detaliul e în editor. */
function SeoDots({ item }: { item: ContentItem }) {
  const s = item.seo;
  const checks: Array<{ ok: boolean; label: string }> = [
    { ok: s.metaDescriptionLength >= 120 && s.metaDescriptionLength <= 160, label: t("content.seo.meta") },
    { ok: s.slugValid, label: t("content.seo.slug") },
    { ok: s.hasHero, label: t("content.seo.hero") },
    { ok: !s.thin, label: t("content.seo.thin") },
  ];
  return (
    <span class="seo-dots">
      {checks.map((c, i) => <span key={i} class={`seo-dot ${c.ok ? "ok" : "warn"}`} title={c.label} />)}
    </span>
  );
}

/** Editorul: workbench Markdown | Preview + inspector (metadata/SEO + review + revizii). */
function ContentEditorView({ kind, id }: { kind: ContentKind; id: number | "new" }) {
  const [item, setItem] = useState<ContentItem | null>(null);
  const [previewToken, setPreviewToken] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const [mode, setMode] = useState<"write" | "preview">("write");
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "err">("idle");
  const [busy, setBusy] = useState(false);
  const { confirmNode, confirm } = useConfirm();

  // Câmpuri editabile, oglindite local ca inputurile să fie controlate.
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [dek, setDek] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [bodyMd, setBodyMd] = useState("");
  const [heroUrl, setHeroUrl] = useState("");
  const [heroAlt, setHeroAlt] = useState("");
  const [heroImagePrompt, setHeroImagePrompt] = useState<string | null>(null);
  const [tags, setTags] = useState<string[]>([]);
  const [authorName, setAuthorName] = useState("");

  // Cardul hero (E3 - imagine AI): stare separată de aiBusy/aiFresh, ca
  // regenerarea imaginii să nu blocheze sau să fie blocată de brief-ul de text.
  const [heroBusy, setHeroBusy] = useState(false);
  const [heroError, setHeroError] = useState<string | null>(null);
  const [heroEditingPrompt, setHeroEditingPrompt] = useState(false);
  const [heroPromptDraft, setHeroPromptDraft] = useState("");

  // Panoul AI (E3): brief/instrucțiune, stare de așteptare onestă (contor de secunde).
  const [aiInstruction, setAiInstruction] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiElapsed, setAiElapsed] = useState(0);
  const [revKey, setRevKey] = useState(0); // forțează RevisionPanel să reîncarce după o operație AI
  const [scheduleFor, setScheduleFor] = useState<"approve" | "reschedule" | null>(null); // modalul de programare (E4)

  // Panoul AI stă deschis până la primul draft reușit, apoi se pliază - brief-ul
  // folosit rămâne vizibil în antet, ca adminul să știe ce a generat rezultatul.
  const [aiOpen, setAiOpen] = useState(true);
  const [lastBrief, setLastBrief] = useState("");
  // Ce câmpuri sunt „proaspete" de la AI și încă neatinse manual - badge-ul
  // „AI" de lângă etichetă dispare la prima editare a acelui câmp, nu la a
  // altuia: reviewul e per-câmp, nu per-articol.
  const [aiFresh, setAiFresh] = useState<Record<string, boolean>>({});
  function touchField(f: string) {
    setAiFresh((cur) => (cur[f] ? { ...cur, [f]: false } : cur));
  }

  // Contorul de secunde cât modelul lucrează. Fără progress bar: durata e
  // necunoscută (10–40s), deci orice bară ar fi ficțiune (kimi-editor §3).
  useEffect(() => {
    if (!aiBusy) return;
    setAiElapsed(0);
    const started = performance.now();
    const h = window.setInterval(() => setAiElapsed(Math.floor((performance.now() - started) / 1000)), 1000);
    return () => window.clearInterval(h);
  }, [aiBusy]);

  // itemId: null cât timp articolul NU există pe server încă (id === "new" și
  // nimeni n-a apăsat Save/Generate). Distinct de `item` (obiectul complet, gol
  // în starea nesalvată) - vezi ensureSaved().
  const [itemId, setItemId] = useState<number | null>(id === "new" ? null : id);

  function hydrate(it: ContentItem, opts?: { markAiFresh?: boolean }) {
    setItem(it);
    setItemId(it.id);
    setTitle(it.title); setSlug(it.slug); setDek(it.dek);
    setMetaDescription(it.metaDescription); setExcerpt(it.excerpt);
    setBodyMd(it.bodyMd); setHeroUrl(it.heroUrl ?? ""); setHeroAlt(it.heroAlt ?? "");
    if (it.heroImagePrompt !== undefined) setHeroImagePrompt(it.heroImagePrompt);
    setTags(it.tags); setAuthorName(it.authorName);
    // Slug e derivat de server, nu de model - nu intră la „AI", ca să nu sugereze
    // adminului că trebuie să-l revadă la fel ca un text scris de model.
    if (opts?.markAiFresh) setAiFresh({ title: true, dek: true, metaDescription: true, excerpt: true });
  }

  useEffect(() => {
    if (id === "new") return; // nimic de încărcat - pornește gol, fără fetch
    setItem(null);
    api.contentGet(id).then((res) => { hydrate(res.item); setPreviewToken(res.previewToken); })
      .catch((err) => setMsg({ kind: "err", text: errText(err) }));
  }, [id]);

  /**
   * Creează articolul pe server la PRIMA acțiune reală a adminului (Save sau
   * Generate) - niciodată la click pe „New item". Cu id deja atribuit, nu mai
   * creează a doua oară. `replaceState`, nu `navigate`: URL-ul reflectă noul id
   * fără să lase „…/new" în istoricul de Back (ar aduce un editor gol, orfan).
   */
  async function ensureSaved(): Promise<number> {
    if (itemId !== null) return itemId;
    const { item: created } = await api.contentCreate(kind);
    setItemId(created.id);
    history.replaceState(null, "", `#/content/${kind}/${created.id}`);
    return created.id;
  }

  async function save() {
    setSaveState("saving");
    setMsg(null);
    try {
      const currentId = await ensureSaved();
      const { item: updated } = await api.contentUpdate(currentId, {
        title, slug, dek, metaDescription, excerpt, bodyMd,
        heroUrl: heroUrl || null, heroAlt: heroAlt || null, tags, authorName,
      });
      // NU rehidratăm câmpurile (ar muta cursorul): actualizăm doar itemul
      // pentru status/SEO/preview. Textul din editor e deja sursa de adevăr.
      setItem(updated);
      setSaveState("saved");
    } catch (err) {
      setSaveState("err");
      // 409 slug_taken e cazul frecvent, merită mesaj clar.
      const e = err as { data?: { error?: string } };
      setMsg({ kind: "err", text: e?.data?.error === "slug_taken" ? t("content.err.slugTaken") : errText(err) });
    }
  }

  /** Tranzițiile de review (submit/reject/approve/…) cer un articol deja salvat - butoanele sunt oricum ascunse până atunci. */
  async function transition(fn: (id: number) => Promise<{ item: ContentItem }>, okKey: string) {
    if (itemId === null) return;
    setBusy(true);
    setMsg(null);
    try {
      const { item: updated } = await fn(itemId);
      setItem(updated);
      setMsg({ kind: "ok", text: t(okKey as never) });
    } catch (err) {
      const e = err as { data?: { error?: string } };
      setMsg({ kind: "err", text: e?.data?.error === "not_reviewed" ? t("content.err.notReviewed") : errText(err) });
    } finally {
      setBusy(false);
    }
  }

  async function suggestSlug() {
    try {
      const { slug: suggested } = await api.contentSuggestSlug(kind, title);
      setSlug(suggested);
    } catch (err) { setMsg({ kind: "err", text: errText(err) }); }
  }

  // Generarea primului draft: acțiune explicită, ca Save - creează articolul
  // dacă nu există încă, apoi cere draftul AI.
  async function generate() {
    setAiBusy(true);
    setMsg(null);
    const brief = aiInstruction.trim();
    try {
      const currentId = await ensureSaved();
      const { item: updated } = await api.contentGenerate(currentId, brief || undefined);
      hydrate(updated, { markAiFresh: true });
      setLastBrief(brief);
      setAiInstruction("");
      setAiOpen(false);
      setMode("preview");
      setRevKey((k) => k + 1);
    } catch (err) {
      setMsg({ kind: "err", text: t("content.ai.failed") });
    } finally {
      setAiBusy(false);
    }
  }

  // Revizia: backendul citește starea STOCATĂ, deci salvăm întâi ce e în editor
  // (creând articolul dacă e nevoie), apoi cerem revizia. Rezultatul
  // rehidratează editorul; istoricul arată diferența.
  async function revise() {
    const instruction = aiInstruction.trim();
    if (!instruction) return;
    setAiBusy(true);
    setMsg(null);
    try {
      const currentId = await ensureSaved();
      await api.contentUpdate(currentId, {
        title, slug, dek, metaDescription, excerpt, bodyMd,
        heroUrl: heroUrl || null, heroAlt: heroAlt || null, tags, authorName,
      });
      const { item: updated } = await api.contentRevise(currentId, instruction);
      hydrate(updated, { markAiFresh: true });
      setLastBrief(instruction);
      setAiInstruction("");
      setAiOpen(false);
      setRevKey((k) => k + 1);
    } catch (err) {
      const e = err as { data?: { error?: string } };
      setMsg({ kind: "err", text: e?.data?.error === "slug_taken" ? t("content.err.slugTaken") : t("content.ai.failed") });
    } finally {
      setAiBusy(false);
    }
  }

  // Regenerare/generare hero, separată de generate()/revise(): un „Regenerate"
  // nu trebuie să retrimită tot articolul prin modelul de text. Cere un item
  // salvat - butonul e oricum ascuns/dezactivat până atunci.
  async function regenerateHeroImage(promptOverride?: string) {
    if (itemId === null) return;
    setHeroBusy(true);
    setHeroError(null);
    try {
      const { item: updated } = await api.contentGenerateImage(itemId, promptOverride);
      hydrate(updated);
      setHeroEditingPrompt(false);
    } catch (err) {
      setHeroError(t("content.hero.failed"));
    } finally {
      setHeroBusy(false);
    }
  }

  // Skeleton doar cât timp se ÎNCARCĂ un articol existent - un editor nou
  // (id === "new") pornește gol instant, fără fetch, deci fără skeleton.
  if (id !== "new" && !item) {
    return <div class="content"><Skeleton h="38px" w="280px" style={{ marginBottom: "16px", borderRadius: "10px" }} /><Skeleton h="60vh" style={{ borderRadius: "16px" }} /></div>;
  }

  const status: ContentStatus = item?.status ?? "draft";
  const renderedHtml = previewMd.render(bodyMd || "");
  const previewUrl = previewToken ? `https://askmywebsiteai.com/${kind}/${slug}?preview=${previewToken}` : null;

  return (
    <div class="content">
      <div class="editor-topbar">
        <span class={`badge ${CONTENT_STATUS_TONE[status]}`}>{statusLabel(status)}</span>
        {status === "scheduled" && item?.publishAt && (
          <span class="sched-when muted">{t("content.sched.for")} {fmtCanary(item.publishAt)}</span>
        )}
        <SaveState state={saveState} />
        <div class="row" style={{ gap: "8px", marginLeft: "auto" }}>
          <button class="btn ghost" disabled={saveState === "saving"} onClick={() => void save()}>{t("content.save")}</button>
          {/* Doar draft SALVAT: un articol care a intrat vreodată în review se
              retrage cu Archive (jos), nu se șterge - vezi contentService.deleteItem. */}
          {itemId !== null && status === "draft" && (
            <button class="btn ghost danger" onClick={async () => {
              if (!(await confirm({ title: t("content.delete"), body: t("content.delete.confirm", title || t("content.untitled")), tone: "danger", confirmLabel: t("content.delete") }))) return;
              try { await api.contentDelete(itemId); navigate(`#/content/${kind}`); }
              catch (err) { setMsg({ kind: "err", text: errText(err) }); }
            }}>{t("content.delete")}</button>
          )}
          {/* Review/publish cer un articol deja salvat - un draft nesalvat n-are ce trimite la review. */}
          {itemId !== null && (
            <ReviewActions status={status} busy={busy}
              onSubmit={() => void transition((cid) => api.contentSubmit(cid), "content.msg.submitted")}
              onReject={() => void transition((cid) => api.contentReject(cid), "content.msg.rejected")}
              onApprove={() => setScheduleFor("approve")}
              onPublishNow={() => void transition((cid) => api.contentPublish(cid), "content.msg.published")}
              onReschedule={() => setScheduleFor("reschedule")}
              onArchive={async () => { if (await confirm({ title: t("content.archive"), body: t("content.archive.confirm"), tone: "danger", confirmLabel: t("content.archive") })) void transition((cid) => api.contentArchive(cid), "content.msg.archived"); }}
            />
          )}
        </div>
      </div>

      {scheduleFor && itemId !== null && (
        <ScheduleModal
          initial={item?.publishAt ?? null}
          mode={scheduleFor}
          onClose={() => setScheduleFor(null)}
          onConfirm={(publishAt) => {
            setScheduleFor(null);
            if (scheduleFor === "reschedule") {
              void transition((cid) => api.contentSchedule(cid, publishAt!), "content.msg.scheduled");
            } else {
              void transition((cid) => api.contentApprove(cid, publishAt ?? undefined), publishAt ? "content.msg.scheduled" : "content.msg.published");
            }
          }}
        />
      )}

      {msg && <div class={`msg ${msg.kind}`}>{msg.text}</div>}

      <AiPanel
        hasBody={bodyMd.trim().length > 0}
        instruction={aiInstruction}
        onInstruction={setAiInstruction}
        busy={aiBusy}
        elapsed={aiElapsed}
        open={aiOpen}
        onToggleOpen={() => setAiOpen((v) => !v)}
        lastBrief={lastBrief}
        onGenerate={() => void generate()}
        onRevise={() => void revise()}
      />

      <div class="editor-grid">
        <div class="editor-main">
          <div class="pane-switch segmented">
            <button class={mode === "write" ? "active" : ""} onClick={() => setMode("write")}>{t("content.write")}</button>
            <button class={mode === "preview" ? "active" : ""} onClick={() => setMode("preview")}>{t("content.preview")}</button>
          </div>
          {mode === "write" ? (
            <textarea class="md-input" value={bodyMd} placeholder={t("content.body.placeholder")}
              onInput={(e) => { setBodyMd((e.target as HTMLTextAreaElement).value); }} />
          ) : (
            // Titlul + dek-ul sunt câmpuri separate (pagina publică le randează ea),
            // deci corpul nu conține H1 - dar un preview fără titlu arăta „ciudat,
            // parcă fără titlu" (3 aug 2026). Le punem deasupra, ca pe pagina reală.
            <article class="md-preview">
              {title && <h1>{title}</h1>}
              {dek && <p class="muted" style={{ marginTop: "-2px", fontSize: "15px" }}>{dek}</p>}
              <div dangerouslySetInnerHTML={{ __html: renderedHtml }} />
            </article>
          )}
        </div>

        <aside class="editor-side">
          <HeroImageCard
            itemId={itemId}
            heroUrl={heroUrl}
            heroAlt={heroAlt}
            prompt={heroImagePrompt}
            busy={heroBusy}
            error={heroError}
            editingPrompt={heroEditingPrompt}
            promptDraft={heroPromptDraft}
            onAlt={setHeroAlt}
            onStartEditPrompt={() => { setHeroPromptDraft(heroImagePrompt ?? ""); setHeroEditingPrompt(true); }}
            onCancelEditPrompt={() => setHeroEditingPrompt(false)}
            onPromptDraft={setHeroPromptDraft}
            onGenerate={() => void regenerateHeroImage()}
            onRegenerate={() => void regenerateHeroImage()}
            onApplyPrompt={() => void regenerateHeroImage(heroPromptDraft.trim() || undefined)}
          />

          <section class="card meta-panel">
            <FieldLabel text={t("content.f.title")} ai={aiFresh.title} />
            <input value={title} maxLength={300} onInput={(e) => { setTitle((e.target as HTMLInputElement).value); touchField("title"); }} />
            <FieldCount n={title.length} max={60} />

            <label>{t("content.f.slug")}</label>
            <div class="slug-row">
              <span class="slug-prefix">/{kind}/</span>
              <input value={slug} spellcheck={false} onInput={(e) => setSlug((e.target as HTMLInputElement).value)} />
            </div>
            <button class="linkbtn" type="button" onClick={() => void suggestSlug()}>{t("content.slug.derive")}</button>

            <FieldLabel text={t("content.f.dek")} ai={aiFresh.dek} />
            <input value={dek} maxLength={500} onInput={(e) => { setDek((e.target as HTMLInputElement).value); touchField("dek"); }} />

            <label>{t("content.f.author")}</label>
            <input value={authorName} maxLength={120} onInput={(e) => setAuthorName((e.target as HTMLInputElement).value)} />

            <label>{t("content.f.tags")}</label>
            <TagInput tags={tags} onChange={setTags} />

            {previewUrl && <a class="linkbtn" href={previewUrl} target="_blank" rel="noreferrer">{t("content.viewPreview")}</a>}
          </section>

          <SeoPanel
            kind={kind} metaDescription={metaDescription} excerpt={excerpt} slug={slug} bodyMd={bodyMd} hasHero={!!heroUrl}
            aiFreshMeta={!!aiFresh.metaDescription} aiFreshExcerpt={!!aiFresh.excerpt}
            onMetaDescription={(v) => { setMetaDescription(v); touchField("metaDescription"); }}
            onExcerpt={(v) => { setExcerpt(v); touchField("excerpt"); }}
          />

          {/* Distribuție + istoric cer un id real - n-au ce arăta pentru un draft nesalvat. */}
          {itemId !== null ? (
            <>
              <DistributionPanel id={itemId} status={status} />
              <RevisionPanel id={itemId} reloadKey={revKey} />
            </>
          ) : (
            <p class="dist-note muted">{t("content.unsavedNote")}</p>
          )}
        </aside>
      </div>
      {confirmNode}
    </div>
  );
}

/** Butoanele de review, în funcție de status. Publicarea trece prin approve (poarta reviewedBy). */
function ReviewActions({ status, busy, onSubmit, onReject, onApprove, onPublishNow, onReschedule, onArchive }: {
  status: ContentStatus; busy: boolean;
  onSubmit: () => void; onReject: () => void; onApprove: () => void;
  onPublishNow: () => void; onReschedule: () => void; onArchive: () => void;
}) {
  if (status === "draft") return <button class="btn primary" disabled={busy} onClick={onSubmit}>{t("content.submit")}</button>;
  if (status === "in_review") return (
    <>
      <button class="btn ghost" disabled={busy} onClick={onReject}>{t("content.reject")}</button>
      {/* „Approve…" deschide modalul: acum sau programat (E4). */}
      <button class="btn primary" disabled={busy} onClick={onApprove}>{t("content.approve")}</button>
    </>
  );
  if (status === "scheduled") return (
    <>
      <button class="btn ghost" disabled={busy} onClick={onArchive}>{t("content.archive")}</button>
      <button class="btn ghost" disabled={busy} onClick={onReschedule}>{t("content.reschedule")}</button>
      <button class="btn primary" disabled={busy} onClick={onPublishNow}>{t("content.publishNow")}</button>
    </>
  );
  if (status === "published") return <button class="btn ghost" disabled={busy} onClick={onArchive}>{t("content.archive")}</button>;
  return null; // archived: nicio acțiune
}

/**
 * Modalul de programare (E4): „publică acum" sau la o dată aleasă, MEREU afișată
 * în ora din Tenerife (Atlantic/Canary). Inputul nativ arată ora locală a
 * browserului, deci eticheta + conversia sunt esențiale - vezi format.ts.
 */
function ScheduleModal({ initial, mode, onClose, onConfirm }: {
  initial: number | null; mode: "approve" | "reschedule"; onClose: () => void; onConfirm: (publishAt: number | null) => void;
}) {
  // Prestabilit: ora programată existentă, sau „acum + 1h" rotunjit, în ora Canary.
  const [when, setWhen] = useState(() => epochToCanaryLocal(initial ?? Date.now() + 3600_000));
  const parsed = canaryLocalToEpoch(when);
  const inPast = parsed !== null && parsed <= Date.now();
  return (
    <Modal onClose={onClose}>
      <h3>{mode === "reschedule" ? t("content.sched.retitle") : t("content.sched.title")}</h3>
      <p class="muted" style={{ fontSize: "13px" }}>{t("content.sched.body")}</p>
      <label>{t("content.sched.at")}</label>
      <input type="datetime-local" value={when} onInput={(e) => setWhen((e.target as HTMLInputElement).value)} />
      <p class="sched-tz muted">{parsed ? fmtCanary(parsed) : t("content.sched.invalid")}</p>
      {inPast && <p class="msg err" style={{ marginTop: "8px" }}>{t("content.sched.past")}</p>}
      <div class="actions">
        <button class="btn ghost" onClick={onClose}>{t("common.cancel")}</button>
        {mode === "approve" && (
          <button class="btn subtle" onClick={() => onConfirm(null)}>{t("content.sched.now")}</button>
        )}
        <button class="btn primary" disabled={parsed === null || inPast} onClick={() => onConfirm(parsed)}>
          {mode === "reschedule" ? t("content.reschedule") : t("content.sched.schedule")}
        </button>
      </div>
    </Modal>
  );
}

function SaveState({ state }: { state: "idle" | "saving" | "saved" | "err" }) {
  if (state === "idle") return null;
  if (state === "saving") return <span class="save-state muted">{t("content.saving")}</span>;
  if (state === "saved") return <span class="save-state muted">{t("content.saved")}</span>;
  return <span class="save-state err">{t("content.saveErr")}</span>;
}

/** Contor de caractere: gri implicit, galben peste maximul dur (trunchiere SERP). */
function FieldCount({ n, max, min }: { n: number; max: number; min?: number }) {
  const over = n > max;
  const under = min !== undefined && n > 0 && n < min;
  return <div class={`field-count${over ? " warn" : ""}`}>{n} / {max}{under ? ` · ${t("content.seo.tooShort")}` : ""}</div>;
}

/** Eticheta unui câmp, cu badge-ul „AI" cât timp valoarea vine neatinsă din ultima generare/revizie. */
function FieldLabel({ text, ai }: { text: string; ai?: boolean }) {
  return <label>{text}{ai && <span class="badge plan ai-field-badge">{t("content.ai.fieldBadge")}</span>}</label>;
}

// Aceleași praguri ca backendul (contentService THIN_CONTENT_FLOOR): o știre
// scurtă e legitimă, un articol de blog de 150 de cuvinte nu.
const THIN_FLOOR: Record<ContentKind, number> = { news: 150, research: 800, blog: 300 };

/**
 * Panoul SEO: patru verificări live + câmpurile care le alimentează (meta
 * description, excerpt). Pliat implicit - după o generare AI astea sunt deja
 * completate corect în majoritatea cazurilor, iar titlul/dek-ul din
 * meta-panel rămân prioritatea vizuală. Punctul din antet arată cel mai rău
 * dintre cele patru verificări, ca plierea să nu ascundă ceva de rezolvat.
 */
function SeoPanel({ kind, metaDescription, excerpt, slug, bodyMd, hasHero, aiFreshMeta, aiFreshExcerpt, onMetaDescription, onExcerpt }: {
  kind: ContentKind; metaDescription: string; excerpt: string; slug: string; bodyMd: string; hasHero: boolean;
  aiFreshMeta: boolean; aiFreshExcerpt: boolean;
  onMetaDescription: (v: string) => void; onExcerpt: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const words = bodyMd.trim().split(/\s+/).filter(Boolean).length;
  const checks = [
    { ok: metaDescription.length >= 120 && metaDescription.length <= 160, label: t("content.seo.metaHint") },
    { ok: /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) && slug.length <= 75, label: t("content.seo.slugHint") },
    { ok: hasHero, label: t("content.seo.heroHint") },
    { ok: words >= THIN_FLOOR[kind], label: t("content.seo.thinHint") },
  ];
  const pass = checks.filter((c) => c.ok).length;
  return (
    <section class={`card seo-panel conv-collapsible${open ? " open" : ""}`}>
      <div class="card-head">
        <button type="button" class="conv-head-toggle" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          <span class="conv-chevron"><IconChevronDown /></span>
          <span class="conv-head-txt"><h2>{t("content.seo.title")}</h2></span>
        </button>
        <span class={`badge ${pass === checks.length ? "ok" : "warn"}`}>{pass} / {checks.length}</span>
      </div>
      {open && (
        <>
          <FieldLabel text={t("content.f.meta")} ai={aiFreshMeta} />
          <textarea rows={3} value={metaDescription} onInput={(e) => onMetaDescription((e.target as HTMLTextAreaElement).value)} />
          <FieldCount n={metaDescription.length} max={160} min={120} />

          <FieldLabel text={t("content.f.excerpt")} ai={aiFreshExcerpt} />
          <textarea rows={2} value={excerpt} onInput={(e) => onExcerpt((e.target as HTMLTextAreaElement).value)} />

          <div class="seo-checks">
            {checks.map((c, i) => (
              <div key={i} class="seo-check"><span class={`seo-dot ${c.ok ? "ok" : "warn"}`} /><span>{c.label}</span></div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

/** Chips de tag: Enter/virgulă adaugă, × scoate. */
function TagInput({ tags, onChange }: { tags: string[]; onChange: (t: string[]) => void }) {
  const [draft, setDraft] = useState("");
  function commit() {
    const v = draft.trim().toLowerCase();
    if (v && !tags.includes(v)) onChange([...tags, v]);
    setDraft("");
  }
  return (
    <div class="tag-box" onClick={(e) => (e.currentTarget.querySelector("input") as HTMLInputElement)?.focus()}>
      {tags.map((tag) => (
        <span key={tag} class="tag-chip">{tag}
          <button type="button" aria-label={t("content.tag.remove")} onClick={() => onChange(tags.filter((x) => x !== tag))}>×</button>
        </span>
      ))}
      <input value={draft} placeholder={tags.length ? "" : t("content.tag.placeholder")}
        onInput={(e) => setDraft((e.target as HTMLInputElement).value)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); commit(); } else if (e.key === "Backspace" && !draft && tags.length) onChange(tags.slice(0, -1)); }} />
    </div>
  );
}

/**
 * Cardul hero (E3 - imagine AI): primul din sidebar, ca ordinea să fie ordinea
 * de review - imagine, apoi metadata text (recomandarea reviewului extern de
 * UI/UX). Nu mai există câmp manual de URL: imaginea vine DOAR din generare
 * (o dată automat, alături de textul articolului, la primul „Generate full
 * article"; oricând după, cu „Regenerate" sau cu un prompt editat aici).
 *
 * Eșecul generării de imagine nu blochează nimic altceva din articol - cardul
 * arată eroarea și rămâne cu „Generate"/„Regenerate" disponibil, restul
 * editorului funcționează normal (contentImage.ts, generateAndSaveHeroImage).
 */
function HeroImageCard({ itemId, heroUrl, heroAlt, prompt, busy, error, editingPrompt, promptDraft, onAlt, onStartEditPrompt, onCancelEditPrompt, onPromptDraft, onGenerate, onRegenerate, onApplyPrompt }: {
  itemId: number | null; heroUrl: string; heroAlt: string; prompt: string | null;
  busy: boolean; error: string | null; editingPrompt: boolean; promptDraft: string;
  onAlt: (v: string) => void;
  onStartEditPrompt: () => void; onCancelEditPrompt: () => void; onPromptDraft: (v: string) => void;
  onGenerate: () => void; onRegenerate: () => void; onApplyPrompt: () => void;
}) {
  const hasImage = !!heroUrl;
  return (
    <section class="card heroimg-panel">
      <div class="card-head">
        <div>
          <h2>{t("content.hero.title")}</h2>
          <p>{hasImage ? t("content.hero.subGenerated") : t("content.hero.subEmpty")}</p>
        </div>
        {busy ? (
          <span class="badge info pulse">{t("content.hero.painting")}</span>
        ) : error ? (
          <span class="badge warn">{t("content.hero.failedBadge")}</span>
        ) : hasImage ? (
          <span class="badge ok">{t("content.hero.ready")}</span>
        ) : null}
      </div>

      <figure class="heroimg-frame">
        {hasImage && <img src={heroUrl} alt={heroAlt} />}
        {busy && <span class="skeleton" />}
      </figure>

      {error && <div class="msg warn">{error}</div>}

      {hasImage && (
        <>
          <label>{t("content.f.heroAlt")}</label>
          <input value={heroAlt} maxLength={500} onInput={(e) => onAlt((e.target as HTMLInputElement).value)} />

          {editingPrompt ? (
            <>
              <label>{t("content.hero.editPrompt")}</label>
              <textarea rows={3} value={promptDraft} disabled={busy}
                placeholder={t("content.hero.promptPlaceholder")}
                onInput={(e) => onPromptDraft((e.target as HTMLTextAreaElement).value)} />
              <div class="ai-actions">
                <button class="btn ghost sm" disabled={busy} onClick={onCancelEditPrompt}>{t("content.hero.cancelEdit")}</button>
                <button class="btn primary sm" disabled={busy || !promptDraft.trim()} onClick={onApplyPrompt}>{t("content.hero.applyPrompt")}</button>
              </div>
            </>
          ) : (
            <>
              {prompt && <p class="heroimg-prompt" title={prompt}>{prompt}</p>}
              <div class="ai-actions">
                <button class="linkbtn" type="button" disabled={busy} onClick={onStartEditPrompt}>{t("content.hero.editPrompt")}</button>
                <button class="btn ghost sm" disabled={busy} onClick={onRegenerate}>{t("content.hero.regenerate")}</button>
              </div>
            </>
          )}
        </>
      )}

      {!hasImage && (
        itemId === null ? (
          <p class="dist-note muted">{t("content.hero.unsavedNote")}</p>
        ) : (
          <div class="ai-actions">
            <button class="btn primary sm" disabled={busy} onClick={onGenerate}>{t("content.hero.generate")}</button>
          </div>
        )
      )}
    </section>
  );
}

/**
 * Panoul AI (E3), acum sus în pagină, deasupra workbench-ului: un singur brief
 * completează absolut tot ce e nevoie - titlu, slug, dek, meta description,
 * excerpt, corp, tag-uri (slug-ul e derivat de server din titlu, vezi
 * `applyDraft`, dar rezultatul e același: nimic manual până la review).
 *
 * Se pliază singur după primul draft reușit - brief-ul folosit rămâne vizibil
 * în antet, iar un click îl redeschide pentru o revizie nouă. Cât modelul
 * lucrează, composerul e blocat și un contor de secunde arată cinstit că merge
 * (10–40s, nu o bară de progres fictivă).
 */
function AiPanel({ hasBody, instruction, onInstruction, busy, elapsed, open, onToggleOpen, lastBrief, onGenerate, onRevise }: {
  hasBody: boolean; instruction: string; onInstruction: (v: string) => void;
  busy: boolean; elapsed: number; open: boolean; onToggleOpen: () => void; lastBrief: string;
  onGenerate: () => void; onRevise: () => void;
}) {
  const collapsedSummary = hasBody && !open && !busy;
  // Brief-ul poate avea mii de caractere (un brief bun chiar are): afișat întreg
  // ar umfla panoul cât jumătate de ecran - găsit cu primul articol real, 3 aug
  // 2026. Rezumatul e o singură linie; textul complet rămâne în title (hover)
  // și în istoricul de revizii.
  const briefPreview = lastBrief.length > 140 ? `${lastBrief.slice(0, 140)}…` : lastBrief;
  return (
    <section class={`card upsell ai-brief${collapsedSummary ? " conv-collapsible" : ""}`}>
      <div class="card-head">
        {collapsedSummary ? (
          <button type="button" class="conv-head-toggle" aria-expanded={false} onClick={onToggleOpen} title={lastBrief || undefined}>
            <span class="conv-chevron"><IconChevronDown /></span>
            <span class="conv-head-txt">
              <h2>✦ {t("content.ai.title")}</h2>
              <p>
                {t("content.ai.collapsedFrom")}: “{briefPreview || t("content.ai.collapsedEmpty")}” - {t("content.ai.reopenHint")}
              </p>
            </span>
          </button>
        ) : (
          <div>
            <h2>✦ {t("content.ai.title")}</h2>
            <p>{hasBody ? t("content.ai.subRevise") : t("content.ai.subGenerate")}</p>
          </div>
        )}
        {collapsedSummary && <span class="badge ok">{t("content.ai.filled")}</span>}
      </div>
      {(open || busy || !hasBody) && (
        <div class="ai-brief-form">
          {/* Brief-ul consumat rămâne vizibil și cu panoul deschis: fără linia asta,
              redeschiderea arăta o casetă goală și „ce am scris a dispărut" (3 aug 2026). */}
          {hasBody && lastBrief && (
            <p class="muted" style={{ margin: "0 0 8px", fontSize: "12px" }} title={lastBrief}>
              {t("content.ai.collapsedFrom")}: “{briefPreview}”
            </p>
          )}
          <textarea
            class="ai-instruction" rows={hasBody ? 2 : 3}
            value={instruction} disabled={busy}
            placeholder={hasBody ? t("content.ai.revisePlaceholder") : t("content.ai.briefPlaceholder")}
            onInput={(e) => onInstruction((e.target as HTMLTextAreaElement).value)}
          />
          <div class="ai-actions">
            {busy ? (
              <span class="ai-working">
                <span class="badge info pulse">{t("content.ai.working")}</span>
                <span class="ai-timer">{elapsed}s</span>
              </span>
            ) : (
              <span class="ai-hint muted">{hasBody ? t("content.ai.wait") : t("content.ai.waitGenerate")}</span>
            )}
            {hasBody ? (
              <button class="btn primary sm" disabled={busy || !instruction.trim()} onClick={onRevise}>{t("content.ai.revise")}</button>
            ) : (
              <button class="btn primary sm" disabled={busy} onClick={onGenerate}>{t("content.ai.generate")}</button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

const DIST_STATUS_TONE: Record<string, string> = { pending: "muted", sent: "ok", failed: "warn", cancelled: "muted" };

/**
 * Distribuția pe social (E5): atașează canale + programare + copy per canal, cu
 * lista celor existente și statusul lor. Un canal neconfigurat (stub) e vizibil
 * dar marcat, ca adminul să știe că e „coming soon". Scadența se afișează din
 * `dueAt` (backendul o derivă din publicarea articolului).
 */
function DistributionPanel({ id, status }: { id: number; status: ContentStatus }) {
  const [dists, setDists] = useState<ContentDistribution[] | null>(null);
  const [channels, setChannels] = useState<SocialChannelInfo[]>([]);
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  // formular
  const [channel, setChannel] = useState("webhook");
  const [mode, setMode] = useState<"publish" | "offset" | "at">("publish");
  const [offsetMin, setOffsetMin] = useState("120");
  const [at, setAt] = useState(() => epochToCanaryLocal(Date.now() + 3600_000));
  const [text, setText] = useState("");

  async function reload() { setDists((await api.contentDistributions(id)).distributions); }
  useEffect(() => {
    setDists(null);
    reload().catch(() => setDists([]));
    api.contentChannels().then((r) => setChannels(r.channels)).catch(() => {});
  }, [id]);

  async function add() {
    setBusy(true);
    setMsg(null);
    try {
      const input: { channel: string; scheduleMode: "publish" | "offset" | "at"; scheduledAt?: number; offsetMinutes?: number; text: string } =
        { channel, scheduleMode: mode, text };
      if (mode === "offset") input.offsetMinutes = Number(offsetMin);
      if (mode === "at") {
        const ms = canaryLocalToEpoch(at);
        if (ms === null) { setMsg(t("content.dist.badTime")); setBusy(false); return; }
        input.scheduledAt = ms;
      }
      await api.contentAddDistribution(id, input);
      setText(""); setAdding(false);
      await reload();
    } catch (err) {
      setMsg(errText(err));
    } finally {
      setBusy(false);
    }
  }

  async function cancel(distId: number) {
    try { await api.contentCancelDistribution(distId); await reload(); }
    catch (err) { setMsg(errText(err)); }
  }

  const configuredMap = new Map(channels.map((c) => [c.channel, c.configured]));

  return (
    <section class="card dist-panel">
      <div class="card-head">
        <h2>{t("content.dist.title")}</h2>
        {!adding && <button class="btn subtle sm" onClick={() => setAdding(true)}>{t("content.dist.add")}</button>}
      </div>
      <p class="dist-sub muted">{t("content.dist.sub")}</p>
      {msg && <div class="msg err" style={{ marginBottom: "8px" }}>{msg}</div>}

      {adding && (
        <div class="dist-form">
          <label>{t("content.dist.channel")}</label>
          <select value={channel} onChange={(e) => setChannel((e.target as HTMLSelectElement).value)}>
            {channels.map((c) => (
              <option value={c.channel}>{c.channel}{c.configured ? "" : ` - ${t("content.dist.soon")}`}</option>
            ))}
          </select>

          <label>{t("content.dist.when")}</label>
          <select value={mode} onChange={(e) => setMode((e.target as HTMLSelectElement).value as "publish" | "offset" | "at")}>
            <option value="publish">{t("content.dist.mode.publish")}</option>
            <option value="offset">{t("content.dist.mode.offset")}</option>
            <option value="at">{t("content.dist.mode.at")}</option>
          </select>
          {mode === "offset" && (
            <div class="dist-offset">
              <input type="number" min={0} value={offsetMin} onInput={(e) => setOffsetMin((e.target as HTMLInputElement).value)} />
              <span class="muted">{t("content.dist.minutesAfter")}</span>
            </div>
          )}
          {mode === "at" && (
            <>
              <input type="datetime-local" value={at} onInput={(e) => setAt((e.target as HTMLInputElement).value)} />
              <p class="sched-tz muted">{canaryLocalToEpoch(at) ? fmtCanary(canaryLocalToEpoch(at)!) : t("content.sched.invalid")}</p>
            </>
          )}

          <label>{t("content.dist.copy")}</label>
          <textarea rows={3} value={text} placeholder={t("content.dist.copyPlaceholder")} onInput={(e) => setText((e.target as HTMLTextAreaElement).value)} />
          <div class="dist-form-actions">
            <button class="btn ghost sm" onClick={() => setAdding(false)}>{t("common.cancel")}</button>
            <button class="btn primary sm" disabled={busy || !text.trim()} onClick={() => void add()}>{t("content.dist.save")}</button>
          </div>
        </div>
      )}

      {!dists && <Skeleton h="40px" style={{ borderRadius: "10px" }} />}
      {dists && dists.length === 0 && !adding && <div class="muted" style={{ fontSize: "12.5px" }}>{t("content.dist.empty")}</div>}
      {dists && dists.length > 0 && (
        <div class="dist-list">
          {dists.map((d) => (
            <div key={d.id} class="dist-item">
              <div class="dist-row">
                <span class="dist-channel">{d.channel}</span>
                <span class={`badge ${DIST_STATUS_TONE[d.status]}`}>{t(`content.dist.status.${d.status}` as never)}</span>
                {configuredMap.get(d.channel) === false && <span class="dist-soon muted">{t("content.dist.soon")}</span>}
              </div>
              <div class="dist-meta muted">
                {d.status === "sent" && d.sentAt ? `${t("content.dist.sentAt")} ${fmtCanary(d.sentAt)}`
                  : d.dueAt ? `${t("content.dist.due")} ${fmtCanary(d.dueAt)}`
                  : t("content.dist.afterPublish")}
              </div>
              {d.text && <div class="dist-text">{d.text}</div>}
              {d.status === "failed" && d.lastError && <div class="dist-err">{d.lastError}</div>}
              {d.status === "pending" && <button class="linkbtn" onClick={() => void cancel(d.id)}>{t("content.dist.cancel")}</button>}
            </div>
          ))}
        </div>
      )}
      {status === "draft" && dists && dists.length > 0 && (
        <p class="dist-note muted">{t("content.dist.draftNote")}</p>
      )}
    </section>
  );
}

/** Istoricul reviziilor + „save a checkpoint". Append-only. reloadKey reîncarcă după o operație AI. */
function RevisionPanel({ id, reloadKey = 0 }: { id: number; reloadKey?: number }) {
  const [revisions, setRevisions] = useState<ContentRevision[] | null>(null);
  const [busy, setBusy] = useState(false);

  async function reload() { setRevisions((await api.contentRevisions(id)).revisions); }
  useEffect(() => { setRevisions(null); reload().catch(() => setRevisions([])); }, [id, reloadKey]);

  async function checkpoint() {
    setBusy(true);
    try { await api.contentAddRevision(id); await reload(); } finally { setBusy(false); }
  }

  return (
    <section class="card rev-panel">
      <div class="card-head">
        <h2>{t("content.rev.title")}</h2>
        <button class="btn subtle sm" disabled={busy} onClick={() => void checkpoint()}>{t("content.rev.save")}</button>
      </div>
      {!revisions && <Skeleton h="40px" style={{ borderRadius: "10px" }} />}
      {revisions && revisions.length === 0 && <div class="muted" style={{ fontSize: "12.5px" }}>{t("content.rev.empty")}</div>}
      {revisions && revisions.length > 0 && (
        <div class="rev-list">
          {revisions.map((r) => (
            <div key={r.id} class="rev-item">
              <span class="rev-no">#{r.revisionNo}</span>
              <span class="rev-src">{r.source === "ai" ? t("content.rev.ai") : t("content.rev.admin")}</span>
              {r.instruction && <span class="rev-note">{r.instruction}</span>}
              <span class="rev-when muted">{fmtDateTime(r.createdAt)}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

const IconDoc = () => (
  <Svg><path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" /><path d="M9 9h1M9 13h6M9 17h6" /></Svg>
);
const IconNews = () => (
  <Svg><path d="M4 5h13a1 1 0 0 1 1 1v12a2 2 0 0 0 2-2V8" /><path d="M4 5a1 1 0 0 0-1 1v12a2 2 0 0 0 2 2h13" /><path d="M7 8h7M7 12h7M7 16h4" /></Svg>
);

type View = "dashboard" | "plan" | "referral" | "activity" | "console" | "codes" | "tickets" | "settings";

/**
 * Vederile vizibile doar super-adminilor. „console"/„codes"/„activity"/„tickets"
 * sunt secțiunea Admin console; „referral" e altceva - o pagină de client ținută
 * ascunsă până când programul de afiliere e gata (document propriu de termeni +
 * privacy actualizat, vezi registru → G14). O promisiune de comision vizibilă în
 * produs fără temei contractual e golul pe care nu-l vrem la primii clienți
 * plătitori; până atunci o testăm live pe conturile noastre.
 */
const ADMIN_VIEWS: View[] = ["console", "codes", "activity", "tickets", "referral"];

const TITLES: Record<View, { title: string; sub: string }> = {
  dashboard: { title: "sites.title", sub: "sites.sub" },
  plan: { title: "plan.title", sub: "plan.sub" },
  referral: { title: "ref.title", sub: "ref.sub" },
  activity: { title: "act.title", sub: "act.sub" },
  console: { title: "ac.title", sub: "ac.sub" },
  codes: { title: "cd.title", sub: "cd.sub" },
  tickets: { title: "tk.title", sub: "tk.sub" },
  settings: { title: "set.title", sub: "set.sub" },
};

function NavItem({ icon, label, active, onClick, badge }: { icon: ComponentChildren; label: string; active: boolean; onClick: () => void; badge?: number }) {
  return (
    <button class={`nav-item ${active ? "active" : ""}`} aria-label={label} onClick={onClick}>
      {icon}<span class="lbl">{label}</span>
      {badge ? <span class="nav-badge">{badge}</span> : null}
    </button>
  );
}

const SUPPORT_EMAIL = "support@askmywebsiteai.com";
const LEGAL_LINKS = [
  { key: "nav.terms", href: "https://askmywebsiteai.com/terms" },
  { key: "nav.privacy", href: "https://askmywebsiteai.com/privacy" },
  { key: "nav.dpa", href: "https://askmywebsiteai.com/dpa" },
  { key: "nav.cookies", href: "https://askmywebsiteai.com/cookies" },
] as const;

/** Modal "Help & support": contact + email copiabil + linkuri legale, în locul listei de linkuri din rail. */
function HelpModal({ email, onClose }: { email: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  return (
    <Modal onClose={onClose}>
      <div class="help-head">
        <div class="ic"><IconHelp /></div>
        <div>
          <h3>{t("help.title")}</h3>
          <p>{t("help.sub")}</p>
        </div>
      </div>
      <a
        class="btn primary block"
        style={{ marginTop: "20px" }}
        href={`https://askmywebsiteai.com/contact?email=${encodeURIComponent(email)}`}
        target="_blank"
        rel="noopener"
      >
        <IconMail /> {t("help.contact")}
      </a>
      <div class="help-or">{t("help.or")}</div>
      <button
        type="button"
        class="help-mail"
        onClick={() => void copyText(SUPPORT_EMAIL).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); })}
      >
        {SUPPORT_EMAIL}
        <span class="ic">{copied ? <IconCheck /> : <IconCopy />}</span>
      </button>
      <div class="help-legal">
        {LEGAL_LINKS.map((l) => (
          <a key={l.key} href={l.href} target="_blank" rel="noopener">{t(l.key)}</a>
        ))}
      </div>
    </Modal>
  );
}

/**
 * „Send feedback" — doar pe planuri plătite (butonul din rail e ascuns altfel,
 * iar serverul refuză oricum cu 403). Text, scris sau dictat: dictarea e a
 * browserului (feedback.ts), cuvintele apar în căsuță cât vorbești și se pot
 * corecta. Nota de sub microfon e informarea de la colectare (privacy §2.1).
 */
const DICTATION_PROBLEM_TEXT: Record<DictationProblem, StringKey> = {
  blocked: "fb.micBlocked",
  network: "fb.micNetwork",
  "no-mic": "fb.micMissing",
  failed: "fb.micFailed",
};

function FeedbackModal({ onClose }: { onClose: () => void }) {
  const [text, setText] = useState("");
  const [lang, setLang] = useState(() => defaultDictationLang(navigator.languages ?? [navigator.language]));
  const [listening, setListening] = useState(false);
  const [dictated, setDictated] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const textRef = useRef("");
  const dictation = useRef<Dictation | null>(null);
  const field = useRef<HTMLTextAreaElement>(null);
  const canDictate = dictationSupported();

  const updateText = (value: string) => {
    textRef.current = value;
    setText(value);
  };
  // Închiderea ferestrei în timpul dictării oprește microfonul.
  useEffect(() => () => dictation.current?.stop(), []);

  const toggleDictation = () => {
    if (listening) {
      dictation.current?.stop();
      return;
    }
    setErr(null);
    // O instanță nouă la fiecare pornire: limba se poate schimba între două dictări.
    dictation.current = createDictation({
      lang,
      getBase: () => textRef.current,
      onText: (value) => { updateText(value); setDictated(true); },
      onListening: (on) => {
        setListening(on);
        if (!on) field.current?.focus();
      },
      onProblem: (problem) => setErr(t(DICTATION_PROBLEM_TEXT[problem])),
    });
    dictation.current?.start();
  };

  const send = async () => {
    dictation.current?.stop();
    const value = textRef.current.trim();
    if (!value) return;
    setSending(true);
    setErr(null);
    try {
      await api.sendFeedback({ text: value, dictated, screen: feedbackScreen(location.hash) });
      setDone(true);
    } catch (e) {
      const code = e instanceof ApiError ? (e.data as { error?: string } | null)?.error : undefined;
      setErr(code === "daily_limit" ? t("fb.dailyLimit") : errText(e));
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <Modal onClose={onClose}>
        <div class="help-head">
          <div class="ic"><IconCheck /></div>
          <div>
            <h3>{t("fb.title")}</h3>
            <p>{t("fb.thanks")}</p>
          </div>
        </div>
        <div class="actions" style={{ marginTop: "20px" }}>
          <button type="button" class="btn primary" onClick={onClose}>{t("common.close")}</button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose}>
      <div class="help-head">
        <div class="ic"><IconChat /></div>
        <div>
          <h3>{t("fb.title")}</h3>
          <p>{t(canDictate ? "fb.sub" : "fb.subTypeOnly")}</p>
        </div>
        <button type="button" class="modal-x fb-x" aria-label={t("common.close")} onClick={onClose}>×</button>
      </div>
      <textarea
        ref={field}
        class={`fb-text${listening ? " live" : ""}`}
        rows={5}
        maxLength={4000}
        placeholder={t(listening ? "fb.listening" : "fb.placeholder")}
        value={text}
        onInput={(e) => updateText((e.target as HTMLTextAreaElement).value)}
      />
      {canDictate && (
        <>
          <div class="fb-voice">
            <button
              type="button"
              class={`btn ghost fb-mic${listening ? " on" : ""}`}
              aria-pressed={listening}
              onClick={toggleDictation}
              disabled={sending}
            >
              {listening ? <span class="fb-dot" /> : <IconMic />} {t(listening ? "fb.stop" : "fb.dictate")}
            </button>
            <select
              class="mini-select"
              aria-label={t("fb.lang")}
              value={lang}
              disabled={listening}
              onChange={(e) => setLang((e.target as HTMLSelectElement).value)}
            >
              {DICTATION_LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
            {listening && <span class="muted fb-hint">{t("fb.listeningHint")}</span>}
          </div>
          <p class="fb-note">{t("fb.voicePrivacy")}</p>
        </>
      )}
      {err && <div style={{ marginTop: "14px" }}><Msg kind="err" text={err} /></div>}
      <div class="actions">
        <button type="button" class="btn ghost" onClick={onClose}>{t("common.cancel")}</button>
        <button type="button" class="btn primary" disabled={sending || !text.trim()} onClick={() => void send()}>
          {sending ? t("fb.sending") : t("fb.send")}
        </button>
      </div>
    </Modal>
  );
}

/**
 * Settings - preferința de temă (per-dispozitiv, localStorage - vezi theme.ts) +
 * datele de facturare ale contului. Ambele reutilizează piese existente: câmpurile
 * de firmă (`BusinessFields`, aceleași ca la register) prin `api.updateProfile`, și
 * portalul Stripe (`api.billingPortal`, același „Manage billing" din Plan & billing)
 * pentru metoda de plată - afișat doar când există abonament activ, altfel portalul
 * n-ar avea client Stripe de deschis.
 */
const THEME_OPTIONS: { pref: ThemePref; icon: ComponentChildren; label: string }[] = [
  { pref: "light", icon: <IconSun />, label: "set.theme.light" },
  { pref: "dark", icon: <IconMoon />, label: "set.theme.dark" },
  { pref: "auto", icon: <IconAuto />, label: "set.theme.auto" },
];

/** Mesaje prietenoase pentru codurile de la /change-password; restul → errText. */
function pwErrText(err: unknown): string {
  if (err instanceof ApiError) {
    const code = (err.data as { error?: string } | null)?.error;
    if (code === "invalid_credentials") return t("set.pw.wrongCurrent");
    if (code === "same_password") return t("set.pw.sameAsOld");
  }
  return errText(err);
}

function SettingsView({ me, reloadMe }: { me: Me; reloadMe: () => void }) {
  const [theme, setTheme] = useState<ThemePref>(getThemePref);
  const [biz, setBiz] = useState<BizState>(() => bizFromProfile(me.business));
  const [savingBiz, setSavingBiz] = useState(false);
  const [bizMsg, setBizMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const [portalBusy, setPortalBusy] = useState(false);
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [repeatPw, setRepeatPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const [pwMsg, setPwMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  // "" = automat (statsResetDay === null); altfel string cu ziua aleasă.
  const savedResetDay = me.plan.statsResetDay === null ? "" : String(me.plan.statsResetDay);
  const [resetDay, setResetDay] = useState(savedResetDay);
  const [savingResetDay, setSavingResetDay] = useState(false);
  const [resetDayMsg, setResetDayMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  // Mutate aici din „Plan & billing" (7 aug 2026), când tabul acela a dispărut
  // odată cu cumpărarea self-serve. Niciuna nu ținea de billing:
  //  - consimțământul de marketing e temeiul Art. 6(1)(a) din privacy §3, iar
  //    Art. 7(3) cere ca retragerea să fie la fel de ușoară ca acordarea. Dacă
  //    dispărea cu tabul, rămâneam fără nicio cale de retragere în aplicație.
  //  - cheia API e credențiala programatică a clientului, nu un articol de vânzare.
  const [marketingConsent, setMarketingConsentLocal] = useState(me.marketingConsent);
  const [consentSaving, setConsentSaving] = useState(false);
  // Cheia API: afișată o singură dată, doar în memorie - nu există niciun GET
  // care s-o recupereze (backendul n-o mai are în clar după rotate).
  const [apiKeyBusy, setApiKeyBusy] = useState(false);
  const [apiKeyValue, setApiKeyValue] = useState<string | null>(null);
  const [apiKeyErr, setApiKeyErr] = useState<string | null>(null);
  // Ștergerea contului: formularul se deschide doar la cerere (F21a)
  const [delOpen, setDelOpen] = useState(false);
  const [delPw, setDelPw] = useState("");
  const [delBusy, setDelBusy] = useState(false);
  const [delMsg, setDelMsg] = useState<string | null>(null);

  async function deleteMyAccount(e: Event) {
    e.preventDefault();
    setDelBusy(true);
    setDelMsg(null);
    try {
      await api.deleteAccount(delPw);
      // backendul a revocat deja sesiunile; curățăm tokenul local și ne întoarcem
      // la login cu un mesaj. Schimbarea de query (?deleted=1) reîncarcă pagina.
      clearKey();
      location.assign(`${location.pathname}?deleted=1#/login`);
    } catch (err) {
      setDelMsg(err instanceof ApiError && err.status === 401 ? t("set.del.wrongPassword") : errText(err));
      setDelBusy(false);
    }
  }

  async function toggleMarketingConsent(next: boolean) {
    setMarketingConsentLocal(next);
    setConsentSaving(true);
    try {
      await api.setMarketingConsent(next);
    } finally {
      setConsentSaving(false);
    }
  }

  async function rotateApiKey() {
    setApiKeyBusy(true);
    setApiKeyErr(null);
    try {
      const { secretKey } = await api.rotateApiKey();
      setApiKeyValue(secretKey);
    } catch (err) {
      setApiKeyErr(errText(err));
    } finally {
      setApiKeyBusy(false);
    }
  }

  // PWA: starea se poate schimba după mount (beforeinstallprompt sosește târziu,
  // appinstalled o stinge) - ne abonăm la modulul pwa.ts care ține promptul.
  const [install, setInstall] = useState<InstallState>(installState);
  useEffect(() => subscribeInstall(() => setInstall(installState())), []);

  function pick(pref: ThemePref) {
    setThemePref(pref); // persist + re-stamp <html data-theme>
    setTheme(pref);
  }

  async function saveBiz(e: Event) {
    e.preventDefault();
    setSavingBiz(true);
    setBizMsg(null);
    try {
      await api.updateProfile(toBusinessInput(biz));
      setBizMsg({ kind: "ok", text: t("set.billing.saved") });
      reloadMe();
    } catch (err) {
      setBizMsg({ kind: "err", text: errText(err) });
    } finally {
      setSavingBiz(false);
    }
  }

  // Același flux ca „Manage billing" din PlanView: redirect către portalul găzduit Stripe.
  async function openPortal() {
    setPortalBusy(true);
    setBizMsg(null);
    try {
      const { url } = await api.billingPortal();
      location.href = url;
    } catch (err) {
      setBizMsg({ kind: "err", text: billingErrText(err) });
      setPortalBusy(false);
    }
  }

  async function saveResetDay(e: Event) {
    e.preventDefault();
    if (resetDay === "") return;
    setSavingResetDay(true);
    setResetDayMsg(null);
    try {
      const { effectiveAt } = await api.setStatsResetDay(Number(resetDay));
      setResetDayMsg({ kind: "ok", text: t("set.resetDay.saved", fmtDate(effectiveAt)) });
      reloadMe();
    } catch (err) {
      setResetDayMsg({ kind: "err", text: errText(err) });
    } finally {
      setSavingResetDay(false);
    }
  }

  async function savePw(e: Event) {
    e.preventDefault();
    // Verificarea cerută: parola nouă = repetarea ei. Plus lungimea minimă, ca să
    // nu facem drumul la server pentru ce backendul oricum respinge (min 8).
    if (newPw !== repeatPw) { setPwMsg({ kind: "err", text: t("set.pw.mismatch") }); return; }
    if (newPw.length < 8) { setPwMsg({ kind: "err", text: t("set.pw.tooShort") }); return; }
    setSavingPw(true);
    setPwMsg(null);
    try {
      await api.changePassword(curPw, newPw);
      setPwMsg({ kind: "ok", text: t("set.pw.saved") });
      setCurPw(""); setNewPw(""); setRepeatPw("");
    } catch (err) {
      setPwMsg({ kind: "err", text: pwErrText(err) });
    } finally {
      setSavingPw(false);
    }
  }

  return (
    <div class="content">
      <div class="center-wide" style={{ maxWidth: "760px" }}>
        <div class="card">
          <div class="setting-row">
            <div class="txt">
              <h4>{t("set.theme.title")}</h4>
              <p>{t("set.theme.desc")}</p>
            </div>
            <div class="segmented" role="group" aria-label={t("set.theme.title")}>
              {THEME_OPTIONS.map((o) => (
                <button
                  key={o.pref}
                  class={theme === o.pref ? "active" : ""}
                  aria-pressed={theme === o.pref}
                  onClick={() => pick(o.pref)}
                >
                  {o.icon}<span>{t(o.label as never)}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div class="card" style={{ marginTop: "18px" }}>
          <div class="setting-row">
            <div class="txt">
              <h4>{t("set.install.title")}</h4>
              <p>
                {install === "installed" ? t("set.install.installed")
                  : install === "ios" ? t("set.install.ios")
                  : install === "prompt" ? t("set.install.desc")
                  : t("set.install.other")}
              </p>
            </div>
            {install === "prompt" && (
              <button type="button" class="btn primary" onClick={() => void promptInstall()}>
                {t("set.install.button")}
              </button>
            )}
          </div>
        </div>

        <div class="card" style={{ marginTop: "18px" }}>
          <div class="setting-row" style={{ paddingTop: 0, borderBottom: "none" }}>
            <div class="txt">
              <h4>{t("set.billing.title")}</h4>
              <p>{t("set.billing.desc")}</p>
            </div>
          </div>
          <form onSubmit={saveBiz}>
            <BusinessFields v={biz} set={(patch) => setBiz((b) => ({ ...b, ...patch }))} disabled={savingBiz} />
            {bizMsg && <div style={{ marginTop: "14px" }}><Msg kind={bizMsg.kind} text={bizMsg.text} /></div>}
            <div class="row" style={{ display: "flex", gap: "12px", alignItems: "center", marginTop: "18px", flexWrap: "wrap" }}>
              <button class="btn primary" disabled={savingBiz} type="submit">{savingBiz ? "…" : t("set.billing.save")}</button>
              {/* Al doilea punct de intrare în portalul Stripe (celălalt e în
                  Plan & billing) - închis odată cu el, altfel poarta ar avea o
                  ușă din spate. */}
              {BILLING_SELF_SERVE && me.plan.hasStripeSubscription && (
                <button type="button" class="btn ghost" disabled={portalBusy} onClick={() => void openPortal()}>
                  <IconExternal /> {t("set.billing.portal")}
                </button>
              )}
            </div>
          </form>
        </div>

        <div class="card" style={{ marginTop: "18px" }}>
          <div class="setting-row" style={{ paddingTop: 0, borderBottom: "none" }}>
            <div class="txt">
              <h4>{t("set.resetDay.title")}</h4>
              <p>{t("set.resetDay.desc")}</p>
            </div>
          </div>
          <form onSubmit={saveResetDay}>
            <select
              value={resetDay}
              onChange={(e) => setResetDay((e.target as HTMLSelectElement).value)}
              disabled={savingResetDay}
            >
              {/* „Automatic" rămâne vizibil doar cât starea inițială e null - nu
                  există endpoint de revenire la automat odată ce ai ales o zi. */}
              <option value="" disabled={savedResetDay !== ""}>{t("set.resetDay.auto")}</option>
              {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                <option key={d} value={String(d)}>{d}</option>
              ))}
            </select>
            <p class="d" style={{ marginTop: "8px" }}>{t("set.resetDay.current", fmtDate(me.plan.answersResetAt))}</p>
            {resetDayMsg && <div style={{ marginTop: "14px" }}><Msg kind={resetDayMsg.kind} text={resetDayMsg.text} /></div>}
            <button
              class="btn primary"
              style={{ marginTop: "18px" }}
              disabled={savingResetDay || resetDay === "" || resetDay === savedResetDay}
              type="submit"
            >
              {savingResetDay ? "…" : t("set.resetDay.save")}
            </button>
          </form>
        </div>

        <div class="card" style={{ marginTop: "18px" }}>
          <h3 style={{ fontSize: "13px", fontWeight: 700 }}>{t("plan.marketingTitle")}</h3>
          <label class="check" style={{ marginTop: "10px" }}>
            <input
              type="checkbox"
              checked={marketingConsent}
              disabled={consentSaving}
              onChange={(e) => void toggleMarketingConsent((e.target as HTMLInputElement).checked)}
            />
            <span class="muted">{t("plan.marketingConsent")}</span>
          </label>
        </div>

        {/* Gated pe feature-ul `api` - la fel ca analytics/clientManagement în
            rest, doar planurile care-l includ (vezi plans.ts) văd secțiunea. */}
        {me.plan.features.includes("api") && (
          <div class="card" style={{ marginTop: "18px" }}>
            <h3 style={{ fontSize: "13px", fontWeight: 700 }}>{t("plan.apiKeyTitle")}</h3>
            <p class="muted" style={{ fontSize: "12.5px", marginTop: "4px" }}>{t("plan.apiKeySub")}</p>
            <button class="btn ghost" style={{ marginTop: "10px" }} disabled={apiKeyBusy} onClick={() => void rotateApiKey()}>
              {apiKeyBusy ? "…" : t("plan.apiKeyBtn")}
            </button>
            {apiKeyErr && <Msg kind="err" text={apiKeyErr} />}
            {apiKeyValue && (
              <div style={{ marginTop: "10px" }}>
                <p class="muted" style={{ fontSize: "12px", color: "var(--danger)" }}>{t("plan.apiKeyWarning")}</p>
                <code style={{ display: "block", marginTop: "6px", padding: "8px 10px", background: "var(--surface-2)", borderRadius: "8px", fontSize: "12.5px", wordBreak: "break-all", userSelect: "all" }}>
                  {apiKeyValue}
                </code>
              </div>
            )}
          </div>
        )}

        {/* Doar conturile cu parolă proprie; cele doar-Google n-au ce schimba. */}
        {me.hasPassword && (
          <div class="card" style={{ marginTop: "18px" }}>
            <div class="setting-row" style={{ paddingTop: 0, borderBottom: "none" }}>
              <div class="txt">
                <h4>{t("set.pw.title")}</h4>
                <p>{t("set.pw.desc")}</p>
              </div>
            </div>
            <form onSubmit={savePw}>
              <label>{t("set.pw.current")}</label>
              <input type="password" value={curPw} onInput={(e) => setCurPw((e.target as HTMLInputElement).value)} required autocomplete="current-password" disabled={savingPw} />
              <label>{t("set.pw.new")}</label>
              <input type="password" value={newPw} onInput={(e) => setNewPw((e.target as HTMLInputElement).value)} required minLength={8} autocomplete="new-password" disabled={savingPw} />
              <label>{t("set.pw.repeat")}</label>
              <input type="password" value={repeatPw} onInput={(e) => setRepeatPw((e.target as HTMLInputElement).value)} required autocomplete="new-password" disabled={savingPw} />
              {pwMsg && <div style={{ marginTop: "14px" }}><Msg kind={pwMsg.kind} text={pwMsg.text} /></div>}
              <button class="btn primary" style={{ marginTop: "18px" }} disabled={savingPw} type="submit">{savingPw ? "…" : t("set.pw.save")}</button>
            </form>
          </div>
        )}

        {/* Zona de pericol, ultima din pagină. Nu în timpul impersonării: un admin
            care "ajută" un client nu trebuie să-i poată șterge contul din greșeală. */}
        {!me.impersonating && (
          <div class="card" style={{ marginTop: "18px", borderColor: "var(--danger-bg)" }}>
            <div class="card-head">
              <div>
                <h2 style={{ color: "var(--danger)" }}>{t("set.del.title")}</h2>
                <p>{t("set.del.desc")}</p>
              </div>
              {!delOpen && me.hasPassword && (
                <button class="btn danger" onClick={() => setDelOpen(true)}><IconTrash /> {t("set.del.open")}</button>
              )}
            </div>
            <ul class="muted" style={{ fontSize: "12.5px", paddingLeft: "18px", margin: "0 0 4px" }}>
              <li>{t("set.del.p1")}</li>
              {me.plan.hasStripeSubscription && <li>{t("set.del.p2")}</li>}
              <li>{t("set.del.p3")}</li>
              <li>{t("set.del.p4")}</li>
            </ul>
            {!me.hasPassword && <p class="muted" style={{ fontSize: "12.5px", marginTop: "10px" }}>{t("set.del.noPassword")}</p>}
            {delOpen && me.hasPassword && (
              <form onSubmit={deleteMyAccount}>
                <label>{t("set.del.password")}</label>
                <input type="password" value={delPw} onInput={(ev) => setDelPw((ev.target as HTMLInputElement).value)} required autocomplete="current-password" disabled={delBusy} />
                {delMsg && <div style={{ marginTop: "14px" }}><Msg kind="err" text={delMsg} /></div>}
                <div class="row" style={{ display: "flex", gap: "10px", marginTop: "18px" }}>
                  <button class="btn ghost" type="button" disabled={delBusy} onClick={() => { setDelOpen(false); setDelPw(""); setDelMsg(null); }}>{t("common.cancel")}</button>
                  <button class="btn solid-danger" type="submit" disabled={delBusy || delPw === ""}>{delBusy ? "…" : t("set.del.confirm")}</button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Meniul de cont din topbar: avatar → email, plan, acțiuni. */
function AcctMenu({ me, onLogout, onLogoutAll }: { me: Me; onLogout: () => void; onLogoutAll: () => void }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") close(); };
    document.addEventListener("click", close);
    window.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("click", close); window.removeEventListener("keydown", onKey); };
  }, [open]);

  return (
    <div class="acct" onClick={(e) => e.stopPropagation()}>
      <button class="acct-btn" aria-label={me.email} aria-expanded={open} onClick={() => setOpen(!open)}>
        <span class="avatar">{initials(me.email)}</span>
        <IconChevronDown />
      </button>
      {open && (
        <div class="acct-menu">
          <div class="who">
            <div class="e">{me.email}</div>
          </div>
          <button class="item danger" onClick={onLogout}><IconLogout /> {t("logout")}</button>
          {/* Secundar, sub logout-ul obișnuit: revocă TOATE sesiunile (alte
              dispozitive/browsere), nu doar pe cea curentă - de-aceea nu e "danger"
              ca butonul de mai sus, e o acțiune mai rar folosită, nu cea implicită. */}
          <button class="item" onClick={onLogoutAll}><IconLogout /> {t("logoutAll")}</button>
        </div>
      )}
    </div>
  );
}

// Cooldown-ul de resend (5 min) și marcajele din localStorage: vezi ./verification.

function fmtMMSS(total: number): string {
  const m = Math.floor(total / 60);
  const s = String(total % 60).padStart(2, "0");
  return `${m}:${s}`;
}

/**
 * Buton de retrimitere a emailului de verificare, cu timer de 5 min. Reutilizat
 * pe ecranul de auth (după register / login blocat de email neverificat) și în
 * bannerul din dashboard. Cooldown-ul e persistat per email - vezi helper-ele
 * de mai sus. Se randează inline, ca să încapă și într-un mesaj, și într-un banner.
 */
function ResendVerify({ email }: { email: string }) {
  const [left, setLeft] = useState(() => resendCooldownLeft(email));
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState(false);
  // backendul a răspuns, dar emailul NU a plecat (emailSent: false) - nu pornim cooldown-ul
  const [notSent, setNotSent] = useState(false);

  // Resincronizează dacă emailul se schimbă (același component, alt cont).
  useEffect(() => { setLeft(resendCooldownLeft(email)); }, [email]);

  // Un singur interval cât timp mai e cooldown; dependința pe `left === 0` face
  // ca efectul să pornească/oprească ceasul la tranziții, nu la fiecare secundă.
  useEffect(() => {
    if (left <= 0) return;
    const id = setInterval(() => setLeft((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(id);
  }, [left === 0, email]);

  async function resend() {
    if (busy || left > 0) return;
    setBusy(true); setErr(false); setNotSent(false); setSent(false);
    try {
      const res = await api.resendVerification(email);
      if (res.emailSent === false) {
        setNotSent(true);
        return;
      }
      markVerificationSent(email);
      setSent(true);
      setLeft(RESEND_COOLDOWN_S);
    } catch {
      setErr(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {sent && <span>{t("verify.sent")} </span>}
      {left > 0 ? (
        <span class="muted">{t("verify.resendIn", fmtMMSS(left))}</span>
      ) : (
        <a href="#" onClick={(e) => { e.preventDefault(); void resend(); }}>
          {busy ? "…" : t("verify.resend")}
        </a>
      )}
      {err && <span class="muted"> {t("verify.resendErr")}</span>}
      {notSent && <span class="muted"> {t("verify.notSent")}</span>}
    </>
  );
}

function VerifyBanner({ email }: { email: string }) {
  return (
    <div class="msg info page-banner">
      {t("verify.banner")}{" "}
      <ResendVerify email={email} />
    </div>
  );
}

/**
 * Contul n-a cumpărat încă nimic (e pe planul `free`) - sau a fost mutat manual
 * pe un plan fără activare. Fără bannerul ăsta, un cont nou vedea doar
 * „0 / 20 answers", fără niciun indiciu că widgetul i se stinge la epuizare.
 */
function NoSubscriptionBanner({ plan }: { plan: PlanSummary }) {
  return (
    <div class="msg info page-banner">
      {t("noSub.banner", num(plan.answersRemaining), num(plan.answersLimit))}{" "}
      {plan.bonusAnswers > 0 && <>{t("noSub.bonus", num(plan.bonusAnswers))}{" "}</>}
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault();
          goUpgrade();
        }}
      >
        {t("noSub.cta")}
      </a>
    </div>
  );
}

/**
 * Cota lunară de răspunsuri, epuizată SAU aproape - singurul indiciu din
 * dashboard, în afara emailului, că widgetul chiar s-a stins pentru vizitatori
 * noi (`answersExhausted` vine din backend (`usage.ts: isAnswersExhausted`)
 * din backend, bit cu bit, ca un cont cu bonus/credit rămas să nu vadă un
 * banner fals). Sub 80% nu se arată nimic - la fel ca pragurile de email.
 */
function QuotaBanner({ me }: { me: Me }) {
  const { plan } = me;
  const exhausted = isQuotaExhausted(plan);
  if (!exhausted && !isQuotaWarning(plan)) return null;
  return (
    <div class={`msg ${exhausted ? "err" : "warn"} page-banner`}>
      {exhausted
        ? t("quota.exhausted.banner", num(plan.answersLimit))
        : t("quota.warning.banner", num(plan.answersUsed), num(plan.answersLimit), pct(plan.answersUsed, plan.answersLimit))}{" "}
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault();
          goUpgrade();
        }}
      >
        {exhausted ? t("quota.exhausted.cta") : t("quota.warning.cta")}
      </a>
    </div>
  );
}

function Shell({ me, reloadMe, onLogout, onLogoutAll }: { me: Me; reloadMe: () => void; onLogout: () => void; onLogoutAll: () => void }) {
  const route = useRoute();
  const [apps, setApps] = useState<AppTraffic[] | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  async function reloadApps() {
    try {
      setApps((await api.appsTraffic()).apps);
    } catch {
      /* 401 tratat în request() */
    }
  }
  /**
   * Situl întors de un PATCH, pus în listă IMEDIAT. Nu e o optimizare: un
   * `reloadApps()` e asincron, iar până aterizează el un card remontat citește
   * încă datele dinainte de salvare - exact fereastra prin care se pierdea
   * brandingul (vezi BrandingCard). Câmpurile de trafic nu vin în răspunsul de
   * PATCH, deci se păstrează cele existente.
   */
  function patchApp(updated: AdminApp) {
    setApps((prev) => (prev ?? []).map((a) => (a.appId === updated.appId ? { ...a, ...updated } : a)));
  }
  useEffect(() => {
    void reloadApps();
  }, []);

  const currentApp = route.name === "site" ? (apps ?? []).find((a) => a.appId === route.appId) ?? null : null;

  // back către un site șters (sau URL invalid) → înapoi la listă
  useEffect(() => {
    if (route.name === "site" && apps && !apps.find((a) => a.appId === route.appId)) navigate("#/dashboard");
  }, [route, apps]);

  // Go live/Take offline + Open configurator trăiesc în topbar (lângă profil), nu în
  // corpul paginii - oglinda optimistă a status-ului stă aici, nu pe SiteDetail, ca
  // butoanele să nu depindă de faptul că SiteDetail e montat sub ele.
  const [siteLive, setSiteLive] = useState(false);
  const [siteBusy, setSiteBusy] = useState(false);
  const [siteActionMsg, setSiteActionMsg] = useState<string | null>(null);
  useEffect(() => {
    if (currentApp) setSiteLive(currentApp.live);
  }, [currentApp?.appId, currentApp?.live]);

  async function toggleSiteLive() {
    if (!currentApp) return;
    setSiteBusy(true);
    setSiteActionMsg(null);
    try {
      if (!siteLive) {
        // activare: publică draftul curent din configurator ca versiune reală
        // înainte de a face vizibil chatul - altfel "Go live" doar aprinde
        // flag-ul fără să existe vreun config publicat de servit (404 pe /config)
        try {
          await api.publishBuilderDraft(currentApp.appId);
        } catch (err) {
          // 409 = niciun draft de publicat (probabil există deja un config publicat anterior) - continuăm
          if (!(err instanceof ApiError) || err.status !== 409) throw err;
        }
      }
      const r = await api.updateApp(currentApp.appId, { live: !siteLive });
      setSiteLive(r.app.live);
      void reloadApps();
    } catch (err) {
      setSiteActionMsg(liveSiteLimitText(err) ?? errText(err));
    } finally {
      setSiteBusy(false);
    }
  }

  async function openSiteBuilder() {
    if (!currentApp) return;
    setSiteBusy(true);
    setSiteActionMsg(null);
    try {
      const { url } = await api.builderToken(currentApp.appId);
      window.open(url, "_blank");
    } catch (err) {
      setSiteActionMsg(errText(err));
    } finally {
      setSiteBusy(false);
    }
  }

  // `site` și `content` au propriul render mai jos; pentru rest, route.name e un View.
  const view: View = route.name === "site" || route.name === "content" ? "dashboard" : (route.name as View);

  // Vederile din secțiunea de admin: nav-ul le ascunde deja non-adminilor, dar
  // hash-ul rămâne tastabil. Backend-ul le refuză oricum (403 pe /console/*),
  // deci nu se scurge nimic - problema e că un client nimerit pe #/activity ar
  // vedea „No events match these filters" în loc să înțeleagă că n-are ce căuta.
  useEffect(() => {
    // Content Hub e admin-only, la fel ca vederile din ADMIN_VIEWS: un client
    // nimerit pe #/content/blog e trimis înapoi (backendul îl refuză oricum 403).
    if (!me.isAdmin && (ADMIN_VIEWS.includes(view) || route.name === "content")) navigate("#/dashboard");
  }, [view, me.isAdmin, route.name]);

  const head = TITLES[view];

  return (
    <div class="shell">
      <aside class="rail">
        <div class="rail-brand">
          <div class="mark"><img src={logoBadge} alt="" /></div>
          <div class="name">AMW <span style={{ color: "var(--brand-600)" }}>[AI]</span></div>
        </div>

        <div class="rail-section">{t("nav.section.workspace")}</div>
        {/* IconOverview, nu IconSites: globul înseamnă „sit web" - e chiar
            pictograma stării goale „No sites yet", pe același ecran. */}
        {/* „site" păstrează Dashboard aprins (breadcrumb-ul duce înapoi la el);
            „content" NU - altfel Dashboard s-ar aprinde odată cu News/Blog,
            fiindcă `view` cade pe „dashboard" pentru rutele fără View propriu. */}
        <NavItem icon={<IconOverview />} label={t("nav.dashboard")} active={route.name === "dashboard" || route.name === "site"} onClick={() => navigate("#/dashboard")} />
        {/* Plan & billing există doar cât cumpărarea e deschisă (route.ts →
            BILLING_SELF_SERVE); închisă, #/plan cade oricum pe dashboard. */}
        {BILLING_SELF_SERVE && <NavItem icon={<IconPlan />} label={t("nav.plan")} active={view === "plan"} onClick={() => navigate("#/plan")} />}

        {me.isAdmin && (
          <>
            {/* Referral stă aici, nu în „workspace", cât timp programul e ascuns. */}
            <NavItem icon={<IconGift />} label={t("nav.referral")} active={view === "referral"} onClick={() => navigate("#/referral")} />
            <div class="rail-section">{t("nav.section.admin")}</div>
            <NavItem icon={<IconConsole />} label={t("nav.console")} active={view === "console"} onClick={() => navigate("#/console")} />
            <NavItem icon={<IconGift />} label={t("nav.codes")} active={view === "codes"} onClick={() => navigate("#/codes")} />
            {/* Auditul e intern: un cont = o singură identitate (fără echipe),
                deci „cine a făcut ce" pe contul clientului = „ce a făcut el".
                Valoarea e la noi - răspundem la ticketul „nu îmi merge". */}
            <NavItem icon={<IconShield />} label={t("nav.activity")} active={view === "activity"} onClick={() => navigate("#/activity")} />
            <NavItem icon={<IconTicket />} label={t("nav.tickets")} active={view === "tickets"} onClick={() => navigate("#/tickets")} badge={me.openTickets} />
            {/* Content Hub - trei intrări, un singur UI. „active" pe tipul din hash. */}
            <div class="rail-section">{t("nav.section.content")}</div>
            <NavItem icon={<IconNews />} label={t("nav.news")} active={route.name === "content" && route.kind === "news"} onClick={() => navigate("#/content/news")} />
            <NavItem icon={<IconDoc />} label={t("nav.research")} active={route.name === "content" && route.kind === "research"} onClick={() => navigate("#/content/research")} />
            <NavItem icon={<IconDoc />} label={t("nav.blog")} active={route.name === "content" && route.kind === "blog"} onClick={() => navigate("#/content/blog")} />
          </>
        )}

        <div class="rail-spacer" />
        <NavItem icon={<IconSettings />} label={t("nav.settings")} active={view === "settings"} onClick={() => navigate("#/settings")} />
        {/* Feedback: doar cine plătește (`plan.plan` = planul facturat, nu grantul) și nu sub impersonare. */}
        {me.plan.plan !== "free" && !me.impersonating && (
          <NavItem icon={<IconChat />} label={t("nav.feedback")} active={false} onClick={() => setFeedbackOpen(true)} />
        )}
        <NavItem icon={<IconHelp />} label={t("nav.help")} active={false} onClick={() => setHelpOpen(true)} />
      </aside>

      <div class="main">
        <div class="topbar">
          <div class="h">
            <h1>
              {route.name === "site" ? (
                <>
                  <a class="crumb-link" href="#/dashboard" onClick={(e) => { e.preventDefault(); navigate("#/dashboard"); }}>{t("sites.title")}</a>
                  <span class="crumb-sep">/</span>
                  {currentApp?.name || route.appId}
                  {currentApp && (
                    <span class={`badge ${siteLive ? "ok" : "muted"}`} style={{ marginLeft: "10px", verticalAlign: "middle", fontSize: "11px" }}>
                      {siteLive ? t("sd.live") : t("sd.notLive")}
                    </span>
                  )}
                </>
              ) : route.name === "content" ? (
                route.id ? (
                  <>
                    <a class="crumb-link" href={`#/content/${route.kind}`} onClick={(e) => { e.preventDefault(); navigate(`#/content/${route.kind}`); }}>{t(`nav.${route.kind}` as never)}</a>
                    <span class="crumb-sep">/</span>
                    {t("content.editor")}
                  </>
                ) : (
                  t(`nav.${route.kind}` as never)
                )
              ) : (
                t(head.title as never)
              )}
            </h1>
            <div class="sub">{route.name === "site" ? currentApp?.allowedOrigins[0] ?? route.appId : route.name === "content" ? t("content.sub") : t(head.sub as never)}</div>
          </div>
          {route.name === "site" && currentApp && (
            <div class="row" style={{ gap: "10px" }}>
              <button class={siteLive ? "btn danger" : "btn ghost"} disabled={siteBusy} onClick={() => void toggleSiteLive()}>
                {siteLive ? t("sd.takeOffline") : t("sd.goLive")}
              </button>
              <button class="btn ghost" disabled={siteBusy} onClick={() => void openSiteBuilder()}>
                {t("sd.openBuilder")}
              </button>
            </div>
          )}
          <AcctMenu me={me} onLogout={onLogout} onLogoutAll={onLogoutAll} />
        </div>

        {!me.emailVerified && <VerifyBanner email={me.email} />}
        {/* Nu și pentru admini: conturile noastre interne n-au abonament prin
            natura lor, deci bannerul le-ar fi permanent și fals - nu au ce
            cumpăra. E un îndemn la conversie, iar noi nu suntem publicul lui. */}
        {/* `BILLING_SELF_SERVE` în plus față de test: în beta, un cont cu plan
            acordat manual n-are nici abonament, nici `renewsAt`, deci ar vedea
            permanent „No active subscription… See plans" - un îndemn la
            conversie către un ecran fără planuri. Exact greșeala descrisă mai
            sus pentru conturile de admin, doar cu alt public. */}
        {/* Nici pe conturile cu plan ACORDAT de echipă (`grantedPlan`): limitele lor
            vin din grant, nu din abonament, deci „No active subscription… See plans"
            ar fi permanent și fals - exact ce a ținut bannerul stins în beta. */}
        {!me.isAdmin && BILLING_SELF_SERVE && !me.plan.grantedPlan && !hasActiveSubscription(me.plan) && <NoSubscriptionBanner plan={me.plan} />}
        {/* Doar pentru conturi cu abonament activ: fără el, `NoSubscriptionBanner`
            deasupra acoperă deja mesajul „îți dispare widgetul" - un al doilea
            banner ar fi zgomot pentru un cont care încă n-a cumpărat nimic.
            În beta bannerul acela nu se mai afișează, deci avertizarea de cotă
            rămâne singura - și trebuie să ajungă la toți: cota se termină la fel
            de real pe un plan acordat ca pe unul plătit. */}
        {!me.isAdmin && (!BILLING_SELF_SERVE || Boolean(me.plan.grantedPlan) || hasActiveSubscription(me.plan)) && <QuotaBanner me={me} />}

        {route.name === "content" ? (
          route.id ? (
            <ContentEditorView kind={route.kind} id={route.id} />
          ) : (
            <ContentListView kind={route.kind} />
          )
        ) : route.name === "site" ? (
          currentApp ? (
            <SiteDetail
              /* key pe appId: trecerea DIRECTĂ de la un sit la altul (hash editat,
                 link extern) remontează tot subarborele - altfel state-urile
                 inițializate din props (numele din Settings, sumarul de
                 conversații, paginarea) ar rămâne ale sitului precedent. */
              key={currentApp.appId}
              app={currentApp}
              me={me}
              tab={route.tab}
              actionMsg={siteActionMsg}
              onChanged={() => { void reloadApps(); reloadMe(); }}
              onAppPatched={patchApp}
              onDeleted={() => { navigate("#/dashboard"); void reloadApps(); reloadMe(); }}
            />
          ) : (
            <div class="content">
              <Skeleton h="38px" w="320px" style={{ borderRadius: "10px", marginBottom: "18px" }} />
              <Skeleton h="180px" style={{ borderRadius: "16px" }} />
            </div>
          )
        ) : view === "dashboard" ? (
          <DashboardView me={me} apps={apps} reloadApps={reloadApps} reloadMe={reloadMe} />
        ) : view === "plan" ? (
          <PlanView me={me} reloadMe={reloadMe} />
        ) : view === "referral" ? (
          <ReferralView />
        ) : view === "activity" ? (
          <AdminAuditView />
        ) : view === "console" ? (
          <AdminConsole />
        ) : view === "codes" ? (
          <AdminCodesView />
        ) : view === "settings" ? (
          <SettingsView me={me} reloadMe={reloadMe} />
        ) : (
          <TicketsView reloadMe={reloadMe} />
        )}
      </div>

      {helpOpen && <HelpModal email={me.email} onClose={() => setHelpOpen(false)} />}
      {feedbackOpen && <FeedbackModal onClose={() => setFeedbackOpen(false)} />}
    </div>
  );
}

function consumeQueryParam(name: string): string | null {
  const url = new URL(location.href);
  const value = url.searchParams.get(name);
  if (value !== null) {
    url.searchParams.delete(name);
    history.replaceState(null, "", url.pathname + url.hash);
  }
  return value;
}

/**
 * Destinația `?return=` din poarta paginilor demo (backend requireDemoPage): userul
 * trimis la login e adus înapoi pe demo după autentificare. Validat strict ca să NU
 * fie open redirect - doar același host (localhost în dev) sau un subdomeniu
 * askmywebsiteai.com, doar http/https. Orice altceva → null (ignorat).
 */
function safeReturnUrl(raw: string | null): string | null {
  if (!raw) return null;
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    return null;
  }
  if (u.protocol !== "https:" && u.protocol !== "http:") return null;
  const h = u.hostname;
  if (h === location.hostname || h === "askmywebsiteai.com" || h.endsWith(".askmywebsiteai.com")) return u.toString();
  return null;
}

/**
 * Planul ales pe landing, ținut până când omul poate efectiv să-l cumpere.
 *
 * Butoanele publice de preț trimit `?plan=growth` aici. Între click și plată se
 * interpun crearea contului, verificarea pe email și login-ul - deci intenția
 * trebuie să supraviețuiască mai multor încărcări de pagină, nu doar unui
 * redirect. localStorage, fiindcă dashboardul e un singur origin (același loc
 * unde stă deja cheia de sesiune); dacă linkul de verificare se deschide în alt
 * browser, intenția se pierde și omul aterizează pur și simplu pe #/plan.
 */
const PLAN_INTENT_KEY = "amwai_plan_intent";
const PLAN_INTENT_IDS = ["starter", "growth", "agency"];
// Aceleași 30 de zile ca la cookie-ul de discount `amwai_ref`, din același motiv:
// cine se întoarce peste luni de zile nu trebuie primit cu un checkout deschis
// pentru un plan pe care l-a uitat. Declarat în cookies §2.
const PLAN_INTENT_TTL_MS = 30 * 24 * 3600 * 1000;

function storePlanIntent(): void {
  const wanted = consumeQueryParam("plan");
  if (!wanted || !PLAN_INTENT_IDS.includes(wanted)) return;
  localStorage.setItem(PLAN_INTENT_KEY, JSON.stringify({ plan: wanted, ts: Date.now() }));
}
/**
 * Se uită fără să consume - App() decide din asta dacă mai are rost să navigheze.
 * Curăță totuși ce e expirat sau stricat: cookies §2 promite „30 days", deci
 * octeții chiar trebuie să dispară la prima vizită de după, nu doar să fie
 * ignorați de cod.
 */
function peekPlanIntent(): string | null {
  const raw = localStorage.getItem(PLAN_INTENT_KEY);
  if (raw === null) return null;
  const plan = readPlanIntent(raw);
  if (plan === null) localStorage.removeItem(PLAN_INTENT_KEY);
  return plan;
}
/** Citește ȘI șterge - o intenție consumată nu trebuie să reapară la următorul refresh. */
function consumePlanIntent(): string | null {
  const raw = localStorage.getItem(PLAN_INTENT_KEY);
  if (!raw) return null;
  localStorage.removeItem(PLAN_INTENT_KEY);
  return readPlanIntent(raw);
}
function readPlanIntent(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const { plan, ts } = JSON.parse(raw) as { plan?: string; ts?: number };
    if (!plan || !PLAN_INTENT_IDS.includes(plan)) return null;
    if (typeof ts !== "number" || Date.now() - ts > PLAN_INTENT_TTL_MS) return null;
    return plan;
  } catch {
    return null; // format vechi sau stricat manual - îl tratăm ca absent
  }
}

// Intrare/ieșire din impersonare (consola de admin). Reload complet e deliberat
// (simplu și robust - App() reciteşte tokenul din localStorage și randează pornind
// de la el) în loc de un mecanism de state mai complicat.
async function enterImpersonation(accountId: number): Promise<void> {
  const { sessionToken } = await api.adminImpersonate(accountId);
  const adminToken = getKey();
  if (adminToken) setImpersonationReturn(adminToken);
  setKey(sessionToken);
  location.reload();
}

async function exitImpersonation(): Promise<void> {
  try {
    await api.logout(); // revocă sesiunea de impersonare pe server → admin.impersonate_end
  } catch {
    // best-effort, ca manualLogout - tot restaurăm tokenul de admin local
  }
  const adminToken = getImpersonationReturn();
  if (adminToken) {
    setKey(adminToken);
    clearImpersonationReturn();
  } else {
    // edge: n-a supraviețuit tokenul de admin - fallback la login, nu excepție
    clearKey();
  }
  location.reload();
}

function App() {
  const [authed, setAuthed] = useState(Boolean(getKey()));
  const [me, setMe] = useState<Me | null>(null);
  const [resetToken] = useState(() => consumeQueryParam("reset_token"));
  // Poarta paginilor demo ne-a trimis aici cu ?return=<pagină demo>. Îl ținem până
  // când userul e complet logat (profil complet → cookie-ul aig_demo e valid), apoi
  // îl ducem înapoi pe demo. Validat anti open-redirect (safeReturnUrl).
  const [returnUrl] = useState(() => safeReturnUrl(consumeQueryParam("return")));
  // Scoatem `?plan=` din URL imediat, chiar dacă abia peste câteva pagini
  // ajungem să-l folosim - altfel un refresh l-ar reactiva la nesfârșit.
  useState(storePlanIntent);
  const [verifyNotice] = useState<{ kind: "ok" | "err" | "info"; text: string } | null>(() => {
    const v = consumeQueryParam("verified");
    if (v !== null) {
      return v === "1"
        ? { kind: "ok" as const, text: t("verify.linkOk") }
        : { kind: "err" as const, text: t("verify.linkBad") };
    }
    // ștergerea contului din Settings ne aduce aici cu ?deleted=1
    return consumeQueryParam("deleted") === "1" ? { kind: "info" as const, text: t("auth.deleted") } : null;
  });
  // După „Email verified": emailul pe care browserul îl știe de la register /
  // login blocat, precompletat în formular (și marcajele se șterg).
  const [verifiedEmail] = useState(() => (verifyNotice?.kind === "ok" ? lastVerificationEmail(true) : null));
  // ?from=demo: marcaj explicit; ?return= vine azi doar din poarta paginilor demo
  // (backend: services/demoSession.ts → requireDemoPage), deci îl tratăm la fel.
  const [fromDemo] = useState(() => consumeQueryParam("from") === "demo" || returnUrl !== null);
  // Fără sesiune și cu intenție de cumpărare (?plan=…) sau venit din demo: omul e
  // nou, nu „înapoi" - deschidem Sign up. Nu după un link de verificare / ștergere
  // (acolo are deja cont) și nu peste un hash explicit.
  const [preferRegister] = useState(
    () => SIGNUPS_OPEN && !getKey() && verifyNotice === null && (fromDemo || peekPlanIntent() !== null),
  );

  async function reloadMe() {
    try {
      setMe(await api.me());
    } catch (err) {
      // Cheie validă pe cont neverificat (rămasă în storage): backendul dă 403.
      // Deslogăm curat, ca userul să reia login-ul și să primească instrucțiunile
      // de verificare, în loc să rămână blocat pe skeleton.
      if (err instanceof ApiError && err.status === 403 && (err.data as { error?: string })?.error === "email_not_verified") {
        logout();
      }
      /* 401 tratat în request() */
    }
  }
  useEffect(() => {
    if (authed) void reloadMe();
  }, [authed]);

  // Nelogat, dar cu hash-ul rămas de la sesiunea trecută (#/dashboard,
  // #/settings…): ecranul afișat e login-ul, însă URL-ul minte - și nu doar
  // cosmetic: widgetul asistentului își decide vizibilitatea după hash
  // (Excluded URLs - #/login e exclus, #/dashboard nu), deci fără normalizare
  // capsula de chat rămâne pe ecranul de autentificare. replaceState, nu
  // navigate: nu vrem intrare de istorie pentru o corecție; SDK-ul tot o vede
  // (își interceptează replaceState + ascultă hashchange).
  useEffect(() => {
    if (authed) return;
    const seg = location.hash.replace(/^#\/?/, "").split("?")[0].split("/")[0];
    if (seg !== "login" && seg !== "register" && seg !== "forgot") {
      history.replaceState(null, "", seg === "" && preferRegister ? "#/register" : "#/login");
    }
  }, [authed]);

  // Simetricul de după login: rămas pe #/login (sau #/register, #/forgot, ori
  // rădăcina goală), URL-ul minte la fel - conținutul e consola, dar hash-ul
  // spune „login", deci widgetul rămâne ascuns pe consolă (Excluded URLs). Îl
  // ducem în #/dashboard prin `navigate` (schimbă hash-ul → hashchange → SDK-ul
  // reevaluează → capsula apare). Excepțiile - intenția de plan (#/plan) și
  // întoarcerea la demo (?return) - au efectele lor de mai jos și au prioritate.
  useEffect(() => {
    if (!authed || peekPlanIntent() || returnUrl) return;
    const seg = location.hash.replace(/^#\/?/, "").split("?")[0].split("/")[0];
    if (seg === "" || seg === "login" || seg === "register" || seg === "forgot") {
      navigate("#/dashboard");
    }
  }, [authed]);

  // Contul e gata: dacă a venit de pe un buton de preț, ducem omul acolo unde
  // poate plăti. PlanView consumă intenția și deschide pasul de facturare.
  //
  // Inert în private beta: nu mai există buton de preț pe landing care să scrie
  // intenția, nici PlanView care s-o consume. Garda pe `BILLING_SELF_SERVE`
  // acoperă cheia rămasă în localStorage de dinaintea închiderii - fără ea, un
  // client vechi ar fi trimis la fiecare login către o rută care îl redirecționa
  // înapoi, adică un salt inexplicabil pe dashboard.
  useEffect(() => {
    if (BILLING_SELF_SERVE && me && peekPlanIntent()) navigate("#/plan");
  }, [me]);

  // Venit prin poarta demo (?return): profilul complet înseamnă că login-ul a setat
  // cookie-ul aig_demo, deci pagina demo îl va lăsa acum să intre. Îl ducem înapoi.
  useEffect(() => {
    if (me?.profileComplete && returnUrl) location.assign(returnUrl);
  }, [me]);

  // Deslogare locală, fără apel de server: folosită și pe căile AUTOMATE (403
  // email_not_verified mai jos, 401 tratat în api.ts request()) unde tokenul
  // e deja mort/nevalid pe server - un POST /logout acolo n-ar face nimic.
  function logout() {
    clearKey();
    setMe(null);
    setAuthed(false);
  }

  // Deslogare manuală (buton din meniu / ProfileGate): revocă sesiunea pe
  // server ÎNAINTE de a curăța local, best-effort - dacă /logout eșuează
  // (rețea, server jos), tot deslogăm local, ca userul să nu rămână blocat.
  async function manualLogout() {
    try {
      await api.logout();
    } catch {
      // ignorăm - vezi comentariul de mai sus
    }
    logout();
  }

  // „Sign out of all devices": revocă TOATE sesiunile contului (nu doar cea
  // curentă), apoi aceeași curățare locală ca manualLogout.
  async function manualLogoutAll() {
    try {
      await api.logoutAll();
    } catch {
      // ignorăm - best-effort, ca la manualLogout
    }
    logout();
  }

  if (resetToken) return <ResetPasswordView token={resetToken} onDone={() => location.assign(location.pathname)} />;
  if (!authed) {
    return (
      <AuthView
        onDone={() => setAuthed(true)}
        notice={verifyNotice}
        preferRegister={preferRegister}
        fromDemo={fromDemo}
        prefillEmail={verifiedEmail}
      />
    );
  }
  if (!me) {
    return (
      <div style={{ padding: "40px", maxWidth: "760px" }}>
        <Skeleton h="22px" w="220px" style={{ borderRadius: "8px", marginBottom: "16px" }} />
        <Skeleton h="140px" style={{ borderRadius: "16px", marginBottom: "14px" }} />
        <Skeleton h="140px" style={{ borderRadius: "16px" }} />
      </div>
    );
  }
  // Banner-ul de impersonare (+ butonul Exit) trebuie vizibil în ORICE ecran
  // autentificat cât timp me.impersonating e true - inclusiv sub ProfileGate,
  // ca adminul să nu rămână blocat pe profilul incomplet al clientului fără cale
  // de ieșire.
  const banner = me.impersonating ? <ImpersonationBanner email={me.email} onExit={() => void exitImpersonation()} /> : null;
  // Poarta „profil complet": fără datele de firmă nu se intră în dashboard.
  // Prinde mai ales conturile Google (register pe parolă vine deja cu ele).
  // Impersonarea o ocolește: profilul incomplet e al CLIENTULUI, nu al adminului -
  // adminul nu trebuie blocat în ProfileGate fără cale de ieșire.
  const body = !me.profileComplete && !me.impersonating
    ? <ProfileGate me={me} onDone={reloadMe} onLogout={manualLogout} />
    : me.profileComplete
      ? <Shell me={me} reloadMe={reloadMe} onLogout={manualLogout} onLogoutAll={manualLogoutAll} />
      : <ProfileGate me={me} onDone={reloadMe} onLogout={manualLogout} />;
  return (
    <>
      {banner}
      <div style={me.impersonating ? { paddingTop: IMPERSONATION_BANNER_HEIGHT } : undefined}>{body}</div>
    </>
  );
}

// Înălțimea aproximativă a banner-ului (folosită ca padding-top pe conținut, ca
// să nu fie acoperit - banner-ul e `position: fixed`).
const IMPERSONATION_BANNER_HEIGHT = "50px";

// Banner fix, mereu deasupra restului conținutului, cât timp o sesiune de
// impersonare e activă - singura cale înapoi la adminul original e butonul Exit.
function ImpersonationBanner({ email, onExit }: { email: string; onExit: () => void }) {
  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: "var(--danger)",
        color: "#fff",
        padding: "10px 16px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "12px",
        fontSize: "13px",
        fontWeight: 600,
        boxShadow: "0 2px 8px rgba(0,0,0,.25)",
      }}
    >
      <span>{t("imp.bannerPrefix")} <strong>{email}</strong>{t("imp.bannerSuffix")}</span>
      <button
        class="btn ghost sm"
        style={{ background: "transparent", borderColor: "#fff", color: "#fff" }}
        onClick={onExit}
      >
        {t("imp.exit")}
      </button>
    </div>
  );
}

// Theme was already stamped on <html> pre-paint by the inline script in
// index.html; here we only keep "auto" in step with later OS theme changes.
watchSystemTheme();

// Înainte de render: `beforeinstallprompt` poate sosi imediat după load, iar
// dacă nu-l capturăm atunci, butonul „Install app" din Settings n-ar apărea.
initPwaInstall();

render(<App />, document.getElementById("root")!);

// Widgetul concierge al aplicației - după mount, cheile din window.AIG_APP_BOT
// (servit de /app-keys.js), SDK-ul de pe location.origin. Vezi ./aiConciergeLoader.
initAIConciergeLoader();
